/* ============================================================================
   DietXP NutriSuite Pro — calculation engine + UI
   File: assets/dietxp-nutrisuite.js

   Source-traceable logic (DietXP_IFCT_RDA_DGI_2024_Final_Audited_v9 workbook):
   - BMR: FAO/WHO/UNU 2004 × Indian adjustment factor (BMR_Equations, RDA p17)
   - TEE: adjusted BMR × PAL (RDA 2024 energy tables reproduce these values
     within ±1 kcal for tabulated weights — validated at build time)
   - Goal calories, protein modes, fat/visible-fat rules: Goal_Logic,
     Protein_Modes, Fat_Oil_Rules, Production_Config_V7
   - Food groups: DGI 2024 My Plate 2000 kcal scaled (DGI_Target_Food_Groups)
   - Meal plans: Meal_Templates_V6/V7 + Portion_Optimizer_V6 priorities
   - Guardrails/messages: Error_Messages_V7, Fat_Loss/Muscle_Gain_Rules_Final_V7
   ========================================================================= */
(function () {
  'use strict';

  var UI_BUILD = '2026-08-26-practical-week-v10';
  if (typeof window !== 'undefined') window.DietXP_UI_BUILD = UI_BUILD;

  /* ============================ pure engine ============================== */
  var Engine = {};

  Engine.ageBand = function (age) {
    if (age < 30) return '18_30';
    if (age < 60) return '30_60';
    return 'Over60';
  };

  Engine.calcAnthro = function (inp) {
    var h = inp.height_cm / 100;
    var bmi = inp.weight_kg / (h * h);
    var cat, catCode;
    /* Asian Indian BMI bands (guide §8.2) */
    if (bmi < 18.5) { cat = 'Below healthy range'; catCode = 'under'; }
    else if (bmi < 23) { cat = 'Healthy range'; catCode = 'normal'; }
    else if (bmi < 25) { cat = 'Increased risk'; catCode = 'over'; }
    else if (bmi < 30) { cat = 'High risk'; catCode = 'obese1'; }
    else { cat = 'Very high risk'; catCode = 'obese2'; }
    var out = { bmi: Math.round(bmi * 10) / 10, bmiCategory: cat, bmiCode: catCode };
    if (inp.waist_cm && inp.hip_cm) out.whr = Math.round((inp.waist_cm / inp.hip_cm) * 100) / 100;
    if (inp.waist_cm && inp.height_cm) out.whtr = Math.round((inp.waist_cm / inp.height_cm) * 100) / 100;
    return out;
  };

  Engine.calcBmr = function (sex, age, weightKg, data) {
    var band = Engine.ageBand(age);
    var eq = null;
    for (var i = 0; i < data.bmr.length; i++) {
      if (data.bmr[i].sex === sex && data.bmr[i].band === band) { eq = data.bmr[i]; break; }
    }
    if (!eq) return null;
    return eq.slope * weightKg + eq.intercept;
  };

  Engine.goalCalories = function (tee, goal, opts, data) {
    var cfg = data.config, out = { tee: Math.round(tee), blocks: [] };
    if (goal === 'fat_loss') {
      var deficit = (opts.deficit_pct != null ? opts.deficit_pct : data.goal.fat_loss.default_deficit);
      if (deficit > cfg.max_deficit_professional_pct) out.blocks.push('ERR_DEFICIT_HIGH');
      out.deficit_pct = deficit;
      out.kcal = Math.round(tee * (1 - deficit / 100));
    } else if (goal === 'muscle_gain') {
      var exp = opts.experience || 'beginner';
      var surplus = data.goal.muscle_gain[exp] != null ? data.goal.muscle_gain[exp] : 250;
      out.surplus = surplus;
      out.kcal = Math.round(tee + surplus);
    } else {
      out.kcal = Math.round(tee);
    }
    if (out.kcal < cfg.minimum_safe_kcal_adult) out.blocks.push('ERR_KCAL_LOW');
    return out;
  };

  Engine.proteinPerKg = function (goal, opts, data) {
    var pm = data.proteinModes;
    if (goal === 'fat_loss') {
      /* "Higher protein" preset lifts to 1.6 g/kg even without logged training */
      if (opts.high_protein) return { gkg: pm.fat_loss.high, mode: 'Fat_Loss_High_1.6' };
      if (opts.resistance_training) return { gkg: pm.fat_loss.training, mode: 'Fat_Loss_Training_1.4' };
      return { gkg: pm.fat_loss.none, mode: 'Fat_Loss_Light_1.2' };
    }
    if (goal === 'muscle_gain') {
      var exp = opts.experience || 'beginner';
      var g = pm.muscle_gain[exp] || pm.muscle_gain.beginner;
      if (opts.high_protein) g = Math.max(g, pm.muscle_gain.advanced);
      return { gkg: g, mode: 'Muscle_Gain_' + exp + (opts.high_protein ? '_High' : '') };
    }
    /* maintenance: NIN RDA base; cereal-heavy vegetarian uses 1.0 g/kg */
    if (opts.high_protein) return { gkg: pm.maintenance.cereal_based, mode: 'Maintenance_Higher_1.0' };
    if (opts.diet_type === 'veg') return { gkg: pm.maintenance.cereal_based, mode: 'Cereal_Based_1.0' };
    return { gkg: pm.maintenance.base, mode: 'NIN_RDA_0.83' };
  };

  Engine.calcTargets = function (inp, data) {
    var cfg = data.config;
    var bmr = Engine.calcBmr(inp.sex, inp.age, inp.weight_kg, data);
    var pal = data.pal[inp.activity] || 1.4;
    var tee = bmr * pal;
    var goal = Engine.goalCalories(tee, inp.goal, inp, data);
    var kcal = goal.kcal;

    var prot = Engine.proteinPerKg(inp.goal, inp, data);
    var protein_g = Math.round(inp.weight_kg * prot.gkg);

    var fatPct = cfg && inp.fat_pct ? inp.fat_pct : data.fatRules.fat_pct_default;
    var fat_g = Math.round(kcal * (fatPct / 100) / 9);
    var carb_g = Math.round((kcal - protein_g * 4 - fat_g * 9) / 4);
    var fibre_g = inp.lower_fibre
      ? Math.max(20, Math.round(11 * kcal / 1000))   /* comfort / lower-fibre preset */
      : Math.max(25, Math.round(14 * kcal / 1000));  /* Engine_V6 */
    var water_l = Math.max(cfg.water_baseline_l,
      Math.round((inp.weight_kg * 35 / 1000) * 10) / 10);

    var vfCapSex = data.fatRules.visible_fat[inp.sex] || {};
    var vfCap = Math.min(vfCapSex[inp.activity] || 30,
                         Math.round(cfg.visible_fat_cap_2000kcal * kcal / 2000)); /* DGI HFSS scaling */
    var sugarCap = Math.min(cfg.added_sugar_cap_g, Math.round(kcal * cfg.added_sugar_cap_pct / 100 / 4));

    return {
      bmr: Math.round(bmr), pal: pal, tee: Math.round(tee),
      kcal: kcal, deficit_pct: goal.deficit_pct, surplus: goal.surplus,
      protein_g: protein_g, protein_gkg: prot.gkg, protein_mode: prot.mode,
      fat_g: fat_g, fat_pct: fatPct, carb_g: carb_g, fibre_g: fibre_g,
      visible_fat_cap_g: vfCap, sugar_cap_g: sugarCap,
      salt_cap_g: cfg.salt_cap_g, water_l: water_l,
      blocks: goal.blocks
    };
  };

  /* DGI My Plate food-group targets scaled from the 2000 kcal model with
     vegetable/fruit floors and the cereal energy cap (DGI_Target_Food_Groups). */
  Engine.foodGroupTargets = function (kcal, dietType, data) {
    var base = data.dgiBase2000[dietType === 'veg' ? 'veg' : 'nonveg'];
    var k = kcal / 2000, cfg = data.config;
    var cerealKcalPerG = 843 / 250; /* DGI 2000 kcal veg plate */
    return base.map(function (b) {
      var qty = Math.round(b.qty * k);
      if (b.grp === 'Vegetables + GLV') qty = Math.max(cfg.vegetable_floor_g, qty);
      if (b.grp === 'Fruits') qty = Math.max(cfg.fruit_floor_g, qty);
      if (b.grp === 'Cereals incl. millets') {
        var cap = Math.round(cfg.cereal_energy_cap_pct / 100 * kcal / cerealKcalPerG);
        qty = Math.min(qty, cap);
      }
      var o = { grp: b.grp, qty: qty, unit: b.unit };
      if (b.grp === 'Cereals incl. millets') {
        o.whole_grain_min = Math.round(qty * cfg.whole_grain_min_pct / 100);
        o.millet_min = Math.round(qty * cfg.millet_min_pct / 100);
        o.millet_max = Math.round(qty * cfg.millet_max_pct / 100);
      }
      return o;
    });
  };

  /* ------------------------- meal plan generation ----------------------- */
  function recipeById(data, rid) {
    for (var i = 0; i < data.recipes.length; i++) if (data.recipes[i].id === rid) return data.recipes[i];
    return null;
  }

  function pickTemplate(inp, data) {
    var goal = inp.goal === 'maintenance' ? 'fat_loss' : inp.goal; /* maintenance reuses structure at TEE */
    var region = inp.region === 'south' ? 'south' : 'north';       /* general → north pattern */
    var best = null;
    for (var i = 0; i < data.templates.length; i++) {
      var t = data.templates[i];
      if (t.goal === goal && t.diet === inp.diet_type && t.region === region) { best = t; break; }
    }
    if (!best) {
      for (var j = 0; j < data.templates.length; j++) {
        if (data.templates[j].goal === goal && data.templates[j].diet === inp.diet_type) { best = data.templates[j]; break; }
      }
    }
    return best;
  }

  function desiredMealCount(mealsPerDay) {
    var n = Math.round(+mealsPerDay || 4);
    return clamp(n, 1, 5);
  }

  function normalizeMealPlan(plan) {
    var total = plan.reduce(function (sum, m) { return sum + (+m.split || 0); }, 0) || 1;
    plan.forEach(function (m, idx) {
      m.no = idx + 1;
      m.split = Math.round((m.split / total) * 1000) / 1000;
    });
    return plan;
  }

  function mealByName(plan, re) {
    for (var i = 0; i < plan.length; i++) {
      if (!plan[i]) continue;
      if (re.test(plan[i].name || '')) return {
        no: plan[i].no,
        name: plan[i].name,
        split: plan[i].split,
        items: plan[i].items.map(function (it) { return { rid: it.rid, sv: it.sv }; })
      };
    }
    return null;
  }

  /* deep-clone template plan and adapt to requested meals/day */
  function instantiate(tpl, mealsPerDay, data, inp) {
    var count = desiredMealCount(mealsPerDay);
    var plan = tpl.plan.map(function (m) {
      return { no: m.no, name: m.name, split: m.split,
               items: m.items.map(function (it) { return { rid: it.rid, sv: it.sv }; }) };
    });
    if (count >= 5 && plan.length === 4) {
      plan.forEach(function (m) { if (m.no === 4) m.split = Math.max(0.2, m.split - 0.05); });
      plan.push({ no: 5, name: 'Bedtime', split: 0.05, items: [{ rid: 'R012', sv: 1 }] });
      return normalizeMealPlan(plan);
    }
    if (count === 5) return normalizeMealPlan(plan);
    if (count <= 4 && plan.length === 5) {
      var bed = plan.pop();
      plan.forEach(function (m) { if (m.no === 4) m.split += bed.split; });
    }
    if (count === 4) return normalizeMealPlan(plan);

    var breakfast = mealByName(plan, /breakfast/i);
    var lunch = mealByName(plan, /lunch/i) || plan[1] || plan[0];
    var dinner = mealByName(plan, /dinner/i) || plan[plan.length - 1];
    var timing = inp && inp.meal_timing_preference || 'standard';
    var out;
    if (count === 3) {
      out = [breakfast, lunch, dinner].filter(Boolean);
      var split3 = [0.28, 0.38, 0.34];
      out.forEach(function (m, i) { m.split = split3[i] || m.split; });
      return normalizeMealPlan(out);
    }
    if (count === 2) {
      out = [lunch, dinner].filter(Boolean);
      if (out[0]) { out[0].name = timing === 'early' ? 'Meal 1' : 'Lunch'; out[0].split = timing === 'late' ? 0.48 : 0.52; }
      if (out[1]) { out[1].name = timing === 'early' ? 'Meal 2' : 'Dinner'; out[1].split = timing === 'late' ? 0.52 : 0.48; }
      return normalizeMealPlan(out);
    }
    var main = timing === 'late' ? dinner : lunch;
    main = main || lunch || dinner || plan[0];
    main.name = 'Main meal';
    main.split = 1;
    return normalizeMealPlan([main]);
  }

  function recipeVisibleFat(r) {
    if (!r) return 0;
    if (r.ing && r.ing.length) {
      return r.ing.reduce(function (sum, g) { return sum + (g.grp === 'oil' ? (+g.g || 0) : 0); }, 0);
    }
    return +r.vfat || 0;
  }

  function planTotals(plan, data, oilCut) {
    var t = { e: 0, p: 0, f: 0, cb: 0, fb: 0, vfat: 0, ca: 0, fe: 0, na: 0, k: 0, vc: 0, fo: 0 };
    plan.forEach(function (m) {
      m.items.forEach(function (it) {
        var r = recipeById(data, it.rid);
        if (!r) return;
        var cut = ((oilCut && oilCut[it.rid]) || 0) + (+it.oilCutG || 0); /* g visible oil removed per serving */
        var visibleFat = recipeVisibleFat(r);
        cut = Math.min(visibleFat, Math.max(0, cut));
        t.e += (r.e - cut * 9) * it.sv;
        t.p += r.p * it.sv;
        t.f += (r.f - cut) * it.sv;
        t.cb += r.cb * it.sv;
        t.fb += r.fb * it.sv;
        t.vfat += Math.max(0, visibleFat - cut) * it.sv;
        t.ca += (r.ca || 0) * it.sv; t.fe += (r.fe || 0) * it.sv;
        t.na += (r.na || 0) * it.sv; t.k += (r.k || 0) * it.sv;
        t.vc += (r.vc || 0) * it.sv; t.fo += (r.fo || 0) * it.sv;
      });
    });
    return t;
  }

  function roundSv(v) { return Math.round(v * 4) / 4; }
  function vegServingFloor(recipe) {
    if (!recipe || recipe.role !== 'veg') return 0.25;
    return /salad|kachumber|raita|shot/i.test(recipe.name || '') ? 0.5 : 1;
  }

  /* Portion optimizer — priorities from Portion_Optimizer_V6:
     1 protein low → add lean protein; 2 fat high → cut oil then paneer/nuts;
     3 carbs high → cut cereal; 4 energy low → add dairy/fruit then cereal;
     5 energy high → cut cereal/fat-dense; 6 fibre low → add vegetables.
     Floors: veg ≥1 serving, fruit ≥1 serving (DGI floors). */
  Engine.buildPlan = function (inp, targets, data) {
    var tpl = pickTemplate(inp, data);
    if (!tpl) return null;
    var plan = instantiate(tpl, inp.meals_per_day || 4, data, inp);
    var cfg = data.config;
    var oilCut = {}; /* rid -> g oil removed per serving */

    /* initial global scale toward the calorie target */
    var base = planTotals(plan, data, oilCut);
    var scale = targets.kcal / Math.max(base.e, 1);
    scale = Math.min(2.5, Math.max(0.55, scale));
    plan.forEach(function (m) {
      m.items.forEach(function (it) {
        var r = recipeById(data, it.rid);
        var s = it.sv * scale;
        if (r.role === 'veg') s = Math.max(vegServingFloor(r), s);
        if (r.role === 'fruit') s = Math.max(1, Math.min(2, s));
        it.sv = roundSv(Math.max(0.25, s));
      });
    });

    var dietOrder = { veg: ['R004', 'R016', 'R012', 'R015', 'R017', 'R021', 'R009'],
                      egg: ['R010', 'R004', 'R012', 'R016', 'R023', 'R009'],
                      nonveg: ['R011', 'R022', 'R010', 'R004', 'R012', 'R009'] };

    function findItem(pred) {
      var found = null;
      plan.forEach(function (m) {
        m.items.forEach(function (it) {
          var r = recipeById(data, it.rid);
          if (!found && pred(it, r, m)) found = { it: it, r: r, meal: m };
        });
      });
      return found;
    }
    function itemsByRole(role) {
      var out = [];
      plan.forEach(function (m) {
        m.items.forEach(function (it) {
          var r = recipeById(data, it.rid);
          if (r.role === role) out.push({ it: it, r: r });
        });
      });
      return out;
    }

    function cutOil() {
      var done = false;
      plan.forEach(function (m) {
        m.items.forEach(function (it) {
          var r = recipeById(data, it.rid);
          var cur = oilCut[it.rid] || 0;
          if (!done && recipeVisibleFat(r) - cur >= 2.5) { oilCut[it.rid] = cur + 2.5; done = true; }
        });
      });
      return done;
    }
    function cutCereal() {
      /* prefer cutting the cereal with the lowest protein density so protein
         survives energy reductions (rice/poha before chapati/idli) */
      var cer = itemsByRole('cereal').filter(function (x) { return x.it.sv > 0.5; })
        .sort(function (a, b) { return (a.r.p / a.r.e) - (b.r.p / b.r.e); });
      if (!cer.length) return false;
      cer[0].it.sv = roundSv(cer[0].it.sv - 0.25);
      return true;
    }
    function addProtein() {
      var order = dietOrder[inp.diet_type] || dietOrder.veg;
      /* bump an existing lean protein recipe first */
      for (var i = 0; i < order.length; i++) {
        var rid2 = order[i];
        var hit = findItem(function (it) { return it.rid === rid2 && it.sv < 3; });
        if (hit) { hit.it.sv = roundSv(hit.it.sv + 0.25); return true; }
      }
      /* otherwise introduce the leanest allowed protein recipe not in the plan
         (Portion_Optimizer_V6 P1: "add lowest-fat protein option allowed by diet") */
      for (var j = 0; j < order.length; j++) {
        var ridNew = order[j];
        var present = findItem(function (it) { return it.rid === ridNew; });
        var rec = recipeById(data, ridNew);
        if (!present && rec && rec.role !== 'protein_fat') {
          var snack = plan[plan.length >= 3 ? 2 : plan.length - 1];
          snack.items.push({ rid: ridNew, sv: 0.5 });
          return true;
        }
      }
      var fallback = plan[plan.length >= 3 ? 2 : plan.length - 1];
      fallback.items.push({ rid: order[0], sv: 0.5 });
      return true;
    }

    var guard = 0;
    while (guard++ < 120) {
      var t = planTotals(plan, data, oilCut);
      var eLow = t.e < targets.kcal * 0.97, eHigh = t.e > targets.kcal * 1.03;
      var pGap = targets.protein_g - t.p;
      var fHigh = t.f > targets.fat_g * 1.10;
      var cbHigh = t.cb > targets.carb_g * 1.10;
      var fbLow = t.fb < targets.fibre_g - 3;

      /* P8 — visible oil cap inside the loop so any energy lost to oil cuts
         is refilled by P4 with low-fat foods afterwards */
      if (t.vfat > targets.visible_fat_cap_g + 0.5) {
        if (cutOil()) continue;
      }
      if (pGap > 5) {                                             /* P1 */
        if (eHigh) {
          /* swap: trade cereal energy for protein so kcal stays in band */
          cutCereal();
          addProtein();
        } else {
          addProtein();
        }
        continue;
      }
      /* P2 — only when energy is not low: if energy is low AND fat is high,
         adding low-fat carbs (P4) fixes both ratios without oscillating. */
      if (fHigh && !eLow) {
        if (!cutOil()) {
          /* don't dismantle protein sources while protein is still at/below
             target — that ping-pongs with P1 */
          var fatDense = pGap <= 0 ? itemsByRole('protein_fat') : [];
          if (fatDense.length && fatDense[0].it.sv > 0.5) { fatDense[0].it.sv = roundSv(fatDense[0].it.sv - 0.25); }
          else {
            var dairy = itemsByRole('dairy');
            if (dairy.length && dairy[0].it.sv > 0.5) dairy[0].it.sv = roundSv(dairy[0].it.sv - 0.25);
            else break;
          }
        }
        continue;
      }
      if ((cbHigh && !eLow) || eHigh) {                           /* P3/P5 */
        if (cutCereal()) continue;
        var dy = itemsByRole('dairy');
        if (eHigh && dy.length && dy[0].it.sv > 0.5) { dy[0].it.sv = roundSv(dy[0].it.sv - 0.25); continue; }
        var fd2 = itemsByRole('protein_fat');
        if (eHigh && fd2.length && fd2[0].it.sv > 0.5) { fd2[0].it.sv = roundSv(fd2[0].it.sv - 0.25); continue; }
        break;
      }
      if (eLow) {                                                 /* P4 */
        /* fat-aware ordering: when fat is already at/over target, fill the
           energy gap with low-fat options first (fruit, cereal). */
        var fatTight = t.f >= targets.fat_g;
        var pref = fatTight ? ['fruit', 'cereal', 'dairy'] : ['dairy', 'fruit', 'cereal'];
        var bumped = false;
        for (var pi = 0; pi < pref.length && !bumped; pi++) {
          var cand = itemsByRole(pref[pi]).filter(function (x) {
            return x.it.sv < (pref[pi] === 'fruit' ? 2.5 : 4);
          });
          if (cand.length) { cand[0].it.sv = roundSv(cand[0].it.sv + 0.25); bumped = true; }
        }
        if (!bumped) {
          var anyVeg = itemsByRole('veg').filter(function (x) { return x.it.sv < 3.5; });
          if (anyVeg.length) { anyVeg[0].it.sv = roundSv(anyVeg[0].it.sv + 0.25); bumped = true; }
        }
        if (!bumped) break;
        continue;
      }
      if (fbLow) {                                                /* P6 */
        var veg = itemsByRole('veg').filter(function (x) { return x.it.sv < 3; });
        if (veg.length) { veg[0].it.sv = roundSv(veg[0].it.sv + 0.25); continue; }
        break;
      }
      break;
    }

    /* "Lower fibre (comfort)" preset: cut the highest-fibre servings, then refill
       the lost energy with low-fibre dairy / lean protein so the energy
       reconciliation below does not simply scale the high-fibre items back up. */
    if (inp.lower_fibre) {
      var lfBefore = planTotals(plan, data, oilCut);
      var byFibre = [];
      plan.forEach(function (m) { m.items.forEach(function (it) {
        var r = recipeById(data, it.rid);
        if (r.fb && it.sv > 0.5) byFibre.push({ it: it, r: r, load: r.fb * it.sv });
      }); });
      byFibre.sort(function (a, b) { return b.load - a.load; });
      byFibre.slice(0, 3).forEach(function (x) { x.it.sv = roundSv(Math.max(0.5, x.it.sv * 0.6)); });
      var lowFibreProtein = itemsByRole('dairy').concat(
        itemsByRole('protein_lean').filter(function (x) { return !(x.r.fb > 3); }));
      var lfGuard = 0;
      while (lowFibreProtein.length && lfGuard++ < 24) {
        if (planTotals(plan, data, oilCut).e >= lfBefore.e * 0.99) break;
        var pick = lowFibreProtein[lfGuard % lowFibreProtein.length];
        if (pick.it.sv < 4) pick.it.sv = roundSv(pick.it.sv + 0.25); else break;
      }
    }

    /* final reconciliation: energy tolerance is the primary constraint
       (Production_Config_V7 ±3%). If the recipe pool cannot satisfy both the
       energy and protein targets (e.g. low-TEE high-protein vegetarian
       profiles), rescale into the energy band and let WARN_PROTEIN_LOW
       surface the protein shortfall for dietitian review. */
    var tFin = planTotals(plan, data, oilCut);
    if (tFin.e > targets.kcal * 1.03 || tFin.e < targets.kcal * 0.97) {
      var ratio = targets.kcal / tFin.e;
      plan.forEach(function (m) {
        m.items.forEach(function (it) {
          var r = recipeById(data, it.rid);
          var s = it.sv * ratio;
          if (r.role === 'veg') s = Math.max(vegServingFloor(r), s);
          if (r.role === 'fruit') s = Math.max(1, Math.min(2.5, s));
          it.sv = roundSv(Math.min(4, Math.max(0.25, s)));
        });
      });
      /* micro-trim: 0.25-serving rounding and veg/fruit floors can leave the
         rescaled plan just above the band at low calorie targets */
      var mt = 0;
      tFin = planTotals(plan, data, oilCut);
      while (tFin.e > targets.kcal * 1.03 && mt++ < 12) {
        var trimmed2 = false;
        ['cereal', 'dairy', 'protein_fat', 'protein_lean'].forEach(function (role) {
          if (trimmed2) return;
          var cand2 = itemsByRole(role).filter(function (x) { return x.it.sv > 0.5; })
            .sort(function (a, b) { return (b.r.e * b.it.sv) - (a.r.e * a.it.sv); });
          if (cand2.length) { cand2[0].it.sv = roundSv(cand2[0].it.sv - 0.25); trimmed2 = true; }
        });
        if (!trimmed2) break;
        tFin = planTotals(plan, data, oilCut);
      }
    }

    var totals = planTotals(plan, data, oilCut);
    var oilNote = 0;
    Object.keys(oilCut).forEach(function (k2) {
      plan.forEach(function (m) {
        m.items.forEach(function (it) { if (it.rid === k2) oilNote += oilCut[k2] * it.sv; });
      });
    });

	  return {
	    templateId: tpl.id, templateSrc: tpl.src, plan: plan, oilCut: oilCut,
	    oilReducedG: Math.round(oilNote),
      totals: {
        e: Math.round(totals.e), p: Math.round(totals.p * 10) / 10,
        f: Math.round(totals.f * 10) / 10, cb: Math.round(totals.cb * 10) / 10,
        fb: Math.round(totals.fb * 10) / 10, vfat: Math.round(totals.vfat * 10) / 10,
        ca: Math.round(totals.ca), fe: Math.round(totals.fe * 10) / 10,
        na: Math.round(totals.na), k: Math.round(totals.k),
        vc: Math.round(totals.vc), fo: Math.round(totals.fo)
      }
	    };
	  };

	  /* ---------------------- weekly dietitian-style plan --------------------- */
	  var WEEK_DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
	  var DEFAULT_WEEKLY_SLOTS = [
	    { slot_id: 'on_waking', sort_order: 1, display_label: 'On waking', kcal_split_pct: 0, carb_split_pct: 0, required: 'No' },
	    { slot_id: 'breakfast', sort_order: 2, display_label: 'Breakfast', kcal_split_pct: 0.22, carb_split_pct: 0.23, required: 'Yes' },
	    { slot_id: 'mid_morning', sort_order: 3, display_label: 'Mid-morning', kcal_split_pct: 0.10, carb_split_pct: 0.10, required: 'Yes' },
	    { slot_id: 'lunch', sort_order: 4, display_label: 'Lunch', kcal_split_pct: 0.32, carb_split_pct: 0.34, required: 'Yes' },
	    { slot_id: 'evening_snack', sort_order: 5, display_label: 'Evening snack', kcal_split_pct: 0.10, carb_split_pct: 0.10, required: 'Yes' },
	    { slot_id: 'dinner', sort_order: 6, display_label: 'Dinner', kcal_split_pct: 0.26, carb_split_pct: 0.23, required: 'Yes' }
	  ];
	  var DEFAULT_WEEKLY_PATTERNS = {
	    Monday: {
	      breakfast: ['High-protein moong chilla + milk', 'R017,R013'],
	      mid_morning: ['Apple + almonds/walnuts', 'R014,R056'],
	      lunch: ['Palak dal + millet roti + salad + curd', 'R034,R043,R048,R012'],
	      evening_snack: ['Roasted makhana + buttermilk', 'R052,R055'],
	      dinner: ['Vegetable khichdi + cucumber raita', 'R042,R047']
	    },
	    Tuesday: {
	      breakfast: ['Besan chilla + curd', 'R018,R012'],
	      mid_morning: ['Papaya/fruit + pumpkin seeds', 'R014,R056'],
	      lunch: ['Soya chunk curry + phulka + lauki/sabzi + curd', 'R016,R002,R005,R012'],
	      evening_snack: ['Sprouts chaat', 'R021'],
	      dinner: ['Tofu bhurji + steamed vegetables', 'R041,R049']
	    },
	    Wednesday: {
	      breakfast: ['Masala oats + curd', 'R028,R012'],
	      mid_morning: ['Orange/fruit + walnuts', 'R014,R056'],
	      lunch: ['Rajma + brown rice + salad + curd', 'R032,R046,R048,R012'],
	      evening_snack: ['Roasted chana + buttermilk', 'R051,R055'],
	      dinner: ['Paneer tikka + yoghurt dip', 'R060,R055,R048']
	    },
	    Thursday: {
	      breakfast: ['Ragi dosa / idli-sambar plate', 'R025,R020'],
	      mid_morning: ['Guava/fruit + flaxseed/curd', 'R014,R012'],
	      lunch: ['Mixed dal + jowar roti + bhindi/sabzi', 'R036,R044,R005'],
	      evening_snack: ['Hung curd / Greek yogurt bowl', 'R054'],
	      dinner: ['Tofu vegetable stir fry + salad', 'R041,R049,R048']
	    },
	    Friday: {
	      breakfast: ['Poha + sprouts/curd', 'R006,R021,R012'],
	      mid_morning: ['Pear/fruit + almonds', 'R014,R056'],
	      lunch: ['Punjabi chole + roti + salad + curd', 'R033,R002,R048,R012'],
	      evening_snack: ['Roasted makhana + green tea', 'R052'],
	      dinner: ['Lauki chana dal + multigrain roti', 'R035,R043,R048']
	    },
	    Saturday: {
	      breakfast: ['Quinoa/vegetable upma', 'R045,R024'],
	      mid_morning: ['Watermelon/fruit + pumpkin seeds', 'R014,R056'],
	      lunch: ['Paneer bhurji + jowar roti + salad', 'R009,R044,R048'],
	      evening_snack: ['Fruit yogurt', 'R054,R014'],
	      dinner: ['Vegetable daliya / khichdi + curd', 'R027,R042,R012']
	    },
	    Sunday: {
	      breakfast: ['Oats besan chilla + mint chutney', 'R026'],
	      mid_morning: ['Kiwi/fruit + walnuts', 'R014,R056'],
	      lunch: ['Kala chana / chole masala + phulka + curd', 'R033,R002,R012,R048'],
	      evening_snack: ['Roasted chana + lemon water', 'R051'],
	      dinner: ['Millet vegetable pulao + raita', 'R045,R047']
	    }
	  };

	  function wText(v) { return v == null ? '' : String(v); }
	  function wNum(v) {
	    var n = +v;
	    return isFinite(n) ? n : 0;
	  }
	  function wCsv(v) {
	    if (Array.isArray(v)) return v;
	    return wText(v).split(/[,\|/]+/).map(function (x) { return x.trim(); }).filter(Boolean);
	  }
	  function wField(row, keys) {
	    for (var i = 0; i < keys.length; i++) {
	      if (row && row[keys[i]] != null && row[keys[i]] !== '') return row[keys[i]];
	    }
	    return '';
	  }
	  function wLower(v) { return wText(v).toLowerCase(); }
	  function wContains(hay, needle) { return wLower(hay).indexOf(wLower(needle)) >= 0; }
	  function wTokens(v) {
	    return wLower(v).split(/[^a-z0-9]+/).filter(function (x) { return x.length >= 3; });
	  }
	  function wRole(r) { return r && (r.role || r.meal_role_or_plate_role || r.recipe_role || ''); }
	  function wRecipeMetaMap(data) {
	    var out = {};
	    (data.recipeRotationPool || []).forEach(function (row) {
	      var id = wText(wField(row, ['recipe_id', 'id'])); if (id) out[id] = row;
	    });
	    return out;
	  }
	  function wRecipeFamily(r, meta) {
	    var fromMeta = wField(meta, ['protein_family']);
	    if (fromMeta) return wText(fromMeta);
	    var t = wLower([r && r.name, wRole(r)].join(' '));
	    if (/paneer/.test(t)) return 'paneer';
	    if (/tofu/.test(t)) return 'tofu';
	    if (/soy|soya/.test(t)) return 'soy';
	    if (/egg/.test(t)) return 'egg';
	    if (/chicken/.test(t)) return 'chicken';
	    if (/fish|rohu|pomfret/.test(t)) return 'fish';
	    if (/rajma/.test(t)) return 'rajma';
	    if (/chole|chana/.test(t)) return 'chana';
	    if (/dal|moong|sattu|besan|sprout/.test(t)) return 'pulse';
	    if (/curd|milk|yogurt|buttermilk|raita/.test(t)) return 'dairy';
	    if (/nut|almond|walnut|seed/.test(t)) return 'nuts';
	    return wRole(r) || 'mixed';
	  }
	  function wRecipeSlots(r, meta) {
	    var slots = wField(meta, ['meal_slots']);
	    if (slots) return wCsv(slots);
	    var name = wLower(r && r.name), role = wLower(wRole(r));
	    var out = [];
	    var breakfastDish = /chilla|poha|idli|dosa|uttapam|upma|oats|daliya|omelette|paratha|egg bhurji|tofu bhurji|paneer bhurji|yogurt fruit bowl/.test(name);
	    var snackDish = /fruit|makhana|sprout|buttermilk|yogurt|nuts|sattu|milk|amla|roasted chana|chana snack|boiled egg|protein shake/.test(name) || /fruit|nuts/.test(role);
	    var mainDish = /roti|rice|dal|curry|sabzi|paneer|tofu|chicken|fish|pulao|khichdi|raita|kachumber|steamed mixed vegetables|beetroot-carrot salad/.test(name);
	    if (breakfastDish) out.push('breakfast');
	    if (snackDish) out.push('mid_morning', 'evening_snack');
	    if (mainDish && !/chilla|poha|idli|dosa|upma|oats|omelette|paratha|yogurt fruit bowl/.test(name)) out.push('lunch', 'dinner');
	    if (/daliya/.test(name)) out.push('lunch', 'dinner');
	    if (/^curd serving/.test(name)) out.push('breakfast', 'mid_morning', 'lunch', 'evening_snack', 'dinner');
	    if (!out.length && role === 'veg') out.push('lunch', 'dinner');
	    if (!out.length && /cereal|protein/.test(role)) out.push('lunch', 'dinner');
	    if (!out.length) out.push('lunch', 'dinner');
	    return out.filter(function (x, i) { return out.indexOf(x) === i; });
	  }
	  function wDietAllowed(r, meta, inp) {
	    var allowed = wField(meta, ['diet_allowed']);
	    if (allowed) return wCsv(allowed).indexOf(inp.diet_type) >= 0;
	    var d = wLower((r && (r.diet_type || r.d || r.note)) || '');
	    if (inp.diet_type === 'veg') return !/egg|non|chicken|fish|meat/.test(d + ' ' + wLower(r && r.name));
	    if (inp.diet_type === 'egg') return !/non|chicken|fish|meat/.test(d + ' ' + wLower(r && r.name));
	    return true;
	  }
	  function wAllergenTags(r, meta) {
	    var tags = wField(meta, ['allergen_tags']);
	    if (tags) return wCsv(tags);
	    var t = wLower([r && r.name, (r && r.ing || []).map(function (g) { return g.item; }).join(' ')].join(' '));
	    var out = [];
	    if (/paneer|curd|milk|yogurt|buttermilk|raita/.test(t)) out.push('dairy');
	    if (/egg/.test(t)) out.push('egg');
	    if (/soy|soya|tofu/.test(t)) out.push('soy');
	    if (/atta|wheat|phulka|chapati|toast|daliya/.test(t)) out.push('gluten');
	    if (/nut|almond|walnut|peanut|seed/.test(t)) out.push('nuts');
	    if (/fish/.test(t)) out.push('fish');
	    if (/chicken/.test(t)) out.push('chicken');
	    return out;
	  }
	  function wExclusionTerms(inp) {
	    return wTokens([inp.allergies_text, inp.dislikes_text].join(' '));
	  }
	  function wPreferredTerms(inp) {
	    return wTokens(inp.preferred_foods_text || '');
	  }
	  function wRecipeText(r, meta) {
	    return wLower([r && r.name, wField(meta, ['recipe_name']), wAllergenTags(r, meta).join(' '),
	      (r && r.ing || []).map(function (g) { return g.item; }).join(' ')].join(' '));
	  }
	  function wDairyCompatGroup(r, meta) {
	    var text = wRecipeText(r, meta);
	    if (/\b(curd|dahi|yogurt|yoghurt|raita|buttermilk|chaas|lassi)\b/.test(text)) return 'cultured';
	    if (/\bmilk\b/.test(text)) return 'milk';
	    return '';
	  }
	  function wDairyFunction(r, meta) {
	    var text = wRecipeText(r, meta);
	    if (/\b(buttermilk|chaas|lassi)\b/.test(text)) return 'cultured_drink';
	    if (/\b(milk|shake)\b/.test(text)) return 'milk_drink';
	    if (/\b(raita)\b/.test(text)) return 'raita';
	    if (/\b(curd|dahi|yogurt|yoghurt)\b/.test(text)) return 'cultured_side';
	    return '';
	  }
	  function wCompatibilityMode(inp) {
	    var mode = wLower(inp && inp.food_compatibility_mode || 'traditional');
	    return mode === 'standard' ? 'standard' : 'traditional';
	  }
	  function wTraditionalTags(r, meta) {
	    var text = wRecipeText(r, meta);
	    return {
	      milk: /\bmilk\b/.test(text),
	      cultured: /\b(curd|dahi|yogurt|yoghurt|raita|buttermilk|chaas|lassi)\b/.test(text),
	      dairy: /\b(milk|curd|dahi|yogurt|yoghurt|raita|buttermilk|chaas|lassi|paneer)\b/.test(text),
	      fruit: /\b(fruit|apple|banana|mango|papaya|orange|kiwi|amla|gooseberry|grapes|pineapple|melon|watermelon|guava|pear|pomegranate)\b/.test(text),
	      sour: /\b(amla|gooseberry|orange|kiwi|pineapple|lemon|lime|citrus|sour|vinegar|pickle|tamarind)\b/.test(text),
	      banana: /\bbanana\b/.test(text),
	      melon: /\b(melon|watermelon)\b/.test(text),
	      fish: /\b(fish|rohu|pomfret|mackerel|sardine|catla)\b/.test(text),
	      meat: /\b(chicken|meat|mutton|goat)\b/.test(text),
	      egg: /\begg\b/.test(text),
	      heavyPulse: /\b(rajma|chole|chana|soy chunk|sattu|sprout)\b/.test(text)
	    };
	  }
	  function wTraditionalTagConflict(a, b, inp) {
	    if (wCompatibilityMode(inp) === 'standard') return '';
	    if ((a.milk && (b.sour || b.banana || b.melon || b.fish || b.meat)) ||
	        (b.milk && (a.sour || a.banana || a.melon || a.fish || a.meat))) return 'milk with sour fruit, banana, melon, fish or meat';
	    if ((a.cultured && b.fish) || (b.cultured && a.fish)) return 'curd/yogurt with fish';
	    return '';
	  }
	  function wTraditionalSelfConflict(r, meta, inp) {
	    if (wCompatibilityMode(inp) === 'standard') return '';
	    var t = wTraditionalTags(r, meta);
	    if (t.milk && (t.sour || t.banana || t.melon || t.fish || t.meat)) return 'milk with sour fruit, banana, melon, fish or meat';
	    if (t.cultured && t.fish) return 'curd/yogurt with fish';
	    return '';
	  }
	  function wTraditionalCompatibilityScore(r, meta, slotId, inp) {
	    if (wCompatibilityMode(inp) === 'standard') return 0;
	    var t = wTraditionalTags(r, meta), score = 0;
	    if (wTraditionalSelfConflict(r, meta, inp)) score -= 80;
	    if (t.dairy && t.fruit) score -= 7;
	    if (t.cultured && t.fruit) score -= 4;
	    if (/lunch|dinner/.test(slotId || '') && t.fruit) score -= 5;
	    if (/dinner/.test(slotId || '') && t.cultured) score -= 3;
	    if (/dinner/.test(slotId || '') && t.heavyPulse) score -= 4;
	    return score;
	  }
	  function wCerealKind(r, meta) {
	    var text = wRecipeText(r, meta), role = wLower(wRole(r));
	    if (!/cereal/.test(role) && !/\b(chapati|phulka|roti|paratha|rice|poha|idli|dosa|upma|oats|daliya|dalia|khichdi|ragi|rava|semolina|quinoa|pulao)\b/.test(text)) return '';
	    if (/\b(chapati|phulka|roti|paratha)\b/.test(text)) return 'roti';
	    if (/\b(brown rice|plain cooked rice|white rice|curd rice|\brice\b)\b/.test(text)) return 'rice';
	    if (/\b(poha|idli|dosa|upma|oats|daliya|dalia|khichdi|ragi|rava|semolina)\b/.test(text)) return 'breakfast_cereal';
	    return 'cereal';
	  }
	  function wBreakfastMainKind(r, meta) {
	    var text = wRecipeText(r, meta);
	    if (/\b(chilla|poha|idli|dosa|uttapam|upma|oats|daliya|dalia|paratha|ragi|rava)\b/.test(text)) return 'breakfast_main';
	    return '';
	  }
	  function wProteinAnchorKind(r, meta) {
	    var text = wRecipeText(r, meta), role = wLower(wRole(r)), fam = wRecipeFamily(r, meta);
	    if (!/protein|dairy/.test(role) && !/\b(dal|rajma|chole|chana|soy|soya|tofu|paneer|egg|chicken|fish|sattu|sprout|yogurt|curd)\b/.test(text)) return '';
	    if (/rajma|chole|chana|pulse|soy/.test(fam) || /\b(dal|rajma|chole|chana|sattu|sprout|soy chunk)\b/.test(text)) return 'pulse';
	    if (/paneer/.test(fam)) return 'paneer';
	    if (/tofu|soy/.test(fam)) return 'soy';
	    if (/egg/.test(fam)) return 'egg';
	    if (/chicken/.test(fam)) return 'chicken';
	    if (/fish/.test(fam)) return 'fish';
	    if (/dairy/.test(fam) || /\b(curd|yogurt|milk|buttermilk)\b/.test(text)) return 'dairy';
	    return role ? 'protein' : '';
	  }
	  function wVegetableKind(r) {
	    var name = wLower(r && r.name), role = wLower(wRole(r));
	    return role === 'veg' || /\b(salad|sabzi|vegetables|vegetable stir fry)\b/.test(name) ? 'vegetable' : '';
	  }
	  function wMealItemLimit(slotId) {
	    var slot = wLower(slotId || '');
	    if (slot === 'on_waking') return 0;
	    if (/breakfast|mid_morning|evening_snack/.test(slot)) return 2;
	    return 4;
	  }
	  function wFoodCompatibleWithItems(r, meta, items, data, metaMap, slotId, inp) {
	    if (wTraditionalSelfConflict(r, meta, inp)) return false;
	    var family = wRecipeFamily(r, meta);
	    var coherentFruitNuts = false;
	    if ((items || []).length === wMealItemLimit(slotId) && /breakfast|mid_morning|evening_snack/.test(wLower(slotId || ''))) {
	      coherentFruitNuts = (family === 'nuts' && (items || []).some(function (it) {
	        var existing = recipeById(data, it.rid);
	        return existing && wRecipeFamily(existing, (metaMap && metaMap[existing.id]) || {}) === 'fruit';
	      })) || (family === 'fruit' && (items || []).some(function (it) {
	        var existing = recipeById(data, it.rid);
	        return existing && wRecipeFamily(existing, (metaMap && metaMap[existing.id]) || {}) === 'nuts';
	      }));
	    }
	    if ((items || []).length >= wMealItemLimit(slotId) && !coherentFruitNuts) return false;
	    var group = wDairyCompatGroup(r, meta), dairyFunction = wDairyFunction(r, meta);
	    var tags = wTraditionalTags(r, meta);
	    for (var i = 0; i < (items || []).length; i++) {
	      var other = recipeById(data, items[i].rid);
	      if (!other) continue;
	      var otherMeta = (metaMap && metaMap[other.id]) || {};
	      var otherGroup = wDairyCompatGroup(other, otherMeta);
	      var otherFunction = wDairyFunction(other, otherMeta);
	      if ((group === 'milk' && otherGroup === 'cultured') || (group === 'cultured' && otherGroup === 'milk')) return false;
	      if (dairyFunction && otherFunction) {
	        var bothDrinks = /_drink$/.test(dairyFunction) && /_drink$/.test(otherFunction);
	        var bothCulturedSides = /raita|cultured_side/.test(dairyFunction) && /raita|cultured_side/.test(otherFunction);
	        if (bothDrinks || bothCulturedSides || dairyFunction === otherFunction) return false;
	      }
	      if (wTraditionalTagConflict(tags, wTraditionalTags(other, otherMeta), inp)) return false;
	    }
	    var slot = wLower(slotId || ''), isMain = /lunch|dinner/.test(slot);
	    var cereal = wCerealKind(r, meta), breakfastMain = wBreakfastMainKind(r, meta);
	    var protein = wProteinAnchorKind(r, meta), vegetable = wVegetableKind(r);
	    var cerealCount = 0, breakfastMainCount = 0, proteinCount = 0;
	    var dairyCount = 0, pulseProteinCount = 0, vegetableCount = 0;
	    (items || []).forEach(function (it) {
	      var other = recipeById(data, it.rid);
	      if (!other) return;
	      var om = (metaMap && metaMap[other.id]) || {};
	      var oc = wCerealKind(other, om), ob = wBreakfastMainKind(other, om), op = wProteinAnchorKind(other, om);
	      if (oc) cerealCount++;
	      if (ob) breakfastMainCount++;
	      if (op === 'dairy') dairyCount++;
	      else if (op) { proteinCount++; if (op === 'pulse') pulseProteinCount++; }
	      if (wVegetableKind(other)) vegetableCount++;
	    });
	    if (breakfastMain && /breakfast/.test(slot) && breakfastMainCount >= 1) return false;
	    if (cereal) {
	      if (!isMain && cerealCount >= 1) return false;
	      if (isMain && cerealCount >= 1) return false;
	    }
	    if (protein) {
	      if (protein === 'pulse' && pulseProteinCount >= 1) return false;
	      if (protein === 'dairy' && dairyCount >= 1) return false;
	      if (protein !== 'dairy' && isMain && proteinCount >= 1) return false;
	    }
	    if (vegetable && isMain && vegetableCount >= 1) return false;
	    return true;
	  }
	  function wHashScore(v) {
	    var s = wText(v), h = 2166136261;
	    for (var i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), 16777619);
	    return h >>> 0;
	  }
	  function wProfileSeed(inp) {
	    return [
	      inp.age, inp.sex, Math.round(wNum(inp.height_cm)), Math.round(wNum(inp.weight_kg)),
	      Math.round(wNum(inp.waist_cm)), Math.round(wNum(inp.hip_cm)),
	      inp.goal, inp.activity, inp.diet_type, inp.region, inp.meals_per_day,
	      inp.budget_tier, inp.cooking_complexity, inp.weekly_variety_preference, inp.meal_timing_preference,
	      inp.food_compatibility_mode, inp.preferred_foods_text, inp.dislikes_text,
	      (inp.medical_flags || []).join(','), inp.plan_seed
	    ].join('|');
	  }
	  function wStableJitter(inp, slotId, rid, dayIndex) {
	    return (wHashScore(wProfileSeed(inp) + '|' + slotId + '|' + rid + '|' + (dayIndex || 0)) % 53) - 26;
	  }
	  function wVariantAffinity(inp, slotId, rid) {
	    var lane = wHashScore(wProfileSeed(inp) + '|recipe-lane') % 4;
	    var recipeLane = wHashScore(slotId + '|' + rid) % 4;
	    return lane === recipeLane ? 21 : -6;
	  }
	  function wDislikeGroups(inp) {
	    var t = wLower(inp.dislikes_text || ''), out = {};
	    if (/\b(curd|dahi|yogurt|yoghurt|raita|buttermilk|chaas|lassi)\b/.test(t)) out.curdFamily = true;
	    if (/\b(dairy|milk|lactose)\b/.test(t)) out.dairy = true;
	    if (/\bpaneer\b/.test(t)) out.paneer = true;
	    if (/\b(soy|soya|tofu)\b/.test(t)) out.soy = true;
	    if (/\b(egg|omelette|bhurji)\b/.test(t)) out.egg = true;
	    if (/\b(chicken)\b/.test(t)) out.chicken = true;
	    if (/\b(fish|rohu|pomfret|mackerel|sardine)\b/.test(t)) out.fish = true;
	    if (/\b(nut|nuts|almond|walnut|peanut|groundnut|seed|seeds)\b/.test(t)) out.nuts = true;
	    return out;
	  }
	  function wExcluded(r, meta, inp) {
	    var text = wRecipeText(r, meta);
	    var terms = wExclusionTerms(inp);
	    for (var i = 0; i < terms.length; i++) {
	      if (text.indexOf(terms[i]) >= 0) return true;
	    }
	    var tags = wAllergenTags(r, meta), dislikes = wDislikeGroups(inp);
	    if (/\b(dairy|milk|lactose)\b/.test(wLower(inp.allergies_text || '')) && tags.indexOf('dairy') >= 0) return true;
	    if (dislikes.dairy && tags.indexOf('dairy') >= 0) return true;
	    if (dislikes.curdFamily && /\b(curd|dahi|yogurt|yoghurt|raita|buttermilk|chaas|lassi)\b/.test(text)) return true;
	    if (dislikes.paneer && /\bpaneer\b/.test(text)) return true;
	    if (dislikes.soy && tags.indexOf('soy') >= 0) return true;
	    if (dislikes.egg && tags.indexOf('egg') >= 0) return true;
	    if (dislikes.chicken && tags.indexOf('chicken') >= 0) return true;
	    if (dislikes.fish && tags.indexOf('fish') >= 0) return true;
	    if (dislikes.nuts && tags.indexOf('nuts') >= 0) return true;
	    return false;
	  }
	  function wConfidenceScore(r, meta) {
	    var q = wLower([r && r.conf, wField(meta, ['confidence']), wField(meta, ['qa_status'])].join(' '));
	    if (/verified|high/.test(q)) return 20;
	    if (/estimated/.test(q)) return -8;
	    return 0;
	  }
	  function wWeeklyRecipeTarget(inp) {
	    var preference = wLower(inp && inp.weekly_variety_preference || 'balanced');
	    if (preference === 'repeat_friendly') return 20;
	    if (preference === 'maximum') return 28;
	    return 24;
	  }
	  function wPreparedRecipeTarget(inp) {
	    var preference = wLower(inp && inp.weekly_variety_preference || 'balanced');
	    if (preference === 'repeat_friendly') return 14;
	    if (preference === 'maximum') return 22;
	    return 18;
	  }
	  function wNeedsWeeklyPrep(r) {
	    var role = wLower(wRole(r)), name = wLower(r && r.name);
	    if (/fruit|dairy|other/.test(role)) return false;
	    if (/plain cooked rice|brown rice, cooked|chapati|phulka/.test(name)) return false;
	    return true;
	  }
	  function wMaxFrequency(r, meta, inp) {
	    var n = wNum(wField(meta, ['max_weekly_frequency']));
	    var fam = wRecipeFamily(r, meta), role = wLower(wRole(r)), name = wLower(r && r.name);
	    var inferred = /tofu bhurji/.test(name) ? 7 : /greek yogurt fruit bowl/.test(name) ? 5 :
	      /mixed dal tadka/.test(name) ? 3 : /paneer|chicken|fish/.test(fam) ? 2 :
	      fam === 'egg' ? 4 : /dairy|fruit/.test(fam) ? 5 : role === 'cereal' ? 4 : 3;
	    var sourceCap = n || inferred;
	    var preference = wLower(inp && inp.weekly_variety_preference || 'balanced');
	    var preferredCap;
	    if (preference === 'repeat_friendly') {
	      preferredCap = /paneer|chicken|fish/.test(fam) || role === 'protein_fat' ? 2 :
	        /fruit/.test(fam) ? 7 : /tofu bhurji/.test(name) ? 7 : /dairy|cereal/.test(fam) ? 6 : 4;
	    } else if (preference === 'maximum') {
	      preferredCap = /fruit/.test(fam) ? 4 : /dairy|cereal/.test(fam) ? 3 : 2;
	    } else {
	      preferredCap = /paneer|chicken|fish/.test(fam) || role === 'protein_fat' ? 2 :
	        /fruit/.test(fam) ? 6 : /tofu bhurji/.test(name) ? 7 : /dairy|cereal/.test(fam) ? 5 : 3;
	    }
	    return Math.max(1, Math.min(sourceCap, preferredCap));
	  }
	  function wInferredRegionScore(r, meta, inp) {
	    var region = wLower(inp.region || 'north');
	    if (!region || region === 'general') return 0;
	    var text = wRecipeText(r, meta), score = 0;
	    var south = /\b(idli|dosa|sambar|uttapam|upma|ragi|rasam|curd rice|lemon rice)\b/;
	    var north = /\b(roti|phulka|paratha|rajma|chole|palak|paneer|bajra|jowar|chilla|punjabi)\b/;
	    if (region === 'south') {
	      if (south.test(text)) score += 20;
	      if (/\b(rice|coconut|tofu|sprout)\b/.test(text)) score += 6;
	      if (north.test(text)) score -= 10;
	    } else if (region === 'north') {
	      if (north.test(text)) score += 14;
	      if (/\b(dal|khichdi|sattu|soy chunk|makhana)\b/.test(text)) score += 4;
	      if (south.test(text)) score -= 12;
	    }
	    return score;
	  }
	  function wInferredGoalScore(r, meta, inp) {
	    var goal = wLower(inp.goal || 'maintenance');
	    var text = wRecipeText(r, meta), role = wLower(wRole(r)), fam = wRecipeFamily(r, meta);
	    var e = wNum(r && r.e), p = wNum(r && r.p), f = wNum(r && r.f), fb = wNum(r && r.fb);
	    var density = e ? p * 100 / e : 0, score = 0;
	    if (goal === 'fat_loss') {
	      if (p >= 12) score += 6;
	      if (density >= 6) score += 5;
	      if (fb >= 5) score += 4;
	      if (e && e <= 180) score += 3;
	      if (e >= 420) score -= 8;
	      if (f >= 16) score -= 10;
	      if (/paneer|nuts/.test(fam) || role === 'protein_fat') score -= 7;
	      if (/\b(fried|paratha|pulao|shake)\b/.test(text)) score -= 4;
	    } else if (goal === 'muscle_gain') {
	      if (p >= 15) score += 10;
	      else if (p >= 9) score += 5;
	      if (e >= 250) score += 4;
	      if (/paneer|tofu|soy|egg|chicken|fish|dairy|pulse|chana/.test(fam)) score += 7;
	      if (/shake|sattu|milk|curd|yogurt/.test(text)) score += 4;
	      if (/fruit|veg/.test(role) && p < 4) score -= 4;
	    } else {
	      if (e >= 120 && e <= 420) score += 3;
	      if (p >= 6) score += 2;
	      if (fb >= 3) score += 2;
	    }
	    return score;
	  }
	  function wInferredBudgetComplexityScore(r, meta, inp) {
	    var text = wRecipeText(r, meta), score = 0;
	    var budget = wLower(inp.budget_tier || 'flexible');
	    var complexity = wLower(inp.cooking_complexity || 'flexible');
	    if (budget === 'budget') {
	      if (/\b(dal|chana|sattu|poha|roti|phulka|rice|khichdi|curd|buttermilk|sprout|makhana)\b/.test(text)) score += 9;
	      if (/\b(quinoa|paneer|almond|walnut|cashew|greek yogurt|fish|chicken|shake)\b/.test(text)) score -= 11;
	    } else if (budget === 'premium') {
	      if (/\b(quinoa|paneer|almond|walnut|greek yogurt|fish|chicken|tofu)\b/.test(text)) score += 5;
	    }
	    if (complexity === 'easy') {
	      if (/\b(fruit|milk|curd|buttermilk|roasted|makhana|chana|sattu|poha|upma|khichdi|roti)\b/.test(text)) score += 8;
	      if (/\b(tikka|bhurji|curry|pulao|stuffed|stir fry|chilla|dosa|sambar)\b/.test(text)) score -= 7;
	    }
	    return score;
	  }
	  var DGI_GRP_MAP = {
	    cereal: 'Cereals incl. millets', pulse: 'Pulses', protein: 'Chicken/meat/eggs',
	    dairy: 'Milk/curd', dairy_ml: 'Milk/curd', dairy_g: 'Milk/curd',
	    veg: 'Vegetables + GLV', glv: 'Vegetables + GLV', fruit: 'Fruits',
	    nuts: 'Nuts & seeds', oil: 'Fats & oils'
	  };
	  function wGroupTargetMap(inp, targets, data) {
	    var out = {};
	    try {
	      Engine.foodGroupTargets(targets.kcal, inp.diet_type, data).forEach(function (g) {
	        out[g.grp] = g.qty;
	      });
	    } catch (e) {}
	    return out;
	  }
	  function wRecipeGroupGrams(r, sv) {
	    var out = {}, mult = sv == null ? 1 : sv;
	    (r && r.ing || []).forEach(function (g) {
	      var grp = DGI_GRP_MAP[g.grp] || '';
	      if (!grp) return;
	      out[grp] = (out[grp] || 0) + (g.g || 0) * mult;
	    });
	    return out;
	  }
	  function wAddGroupGrams(target, grams) {
	    Object.keys(grams || {}).forEach(function (grp) {
	      target[grp] = (target[grp] || 0) + grams[grp];
	    });
	  }
	  function wFoodGroupNeedScore(r, state) {
	    if (!state || !state.groupTargets) return 0;
	    var contrib = wRecipeGroupGrams(r, 1), score = 0;
	    Object.keys(contrib).forEach(function (grp) {
	      var target = state.groupTargets[grp] || 0;
	      if (!target) return;
	      var planned = (state.dayGroupGrams && state.dayGroupGrams[grp]) || 0;
	      var need = target - planned;
	      var impact = Math.min(1.4, contrib[grp] / Math.max(20, target * 0.35));
	      if (need > target * 0.35) score += Math.round(14 * impact);
	      else if (need > target * 0.10) score += Math.round(7 * impact);
	      else if (planned > target * 1.15) score -= Math.round(10 * impact);
	      if (/Pulses/.test(grp) && planned > target * 1.05) score -= Math.round(8 * impact);
	      if (/Fats/.test(grp) && planned > target * 0.85) score -= Math.round(10 * impact);
	      if (/Fruits/.test(grp) && planned > target * 1.15) score -= Math.round(8 * impact);
	    });
	    return clamp(score, -24, 24);
	  }
	  function wRegisterCellState(cell, data, state) {
	    if (!cell || !state) return;
	    state.slotUse = state.slotUse || {};
	    state.slotFamilyUse = state.slotFamilyUse || {};
	    state.dayGroupGrams = state.dayGroupGrams || {};
	    state.weekGroupGrams = state.weekGroupGrams || {};
	    state.slotUse[cell.slotId] = state.slotUse[cell.slotId] || {};
	    state.slotFamilyUse[cell.slotId] = state.slotFamilyUse[cell.slotId] || {};
	    (cell.items || []).forEach(function (it) {
	      var r = recipeById(data, it.rid), meta = state.metaMap && state.metaMap[it.rid] || {};
	      if (!r) return;
	      var fam = wRecipeFamily(r, meta);
	      state.slotUse[cell.slotId][r.id] = (state.slotUse[cell.slotId][r.id] || 0) + 1;
	      state.slotFamilyUse[cell.slotId][fam] = (state.slotFamilyUse[cell.slotId][fam] || 0) + 1;
	      var grams = wRecipeGroupGrams(r, it.sv);
	      wAddGroupGrams(state.dayGroupGrams, grams);
	      wAddGroupGrams(state.weekGroupGrams, grams);
	    });
	  }
	  function wRecipeCandidateScore(r, meta, slotId, inp, state, dayIndex) {
	    var pref = wPreferredTerms(inp);
	    var used = state && state.recipeUse && state.recipeUse[r.id] || 0;
	    var usedRecently = state && state.previousRecipeUse && state.previousRecipeUse[r.id] || 0;
	    var fam = wRecipeFamily(r, meta);
	    var slotUsed = state && state.slotUse && state.slotUse[slotId] && state.slotUse[slotId][r.id] || 0;
	    var slotFamUsed = state && state.slotFamilyUse && state.slotFamilyUse[slotId] && state.slotFamilyUse[slotId][fam] || 0;
	    var score = 50 + wConfidenceScore(r, meta) - used * 18 - slotUsed * 22 - slotFamUsed * 7 - usedRecently * 30;
	    if (fam && state && state.dayFamilies && state.dayFamilies[fam] && !/dairy|fruit|cereal|veg|nuts/.test(fam)) score -= 22;
	    var region = wLower(wField(meta, ['region']) || r.region || '');
	    if (!region || /all india/.test(region)) score += 4;
	    else if (inp.region && region.indexOf(inp.region) >= 0) score += 8;
	    var goal = wLower(wField(meta, ['goal_fit']) || r.note || '');
	    if (!goal || /both|general/.test(goal)) score += 4;
	    else if (goal.indexOf(inp.goal) >= 0) score += 8;
	    var complexity = wLower(wField(meta, ['complexity']));
	    if (inp.cooking_complexity && inp.cooking_complexity !== 'flexible' && complexity && complexity !== inp.cooking_complexity) score -= 8;
	    var budget = wCsv(wField(meta, ['budget_tier']));
	    if (inp.budget_tier && inp.budget_tier !== 'flexible' && budget.length && budget.indexOf(inp.budget_tier) < 0) score -= 8;
	    var text = wRecipeText(r, meta);
	    pref.forEach(function (p) { if (text.indexOf(p) >= 0) score += 12; });
	    score += wInferredRegionScore(r, meta, inp);
	    score += wInferredGoalScore(r, meta, inp);
	    score += wInferredBudgetComplexityScore(r, meta, inp);
	    score += wFoodGroupNeedScore(r, state);
	    score += wTraditionalCompatibilityScore(r, meta, slotId, inp);
	    score += wStableJitter(inp, slotId, r.id, dayIndex);
	    score += wVariantAffinity(inp, slotId, r.id);
	    if (used >= wMaxFrequency(r, meta, inp)) score -= 35;
	    return score;
	  }
	  function wCandidateRecipes(slotId, inp, data, state, dayIndex) {
	    var metaMap = wRecipeMetaMap(data);
	    return (data.recipes || []).map(function (r) {
	      var meta = metaMap[r.id] || {};
	      if (wRecipeSlots(r, meta).indexOf(slotId) < 0) return null;
	      if (!wDietAllowed(r, meta, inp) || wExcluded(r, meta, inp)) return null;
	      return { r: r, meta: meta, score: wRecipeCandidateScore(r, meta, slotId, inp, state, dayIndex) };
	    }).filter(Boolean).sort(function (a, b) { return b.score - a.score; });
	  }
	  function wDefaultPatternRows() {
	    var rows = [];
	    WEEK_DAYS.forEach(function (day, idx) {
	      rows.push({ day_index: idx + 1, day_name: day, slot_id: 'on_waking', pattern_label: 'Medication / hydration cue', preferred_recipe_ids: '' });
	      Object.keys(DEFAULT_WEEKLY_PATTERNS[day]).forEach(function (slotId) {
	        var p = DEFAULT_WEEKLY_PATTERNS[day][slotId];
	        rows.push({ day_index: idx + 1, day_name: day, slot_id: slotId, pattern_label: p[0], preferred_recipe_ids: p[1], allow_recipe_ids: p[1] });
	      });
	    });
	    return rows;
	  }
	  function wSlotRows(data) {
	    var rows = data.weeklyMealSlots && data.weeklyMealSlots.length ? data.weeklyMealSlots : DEFAULT_WEEKLY_SLOTS;
	    return rows.map(function (s) {
	      return {
	        slot_id: wText(wField(s, ['slot_id'])),
	        sort_order: wNum(wField(s, ['sort_order'])) || 99,
	        display_label: wText(wField(s, ['display_label'])) || wText(wField(s, ['slot_id'])),
	        kcal_split_pct: wNum(wField(s, ['kcal_split_pct'])),
	        carb_split_pct: wNum(wField(s, ['carb_split_pct'])),
	        required: wText(wField(s, ['required'])) || 'Yes'
	      };
	    }).sort(function (a, b) { return a.sort_order - b.sort_order; });
	  }
	  function wSlotsForMealCount(rows, inp) {
	    var count = desiredMealCount(inp && inp.meals_per_day);
	    var timing = inp && inp.meal_timing_preference || 'standard';
	    var mainSlot = timing === 'late' ? 'dinner' : 'lunch';
	    var config = count === 1
	      ? { ids: ['on_waking', mainSlot], split: {}, label: {} }
	      : count === 2
	        ? { ids: ['on_waking', 'lunch', 'dinner'], split: { lunch: timing === 'late' ? 0.48 : 0.52, dinner: timing === 'late' ? 0.52 : 0.48 }, label: {} }
	        : count === 3
	          ? { ids: ['on_waking', 'breakfast', 'lunch', 'dinner'], split: { breakfast: 0.28, lunch: 0.38, dinner: 0.34 }, label: {} }
	          : count === 4
	            ? { ids: ['on_waking', 'breakfast', 'lunch', 'evening_snack', 'dinner'], split: { breakfast: 0.25, lunch: 0.35, evening_snack: 0.10, dinner: 0.30 }, label: {} }
	            : { ids: ['on_waking', 'breakfast', 'mid_morning', 'lunch', 'evening_snack', 'dinner'], split: { breakfast: 0.22, mid_morning: 0.10, lunch: 0.32, evening_snack: 0.10, dinner: 0.26 }, label: {} };
	    if (count === 1) {
	      config.split[mainSlot] = 1;
	      config.label[mainSlot] = 'Main meal';
	    }
	    var order = {};
	    config.ids.forEach(function (id, idx) { order[id] = idx + 1; });
	    return rows.filter(function (s) { return order[s.slot_id]; }).map(function (s) {
	      var out = {
	        slot_id: s.slot_id,
	        sort_order: order[s.slot_id] || s.sort_order,
	        display_label: config.label[s.slot_id] || s.display_label,
	        kcal_split_pct: s.slot_id === 'on_waking' ? 0 : (config.split[s.slot_id] != null ? config.split[s.slot_id] : s.kcal_split_pct),
	        carb_split_pct: s.slot_id === 'on_waking' ? 0 : (config.split[s.slot_id] != null ? config.split[s.slot_id] : s.carb_split_pct),
	        required: s.required
	      };
	      return out;
	    }).sort(function (a, b) { return a.sort_order - b.sort_order; });
	  }
	  function wPatternRows(data) {
	    return data.weeklyMealPatterns && data.weeklyMealPatterns.length ? data.weeklyMealPatterns : wDefaultPatternRows();
	  }
	  function wPatternMap(data) {
	    var out = {};
	    wPatternRows(data).forEach(function (p) {
	      var day = wText(wField(p, ['day_name']));
	      var slot = wText(wField(p, ['slot_id']));
	      if (day && slot) out[day + '|' + slot] = p;
	    });
	    return out;
	  }
	  function wDietSpecificIds(ids, inp, slotId, dayIndex) {
	    if (!/lunch|dinner|breakfast|evening_snack/.test(slotId)) return ids;
	    var nonveg = [['R038'], ['R040'], ['R011'], ['R022']];
	    var egg = [['R030'], ['R039'], ['R053'], ['R023']];
	    if (inp.diet_type === 'nonveg' && /lunch|dinner/.test(slotId) && dayIndex % 3 === 1) return nonveg[dayIndex % nonveg.length].concat(ids.slice(1));
	    if (inp.diet_type === 'egg' && /breakfast|lunch|dinner|evening_snack/.test(slotId) && dayIndex % 3 === 0) return egg[dayIndex % egg.length].concat(ids.slice(1));
	    return ids;
	  }
	  function wRoleCompatible(a, b) {
	    a = wLower(a); b = wLower(b);
	    if (!a || !b || a.indexOf(b) >= 0 || b.indexOf(a) >= 0) return true;
	    if (/protein|dairy|pulse|egg|non/.test(a) && /protein|dairy|pulse|egg|non/.test(b)) return true;
	    if (/cereal/.test(a) && /cereal/.test(b)) return true;
	    if (/veg|fruit/.test(a) && /veg|fruit/.test(b)) return true;
	    if (/snack/.test(a) && /snack|fruit|dairy|nuts|protein/.test(b)) return true;
	    return false;
	  }
	  function wPersonalizationThreshold(inp) {
	    var threshold = 9;
	    if (inp.region && inp.region !== 'general') threshold -= 3;
	    if (inp.goal && inp.goal !== 'maintenance') threshold -= 2;
	    if (inp.budget_tier && inp.budget_tier !== 'flexible') threshold -= 2;
	    if (inp.cooking_complexity && inp.cooking_complexity !== 'flexible') threshold -= 2;
	    if (wPreferredTerms(inp).length || wExclusionTerms(inp).length) threshold -= 2;
	    return Math.max(3, threshold);
	  }
	  function wPickReplacement(slotId, inp, data, state, avoidIds, roleHint, familyHint, dayIndex, currentItems, metaMap) {
	    metaMap = metaMap || wRecipeMetaMap(data);
	    var list = wCandidateRecipes(slotId, inp, data, state, dayIndex).filter(function (x) {
	      if (avoidIds.indexOf(x.r.id) >= 0) return false;
	      if (familyHint && wRecipeFamily(x.r, x.meta) === familyHint && state.dayFamilies[familyHint]) return false;
	      if (roleHint && wLower(wRole(x.r)).indexOf(wLower(roleHint)) < 0 && x.score < 70) return false;
	      if (!wFoodCompatibleWithItems(x.r, x.meta, currentItems || [], data, metaMap, slotId, inp)) return false;
	      return true;
	    });
	    return list.length ? list[0].r.id : null;
	  }
	  function wMaybePersonalizeRid(rid, slotId, dayIndex, inp, data, state, avoidIds, currentItems, metaMap) {
	    var r = recipeById(data, rid), meta = metaMap[rid] || {};
	    if (!r || !wDietAllowed(r, meta, inp) || wExcluded(r, meta, inp) ||
	        ((state.recipeUse[rid] || 0) >= wMaxFrequency(r, meta, inp)) ||
	        !wFoodCompatibleWithItems(r, meta, currentItems || [], data, metaMap, slotId, inp)) {
	      return wPickReplacement(slotId, inp, data, state, avoidIds, r && wRole(r), r && wRecipeFamily(r, meta), dayIndex, currentItems, metaMap);
	    }
	    var ownScore = wRecipeCandidateScore(r, meta, slotId, inp, state, dayIndex);
	    var roleHint = wRole(r), familyHint = wRecipeFamily(r, meta);
	    var repeatedFamily = familyHint && state.dayFamilies[familyHint] && !/dairy|fruit|cereal|veg|nuts/.test(familyHint);
	    var list = wCandidateRecipes(slotId, inp, data, state, dayIndex).filter(function (x) {
	      if (x.r.id === rid || avoidIds.indexOf(x.r.id) >= 0) return false;
	      var fam = wRecipeFamily(x.r, x.meta);
	      if (fam && state.dayFamilies[fam] && !/dairy|fruit|cereal|veg|nuts/.test(fam)) return false;
	      if (!wRoleCompatible(roleHint, wRole(x.r)) && x.score < ownScore + 12) return false;
	      if (!wFoodCompatibleWithItems(x.r, x.meta, currentItems || [], data, metaMap, slotId, inp)) return false;
	      return true;
	    });
	    if (!list.length) return rid;
	    var best = list[0], threshold = repeatedFamily ? 2 : wPersonalizationThreshold(inp);
	    if (best.score >= ownScore + threshold) return best.r.id;
	    var ownRegionScore = wInferredRegionScore(r, meta, inp);
	    var tiePool = list.filter(function (x) {
	      return x.score >= ownScore - 14 &&
	        wRoleCompatible(roleHint, wRole(x.r)) &&
	        wInferredRegionScore(x.r, x.meta, inp) >= ownRegionScore - 1;
	    }).slice(0, 8);
	    var tieHash = wHashScore(wProfileSeed(inp) + '|tie|' + slotId + '|' + rid + '|' + dayIndex);
	    var tiePick = tiePool.length ? tiePool[tieHash % tiePool.length] : null;
	    var tieRotation = tiePick && (tieHash % 5 !== 0 || (state.previousRecipeUse && state.previousRecipeUse[rid]));
	    return tieRotation ? tiePick.r.id : rid;
	  }
	  function wCellTotals(items, data) {
	    return planTotals([{ items: items }], data, {});
	  }
	  function wScaleItems(items, slot, targets, data, inp) {
	    if (!items.length || !slot.kcal_split_pct) return items;
	    var targetEnergy = targets.kcal * slot.kcal_split_pct;
	    var t = wCellTotals(items, data);
	    var ratio = t.e ? targetEnergy / t.e : 1;
	    var maxRatio = /snack|morning/.test(slot.slot_id) ? 1.7 : 2.25;
	    if (slot.kcal_split_pct >= 0.75) maxRatio = 3.5;
	    else if (slot.kcal_split_pct >= 0.45) maxRatio = 2.75;
	    ratio = clamp(ratio, 0.45, maxRatio);
	    items.forEach(function (it) {
	      var r = recipeById(data, it.rid);
	      var s = it.sv * ratio;
	      var role = wLower(wRole(r));
	      if (role === 'fruit') s = clamp(s, 0.75, 2);
	      else if (role === 'veg') s = clamp(s, 0.5, slot.kcal_split_pct >= 0.75 ? 3.5 : 2.5);
	      else if (role === 'protein_lean' || role === 'dairy') s = clamp(s, 0.25, slot.kcal_split_pct >= 0.75 ? 3.25 : 2);
	      else if (role === 'protein_fat') s = clamp(s, 0.25, slot.kcal_split_pct >= 0.75 ? 2 : 1.5);
	      else if (r && /nuts|snack/.test(wRecipeFamily(r, {}))) s = clamp(s, 0.25, 1.25);
	      else if (role === 'cereal') s = clamp(s, 0.25, slot.kcal_split_pct >= 0.75 ? 4 : slot.kcal_split_pct >= 0.45 ? 2.75 : 2.5);
	      else s = clamp(s, 0.25, slot.kcal_split_pct >= 0.75 ? 3 : 2);
	      if (r) s = Math.min(s, wPracticalServingCap(r, inp || {}, slot.slot_id));
	      it.sv = roundSv(s);
	    });
	    return items;
	  }
	  function wBuildCell(dayName, dayIndex, slot, pattern, inp, targets, data, state) {
	    if (slot.slot_id === 'on_waking') {
	      var note = wText(inp.on_waking_note).trim();
	      if (!note && inp.medical_flags && inp.medical_flags.length) note = 'Medication/water as advised';
	      if (!note) note = inp.meal_timing_preference === 'early' ? 'Water after waking' : 'Water';
	      return { slotId: slot.slot_id, label: slot.display_label, text: note, items: [], totals: wCellTotals([], data), confidence: 'instruction' };
	    }
	    var ids = wCsv(wField(pattern, ['preferred_recipe_ids', 'allow_recipe_ids']));
	    ids = wDietSpecificIds(ids, inp, slot.slot_id, dayIndex);
	    var metaMap = state.metaMap || wRecipeMetaMap(data), items = [], avoid = [];
	    ids.forEach(function (rid) {
	      var useRid = wMaybePersonalizeRid(rid, slot.slot_id, dayIndex, inp, data, state, avoid, items, metaMap);
	      var r = useRid ? recipeById(data, useRid) : null, meta = useRid ? (metaMap[useRid] || {}) : {};
	      if (r && avoid.indexOf(r.id) < 0 && wFoodCompatibleWithItems(r, meta, items, data, metaMap, slot.slot_id, inp)) {
	        var fam = wRecipeFamily(r, meta);
	        if (!/dairy|fruit|cereal|veg|nuts/.test(fam)) state.dayFamilies[fam] = 1;
	        items.push({ rid: r.id, sv: 1 });
	        avoid.push(r.id);
	        state.recipeUse[r.id] = (state.recipeUse[r.id] || 0) + 1;
	      }
	    });
	    if (!items.length) {
	      var repl = wPickReplacement(slot.slot_id, inp, data, state, [], '', '', dayIndex, [], metaMap);
	      if (repl) {
	        items.push({ rid: repl, sv: 1 });
	        state.recipeUse[repl] = (state.recipeUse[repl] || 0) + 1;
	      }
	    }
	    wScaleItems(items, slot, targets, data, inp);
	    var totals = wCellTotals(items, data);
	    var est = items.some(function (it) {
	      var r2 = recipeById(data, it.rid), m2 = metaMap[it.rid] || {};
	      return /estimated/i.test([r2 && r2.conf, wField(m2, ['confidence']), wField(m2, ['qa_status']), wField(m2, ['warning_tags'])].join(' '));
	    });
	    var cell = {
	      slotId: slot.slot_id, label: slot.display_label, pattern: wField(pattern, ['pattern_label']) || slot.display_label,
	      exchangeTargets: wField(pattern, ['exchange_targets']), items: items, totals: totals,
	      confidence: est ? 'estimated recipes included' : 'verified/high-confidence preferred'
	    };
	    wRegisterCellState(cell, data, state);
	    return cell;
	  }
	  function wFormatTotals(t) {
	    return {
	      e: Math.round(t.e || 0), p: Math.round((t.p || 0) * 10) / 10,
	      f: Math.round((t.f || 0) * 10) / 10, cb: Math.round((t.cb || 0) * 10) / 10,
	      fb: Math.round((t.fb || 0) * 10) / 10, vfat: Math.round((t.vfat || 0) * 10) / 10,
	      ca: Math.round(t.ca || 0), fe: Math.round((t.fe || 0) * 10) / 10,
	      na: Math.round(t.na || 0), k: Math.round(t.k || 0),
	      vc: Math.round(t.vc || 0), fo: Math.round(t.fo || 0)
	    };
	  }
	  function wSumTotals(list) {
	    var t = { e: 0, p: 0, f: 0, cb: 0, fb: 0, vfat: 0, ca: 0, fe: 0, na: 0, k: 0, vc: 0, fo: 0 };
	    list.forEach(function (x) {
	      var tx = x.totals || x;
	      Object.keys(t).forEach(function (k) { t[k] += tx[k] || 0; });
	    });
	    return t;
	  }
	  function wRecomputeWeekly(weekly, data) {
	    var weekTotals = { e: 0, p: 0, f: 0, cb: 0, fb: 0, vfat: 0, ca: 0, fe: 0, na: 0, k: 0, vc: 0, fo: 0 };
	    weekly.days.forEach(function (d) {
	      d.totals = wFormatTotals(wSumTotals(d.slots));
	      Object.keys(weekTotals).forEach(function (k) { weekTotals[k] += d.totals[k] || 0; });
	    });
	    weekly.totals = wFormatTotals(weekTotals);
	    var avg = {};
	    Object.keys(weekTotals).forEach(function (k) { avg[k] = weekTotals[k] / 7; });
	    weekly.avgTotals = wFormatTotals(avg);
	    return weekly;
	  }
	  function wSwapOptions(cell, slot, inp, targets, data, state) {
	    if (!cell.items.length || slot.slot_id === 'on_waking') return [];
	    var current = cell.items.map(function (it) { return it.rid; });
	    var primaryIdx = 0, metaMap = wRecipeMetaMap(data);
	    for (var i = 0; i < cell.items.length; i++) {
	      var rr = recipeById(data, cell.items[i].rid);
	      if (rr && /protein|cereal|pulse|snack|fruit/.test(wLower(wRole(rr)))) { primaryIdx = i; break; }
	    }
	    var primary = recipeById(data, cell.items[primaryIdx].rid);
	    var primaryMeta = metaMap[primary && primary.id] || {};
	    var primaryRole = wRole(primary), primaryFamily = wRecipeFamily(primary, primaryMeta);
	    var otherItems = cell.items.filter(function (it, idx) { return idx !== primaryIdx; });
	    return wCandidateRecipes(slot.slot_id, inp, data, state).filter(function (x) {
	      return current.indexOf(x.r.id) < 0 && wFoodCompatibleWithItems(x.r, x.meta, otherItems, data, metaMap, slot.slot_id, inp);
	    }).slice(0, 5).map(function (x) {
	      var items = cell.items.map(function (it, idx) {
	        return idx === primaryIdx ? { rid: x.r.id, sv: 1 } : { rid: it.rid, sv: it.sv, oilCutG: +it.oilCutG || 0 };
	      });
	      wScaleItems(items, slot, targets, data, inp);
	      var same = wLower(wRole(x.r)).indexOf(wLower(primaryRole)) >= 0 || wRecipeFamily(x.r, x.meta) === primaryFamily;
	      return {
	        label: (same ? 'Swap: ' : 'Smart swap: ') + x.r.name,
	        type: same ? 'same_exchange' : 'smart_cross_exchange',
	        caution: same ? 'Similar exchange group.' : 'Day is rebalanced by portions.',
	        items: items,
	        totals: wFormatTotals(wCellTotals(items, data))
	      };
	    });
	  }
	  function wDayFamilyMap(day, data) {
	    var metaMap = wRecipeMetaMap(data), out = {};
	    day.slots.forEach(function (s) {
	      (s.items || []).forEach(function (it) {
	        var r = recipeById(data, it.rid);
	        if (r) out[wRecipeFamily(r, metaMap[r.id] || {})] = 1;
	      });
	    });
	    return out;
	  }
	  function wProteinCandidate(slotId, day, inp, data, state, cell) {
	    var fams = wDayFamilyMap(day, data), metaMap = wRecipeMetaMap(data);
	    var pulseTarget = (state.groupTargets && state.groupTargets.Pulses) || 75;
	    var dairyTarget = (state.groupTargets && state.groupTargets['Milk/curd']) || 300;
	    var dayPulse = 0, dayDairy = 0;
	    (day && day.slots || []).forEach(function (dayCell) {
	      dayPulse += wCellGroupGrams(dayCell, data, 'Pulses');
	      dayDairy += wCellGroupGrams(dayCell, data, 'Milk/curd');
	    });
	    var picks = wCandidateRecipes(slotId, inp, data, state, day && day.dayIndex).filter(function (x) {
	      var role = wLower(wRole(x.r)), fam = wRecipeFamily(x.r, x.meta);
	      if ((state.recipeUse[x.r.id] || 0) >= wMaxFrequency(x.r, x.meta, inp)) return false;
	      if (fams[fam] && !/dairy|pulse/.test(fam)) return false;
	      if (role === 'protein_fat') return false;
	      if (cell && !wFoodCompatibleWithItems(x.r, x.meta, cell.items || [], data, metaMap, slotId, inp)) return false;
	      if (/protein|dairy/.test(role)) return true;
	      return (x.r.p || 0) >= 8 && (x.r.e || 0) <= 260;
	    }).sort(function (a, b) {
	      var ad = (a.r.p || 0) / Math.max(a.r.e || 1, 1);
	      var bd = (b.r.p || 0) / Math.max(b.r.e || 1, 1);
	      var fibrePenalty = ((a.r.fb || 0) - (b.r.fb || 0)) * 8;
	      var fatPenalty = inp.goal === 'fat_loss' ? ((a.r.f || 0) - (b.r.f || 0)) * 3 : 0;
	      var aPulse = (wRecipeGroupGrams(a.r, 1).Pulses || 0) > 0;
	      var bPulse = (wRecipeGroupGrams(b.r, 1).Pulses || 0) > 0;
	      var aDairy = (wRecipeGroupGrams(a.r, 1)['Milk/curd'] || 0) > 0;
	      var bDairy = (wRecipeGroupGrams(b.r, 1)['Milk/curd'] || 0) > 0;
	      var pulsePenalty = (aPulse ? 24 : 0) - (bPulse ? 24 : 0);
	      if (dayPulse > pulseTarget) pulsePenalty += (aPulse ? 80 : 0) - (bPulse ? 80 : 0);
	      var dairyPenalty = (aDairy ? 8 : 0) - (bDairy ? 8 : 0);
	      if (dayDairy > dairyTarget) dairyPenalty += (aDairy ? 65 : 0) - (bDairy ? 65 : 0);
	      return (bd - ad) * 500 + (b.score - a.score) + fibrePenalty + fatPenalty + pulsePenalty + dairyPenalty;
	    });
	    return picks.length ? picks[0].r : null;
	  }
	  function wAddItem(cell, rid, sv) {
	    var hit = null;
	    (cell.items || []).forEach(function (it) { if (it.rid === rid) hit = it; });
	    if (hit) hit.sv = roundSv(hit.sv + sv);
	    else cell.items.push({ rid: rid, sv: sv });
	  }
	  function wPracticalServingCap(r, inp, slotId) {
	    var count = desiredMealCount(inp && inp.meals_per_day);
	    var role = wLower(wRole(r)), slot = wLower(slotId || '');
	    var text = wLower([r && r.name, (r && r.ing || []).map(function (g) { return g.item; }).join(' ')].join(' '));
	    if (/greek yogurt fruit bowl/.test(text)) return count === 1 ? 2 : count === 2 ? 1.5 : 1;
	    if (/tofu bhurji/.test(text)) return count === 1 ? 2.5 : count === 2 ? 1.75 : /breakfast/.test(slot) ? 1 : 1.5;
	    if (/\bchilla\b/.test(text)) return count === 1 ? 2.5 : count === 2 ? 1.75 : 1;
	    if (/\b(buttermilk|chaas|lassi|milk serving|protein shake)\b/.test(text)) return count === 1 ? 2.5 : count === 2 ? 1.5 : 1;
	    if (/\b(curd serving|raita)\b/.test(text)) return count === 1 ? 2 : count === 2 ? 1.5 : 1;
	    if (/snack|morning/.test(slot)) return /nuts|other/.test(role) ? 1.25 : 1.75;
	    if (role === 'protein_lean' || role === 'dairy') {
	      if (count === 1) return 3.75;
	      if (count === 2) return 2.5;
	      return /breakfast/.test(slot) ? 1.25 : 1.5;
	    }
	    if (role === 'protein_fat') return count === 1 ? 2 : count === 2 ? 1.75 : 1.5;
	    if (role === 'cereal') return count === 1 ? 4.5 : count === 2 ? 2.75 : 2.5;
	    if (role === 'veg') return count === 1 ? 4 : count === 2 ? 3 : 2.5;
	    return count <= 2 ? 2.5 : 2;
	  }
	  function wIncreaseExistingProtein(cell, inp, data) {
	    var best = null;
	    (cell.items || []).forEach(function (it) {
	      var r = recipeById(data, it.rid);
	      var maxSv = r ? wPracticalServingCap(r, inp, cell.slotId) : 0;
	      if (!r || it.sv >= maxSv) return;
	      var role = wLower(wRole(r));
	      if (!/protein|dairy/.test(role) || (role === 'protein_fat' && !/breakfast/.test(cell.slotId || ''))) return;
	      if ((cell.totals.fb || 0) > 14 && (r.fb || 0) > 5) return;
	      var density = (r.p || 0) / Math.max(r.e || 1, 1);
	      if (!best || density > best.density) best = { it: it, r: r, density: density, maxSv: maxSv };
	    });
	    if (!best) return false;
	    var step = /snack|morning/.test(cell.slotId || '') ? 0.25 : 0.5;
	    best.it.sv = roundSv(Math.min(best.maxSv, best.it.sv + step));
	    cell.totals = wFormatTotals(wCellTotals(cell.items, data));
	    return true;
	  }
	  function wReplaceForProtein(day, cell, inp, data, state, addServing) {
	    var metaMap = state.metaMap || wRecipeMetaMap(data), choices = [];
	    var replacementServing = Math.max(1, addServing || 0);
	    (cell.items || []).forEach(function (it, idx) {
	      var old = recipeById(data, it.rid), role = wLower(wRole(old));
	      if (!old || (/protein/.test(role) && role !== 'protein_fat') || role === 'fruit') return;
	      var removePriority = role === 'protein_fat' ? 7 : role === 'dairy' ? 5 : role === 'other' ? 4 : role === 'veg' ? 2 : 0;
	      if (!removePriority) return;
	      var remaining = cell.items.filter(function (_, i) { return i !== idx; });
	      var candidates = wCandidateRecipes(cell.slotId, inp, data, state, day && day.dayIndex).filter(function (x) {
	        var newRole = wLower(wRole(x.r));
	        if (!/protein/.test(newRole) || newRole === 'protein_fat') return false;
	        if ((state.recipeUse[x.r.id] || 0) >= wMaxFrequency(x.r, x.meta, inp)) return false;
	        if ((x.r.p || 0) * replacementServing <= (old.p || 0) * it.sv + 3) return false;
	        return wFoodCompatibleWithItems(x.r, x.meta, remaining, data, metaMap, cell.slotId, inp);
	      }).sort(function (a, b) {
	        var av = (a.r.p || 0) * 8 - (a.r.f || 0) * 4 - (a.r.fb || 0) * 5;
	        var bv = (b.r.p || 0) * 8 - (b.r.f || 0) * 4 - (b.r.fb || 0) * 5;
	        return bv - av;
	      });
	      if (candidates.length) choices.push({ idx: idx, old: old, candidate: candidates[0].r, priority: removePriority });
	    });
	    choices.sort(function (a, b) {
	      var ag = (a.candidate.p || 0) * replacementServing - (a.old.p || 0) + a.priority;
	      var bg = (b.candidate.p || 0) * replacementServing - (b.old.p || 0) + b.priority;
	      return bg - ag;
	    });
	    if (!choices.length) return false;
	    var pick = choices[0];
	    cell.items.splice(pick.idx, 1, { rid: pick.candidate.id, sv: Math.min(replacementServing, wPracticalServingCap(pick.candidate, inp, cell.slotId)) });
	    state.recipeUse[pick.old.id] = Math.max(0, (state.recipeUse[pick.old.id] || 0) - 1);
	    state.recipeUse[pick.candidate.id] = (state.recipeUse[pick.candidate.id] || 0) + 1;
	    cell.totals = wFormatTotals(wCellTotals(cell.items, data));
	    return true;
	  }
	  function wBoostProteinMeal(day, cell, inp, data, state, addServing) {
	    if (wIncreaseExistingProtein(cell, inp, data)) return true;
	    if ((cell.items || []).length >= wMealItemLimit(cell.slotId)) return wReplaceForProtein(day, cell, inp, data, state, addServing);
	    var cand = wProteinCandidate(cell.slotId, day, inp, data, state, cell);
	    if (!cand) return false;
	    wAddItem(cell, cand.id, addServing);
	    state.recipeUse[cand.id] = (state.recipeUse[cand.id] || 0) + 1;
	    cell.totals = wFormatTotals(wCellTotals(cell.items, data));
	    return true;
	  }
	  function wEnergyStapleCandidate(cell, inp, data, state) {
	    var metaMap = state.metaMap || wRecipeMetaMap(data);
	    var north = ['R002', 'R001', 'R043', 'R044', 'R046', 'R045', 'R003', 'R024', 'R020'];
	    var south = ['R003', 'R020', 'R046', 'R024', 'R045', 'R001', 'R002', 'R043', 'R044'];
	    var ids = inp.region === 'south' ? south : north;
	    var current = (cell.items || []).map(function (it) { return it.rid; });
	    for (var i = 0; i < ids.length; i++) {
	      if (current.indexOf(ids[i]) >= 0) continue;
	      var r = recipeById(data, ids[i]), meta = (metaMap && metaMap[ids[i]]) || {};
	      if (!r || wLower(wRole(r)) !== 'cereal') continue;
	      if (!wDietAllowed(r, meta, inp) || wExcluded(r, meta, inp)) continue;
	      if (!wFoodCompatibleWithItems(r, meta, cell.items || [], data, metaMap, cell.slotId, inp)) continue;
	      return r.id;
	    }
	    return '';
	  }
	  function wBoostEnergyCell(day, inp, data, state) {
	    var best = null;
	    day.slots.forEach(function (s) {
	      if (!/breakfast|lunch|dinner/.test(s.slotId || '')) return;
	      var slot = state.slotMap && state.slotMap[s.slotId] || {};
	      var targetEnergy = (state.targetKcal || 0) * (+slot.kcal_split_pct || 0);
	      var energyGap = targetEnergy ? targetEnergy - (s.totals.e || 0) : 0;
	      if (targetEnergy && energyGap < -targetEnergy * 0.05) return;
	      (s.items || []).forEach(function (it) {
	        var r = recipeById(data, it.rid);
	        var maxSv = r ? wPracticalServingCap(r, inp, s.slotId) : 0;
	        if (!r || it.sv >= maxSv) return;
	        var role = wLower(wRole(r));
	        if (role !== 'cereal' && role !== 'protein_lean') return;
	        var priority = role === 'cereal' ? 3 : 2;
	        var value = energyGap * 20 + priority * 1000 + (r.e || 0) - (r.fb || 0) * 75 - (r.f || 0) * 80 - recipeVisibleFat(r) * 90;
	        if (!best || value > best.value) best = { cell: s, it: it, r: r, value: value, maxSv: maxSv };
	      });
	    });
	    if (best) {
	      best.it.sv = roundSv(Math.min(best.maxSv, best.it.sv + (desiredMealCount(inp.meals_per_day) <= 2 ? 0.5 : 0.25)));
	      best.cell.totals = wFormatTotals(wCellTotals(best.cell.items, data));
	      return true;
	    }
	    var targetCell = null;
	    day.slots.forEach(function (s) {
	      if (!targetCell && /lunch|dinner|breakfast/.test(s.slotId || '') && s.items && s.items.length) targetCell = s;
	    });
	    if (!targetCell) return false;
	    var rid = wEnergyStapleCandidate(targetCell, inp, data, state);
	    if (!rid) return false;
	    if ((targetCell.items || []).length >= wMealItemLimit(targetCell.slotId)) return false;
	    wAddItem(targetCell, rid, desiredMealCount(inp.meals_per_day) <= 2 ? 1 : 0.5);
	    targetCell.totals = wFormatTotals(wCellTotals(targetCell.items, data));
	    return true;
	  }
	  function wBalanceWeeklyEnergy(weekly, inp, targets, data, state) {
	    var floor = desiredMealCount(inp.meals_per_day) <= 2 ? 0.90 : 0.94;
	    var guard = 0, guardLimit = desiredMealCount(inp.meals_per_day) <= 2 ? 96 : 28;
	    wRecomputeWeekly(weekly, data);
	    while (weekly.avgTotals.e < targets.kcal * floor && guard++ < guardLimit) {
	      var lowDays = weekly.days.slice().sort(function (a, b) { return (a.totals.e || 0) - (b.totals.e || 0); });
	      var boosted = false;
	      for (var i = 0; i < lowDays.length && !boosted; i++) boosted = wBoostEnergyCell(lowDays[i], inp, data, state);
	      if (!boosted) break;
	      wRecomputeWeekly(weekly, data);
	    }
	  }
	  function wTrimCereal(day, data) {
	    var best = null;
	    day.slots.forEach(function (s) {
	      (s.items || []).forEach(function (it) {
	        var r = recipeById(data, it.rid);
	        if (r && r.role === 'cereal' && it.sv > 0.5 && (!best || r.e * it.sv > best.r.e * best.it.sv)) {
	          best = { cell: s, it: it, r: r };
	        }
	      });
	    });
	    if (!best) return false;
	    best.it.sv = roundSv(best.it.sv - 0.25);
	    best.cell.totals = wFormatTotals(wCellTotals(best.cell.items, data));
	    return true;
	  }
	  function wTrimFatDense(day, data) {
	    var metaMap = wRecipeMetaMap(data), best = null;
	    day.slots.forEach(function (s) {
	      (s.items || []).forEach(function (it) {
	        var r = recipeById(data, it.rid);
	        if (!r || it.sv <= 0.25) return;
	        var fam = wRecipeFamily(r, metaMap[r.id] || {}), role = wLower(wRole(r));
	        if (!/paneer|nuts/.test(fam) && role !== 'protein_fat' && (r.f || 0) < 9) return;
	        if (!best || r.f * it.sv > best.r.f * best.it.sv) best = { cell: s, it: it, r: r };
	      });
	    });
	    if (!best) return false;
	    best.it.sv = roundSv(Math.max(0.25, best.it.sv - 0.25));
	    best.cell.totals = wFormatTotals(wCellTotals(best.cell.items, data));
	    return true;
	  }
	function wTrimOneOilStep(weekly, data) {
	  var best = null;
	  weekly.days.forEach(function (day) {
		day.slots.forEach(function (cell) {
		  (cell.items || []).forEach(function (it) {
			var r = recipeById(data, it.rid);
			var available = r ? recipeVisibleFat(r) - (+it.oilCutG || 0) : 0;
			if (available < 0.25 || !it.sv) return;
			var contribution = available * it.sv;
			if (!best || contribution > best.contribution) best = { cell: cell, it: it, available: available, contribution: contribution };
		  });
		});
	  });
	  if (!best) return false;
	  best.it.oilCutG = Math.round(((+best.it.oilCutG || 0) + Math.min(0.5, best.available)) * 4) / 4;
	  best.cell.totals = wFormatTotals(wCellTotals(best.cell.items, data));
	  return true;
	}
	  function wEnforceWeeklyOilCap(weekly, targets, data) {
	    if (!weekly || !targets) return weekly;
	    var guard = 0;
	    wRecomputeWeekly(weekly, data);
	    while (weekly.avgTotals.vfat > targets.visible_fat_cap_g + 0.05 && guard++ < 160) {
	      var best = null;
	      weekly.days.forEach(function (day) {
	        day.slots.forEach(function (cell) {
	          (cell.items || []).forEach(function (it) {
	            var r = recipeById(data, it.rid);
	            var available = r ? recipeVisibleFat(r) - (+it.oilCutG || 0) : 0;
	            if (available <= 0 || !it.sv) return;
	            var contribution = available * it.sv;
	            if (!best || contribution > best.contribution) {
	              best = { cell: cell, it: it, r: r, available: available, contribution: contribution };
	            }
	          });
	        });
	      });
	      if (!best) break;
	      var weeklyExcess = (weekly.avgTotals.vfat - targets.visible_fat_cap_g) * 7;
	      var cutStep = Math.min(best.available, Math.max(0.25, Math.ceil(Math.min(1, weeklyExcess / best.it.sv) * 4) / 4));
	      best.it.oilCutG = Math.round(((+best.it.oilCutG || 0) + cutStep) * 4) / 4;
	      best.cell.totals = wFormatTotals(wCellTotals(best.cell.items, data));
	      wRecomputeWeekly(weekly, data);
	    }
	    return weekly;
	  }
	  function wTrimWeeklyFat(weekly, targets, data, inp) {
	    if (desiredMealCount(inp && inp.meals_per_day) <= 2) return weekly;
	    var guard = 0;
	    wRecomputeWeekly(weekly, data);
	    while (weekly.avgTotals.f > targets.fat_g * 1.10 && guard++ < 56) {
	      var days = weekly.days.slice().sort(function (a, b) { return (b.totals.f || 0) - (a.totals.f || 0); });
	      var changed = wTrimOneOilStep(weekly, data);
	      for (var i = 0; i < days.length && !changed; i++) changed = wTrimFatDense(days[i], data);
	      if (!changed) break;
	      wRecomputeWeekly(weekly, data);
	    }
	    return weekly;
	  }
	  function wBalanceWeeklyProtein(weekly, inp, targets, data, state) {
	    wRecomputeWeekly(weekly, data);
	    var slotOrder = ['lunch', 'dinner', 'breakfast', 'evening_snack', 'mid_morning'];
	var strictProtein = inp.high_protein || inp.optimize_balance;
	    var proteinFloor = targets.protein_g * (strictProtein ? 1.0 : 0.95);
	    var energyCeiling = targets.kcal * (strictProtein ? 1.08 : 1.06);
	    var guard = 0;
	    while (weekly.avgTotals.p < proteinFloor && guard++ < (strictProtein ? 34 : 24)) {
	      var choices = [];
	      weekly.days.forEach(function (d) {
	        slotOrder.forEach(function (slotId) {
	          var cell = weeklyCellBySlotEngine(d, slotId);
	          if (!cell || !cell.items || !cell.items.length) return;
	          choices.push({ day: d, cell: cell });
	        });
	      });
	      choices.sort(function (a, b) {
	        function needScore(x) {
	          var slot = state.slotMap && state.slotMap[x.cell.slotId] || {};
	          var energyTarget = targets.kcal * (+slot.kcal_split_pct || 0);
	          var energyPenalty = energyTarget ? Math.max(0, (x.cell.totals.e || 0) - energyTarget * 1.1) / 8 : 0;
	          var fibrePenalty = Math.max(0, (x.cell.totals.fb || 0) - (/dinner/.test(x.cell.slotId) ? 14 : 16)) * 2;
	          return (x.cell.totals.p || 0) + energyPenalty + fibrePenalty;
	        }
	        return needScore(a) - needScore(b);
	      });
	      var chosen = null;
	      for (var c = 0; c < choices.length; c++) {
	        var serving = /snack|morning/.test(choices[c].cell.slotId) ? (strictProtein ? 0.75 : 0.5) : (strictProtein ? 1 : 0.75);
	        if (wBoostProteinMeal(choices[c].day, choices[c].cell, inp, data, state, serving)) { chosen = choices[c]; break; }
	      }
	      if (!chosen) break;
	      wRecomputeWeekly(weekly, data);
	      if (weekly.avgTotals.e > energyCeiling) wTrimCereal(chosen.day, data);
	      wRecomputeWeekly(weekly, data);
	    }
	    guard = 0;
	    while (weekly.avgTotals.e > targets.kcal * 1.05 && guard++ < 14) {
	      var trimmed = false;
	      weekly.days.forEach(function (d) { if (!trimmed) trimmed = wTrimCereal(d, data); });
	      if (!trimmed) break;
	      wRecomputeWeekly(weekly, data);
	    }
	    guard = 0;
	    while (weekly.avgTotals.f > targets.fat_g * 1.15 && guard++ < 14) {
	      var trimFat = false;
	      weekly.days.forEach(function (d) { if (!trimFat) trimFat = wTrimFatDense(d, data); });
	      if (!trimFat) break;
	      wRecomputeWeekly(weekly, data);
	    }
	    guard = 0;
	    while (weekly.avgTotals.p < proteinFloor && weekly.avgTotals.e < energyCeiling && guard++ < (strictProtein ? 16 : 10)) {
	      var choices2 = [];
	      weekly.days.forEach(function (d) {
	        slotOrder.forEach(function (slotId) {
	          var cell = weeklyCellBySlotEngine(d, slotId);
	          if (!cell || !cell.items || !cell.items.length) return;
	          choices2.push({ day: d, cell: cell });
	        });
	      });
	      choices2.sort(function (a, b) {
	        return ((a.cell.totals.p || 0) + Math.max(0, (a.cell.totals.fb || 0) - 16) * 2) -
	          ((b.cell.totals.p || 0) + Math.max(0, (b.cell.totals.fb || 0) - 16) * 2);
	      });
	      var chosen2 = null;
	      for (var c2 = 0; c2 < choices2.length; c2++) {
	        if (wBoostProteinMeal(choices2[c2].day, choices2[c2].cell, inp, data, state, 0.5)) { chosen2 = choices2[c2]; break; }
	      }
	      if (!chosen2) break;
	      wRecomputeWeekly(weekly, data);
	    }
	    return weekly;
	  }
	  function weeklyCellBySlotEngine(day, slotId) {
	    for (var i = 0; i < day.slots.length; i++) {
	      if (day.slots[i].slotId === slotId) return day.slots[i];
	    }
	    return null;
	  }
	  function wDayGroupGrams(day, data, groupName) {
	    var total = 0;
	    (day.slots || []).forEach(function (cell) {
	      (cell.items || []).forEach(function (it) {
	        var r = recipeById(data, it.rid);
	        var grams = wRecipeGroupGrams(r, it.sv);
	        total += grams[groupName] || 0;
	      });
	    });
	    return total;
	  }
	  function wPreferredFruitRecipe(inp, data, state) {
	    var metaMap = state.metaMap || wRecipeMetaMap(data);
	    var candidates = (data.recipes || []).filter(function (r) {
	      var meta = metaMap[r.id] || {};
	      return wLower(wRole(r)) === 'fruit' && wDietAllowed(r, meta, inp) && !wExcluded(r, meta, inp) && !wTraditionalSelfConflict(r, meta, inp);
	    }).sort(function (a, b) {
	      var ag = wRecipeGroupGrams(a, 1).Fruits || 0;
	      var bg = wRecipeGroupGrams(b, 1).Fruits || 0;
	      return bg - ag || (a.e || 0) - (b.e || 0);
	    });
	    return candidates[0] || null;
	  }
	  function wEnsureDailyFruit(weekly, inp, data, state) {
	    if (desiredMealCount(inp.meals_per_day) <= 2) return weekly;
	    var fruit = wPreferredFruitRecipe(inp, data, state);
	    if (!fruit) return weekly;
	    var metaMap = state.metaMap || wRecipeMetaMap(data), fruitMeta = metaMap[fruit.id] || {};
	    weekly.days.forEach(function (day) {
	      if (wDayGroupGrams(day, data, 'Fruits') >= 75) return;
	      var cells = ['mid_morning', 'breakfast', 'evening_snack', 'lunch'].map(function (id) {
	        return weeklyCellBySlotEngine(day, id);
	      }).filter(Boolean).sort(function (a, b) {
	        var ad = (a.items || []).some(function (it) {
	          var r = recipeById(data, it.rid);
	          return r && wTraditionalTags(r, metaMap[r.id] || {}).dairy;
	        });
	        var bd = (b.items || []).some(function (it) {
	          var r = recipeById(data, it.rid);
	          return r && wTraditionalTags(r, metaMap[r.id] || {}).dairy;
	        });
	        return (ad ? 1 : 0) - (bd ? 1 : 0) || (a.items || []).length - (b.items || []).length;
	      });
	      var chosen = null;
	      for (var i = 0; i < cells.length; i++) {
	        if (wFoodCompatibleWithItems(fruit, fruitMeta, cells[i].items || [], data, metaMap, cells[i].slotId, inp)) { chosen = cells[i]; break; }
	      }
	      if (!chosen) {
	        for (var c = 0; c < cells.length && !chosen; c++) {
	          var cell = cells[c], removable = -1;
	          for (var j = 0; j < (cell.items || []).length; j++) {
	            var rr = recipeById(data, cell.items[j].rid), role = wLower(wRole(rr));
	            if (role === 'dairy' || role === 'protein_fat') { removable = j; break; }
	          }
	          if (removable < 0) continue;
	          var remaining = cell.items.filter(function (_, idx) { return idx !== removable; });
	          if (wFoodCompatibleWithItems(fruit, fruitMeta, remaining, data, metaMap, cell.slotId, inp)) {
	            cell.items = remaining;
	            chosen = cell;
	          }
	        }
	      }
	      if (chosen) {
	        wAddItem(chosen, fruit.id, 1);
	        state.recipeUse[fruit.id] = (state.recipeUse[fruit.id] || 0) + 1;
	        chosen.totals = wFormatTotals(wCellTotals(chosen.items, data));
	      }
	    });
	    return wRecomputeWeekly(weekly, data);
	  }
	  function wEnsureWeeklyNuts(weekly, inp, targets, data, state) {
	    var avoidText = wLower([inp.allergies_text, inp.dislikes_text].join(' '));
	    if (/nut|seed|almond|walnut|peanut|groundnut/.test(avoidText)) return weekly;
	    var metaMap = state.metaMap || wRecipeMetaMap(data);
	    var nutRecipe = (data.recipes || []).filter(function (r) {
	      var grams = wRecipeGroupGrams(r, 1)['Nuts & seeds'] || 0;
	      return grams >= 10 && wDietAllowed(r, metaMap[r.id] || {}, inp) && !wExcluded(r, metaMap[r.id] || {}, inp);
	    }).sort(function (a, b) {
	      return (wRecipeGroupGrams(b, 1)['Nuts & seeds'] || 0) - (wRecipeGroupGrams(a, 1)['Nuts & seeds'] || 0);
	    })[0];
	    if (!nutRecipe) return weekly;
	    wRecomputeWeekly(weekly, data);
	    var trimGuard = 0;
	    while (weekly.avgTotals.f > targets.fat_g * 0.96 && trimGuard++ < 8) {
	      var changed = false;
	      weekly.days.forEach(function (day) { if (!changed) changed = wTrimFatDense(day, data); });
	      if (!changed) break;
	      wRecomputeWeekly(weekly, data);
	    }
	    var targetMinimum = 7, addGuard = 0;
	    while ((Engine.weeklyPlanGroupGrams(weekly, data)['Nuts & seeds'] || 0) < targetMinimum && addGuard++ < 7) {
	      var chosen = null;
	      for (var d = 0; d < weekly.days.length && !chosen; d++) {
	        var day = weekly.days[(d + addGuard - 1) % weekly.days.length];
	        var cells = ['mid_morning', 'evening_snack', 'breakfast'].map(function (id) {
	          return weeklyCellBySlotEngine(day, id);
	        }).filter(Boolean);
	        for (var c = 0; c < cells.length; c++) {
	          if (wFoodCompatibleWithItems(nutRecipe, metaMap[nutRecipe.id] || {}, cells[c].items || [], data, metaMap, cells[c].slotId, inp)) {
	            chosen = cells[c]; break;
	          }
	        }
	      }
	      if (!chosen) break;
	      var projectedFat = weekly.avgTotals.f + (nutRecipe.f || 0) * 0.5 / 7;
	      if (projectedFat > targets.fat_g * 1.08) break;
	      wAddItem(chosen, nutRecipe.id, 0.5);
	      state.recipeUse[nutRecipe.id] = (state.recipeUse[nutRecipe.id] || 0) + 1;
	      chosen.totals = wFormatTotals(wCellTotals(chosen.items, data));
	      wRecomputeWeekly(weekly, data);
	    }
	    return weekly;
	  }
	  function wCellGroupGrams(cell, data, groupName) {
	    var total = 0;
	    (cell && cell.items || []).forEach(function (it) {
	      var r = recipeById(data, it.rid);
	      total += (wRecipeGroupGrams(r, it.sv)[groupName] || 0);
	    });
	    return total;
	  }
	  function wVegetableCandidate(cell, inp, data, state) {
	    var ids = ['R048', 'R005', 'R050', 'R049'], metaMap = state.metaMap || wRecipeMetaMap(data);
	    for (var i = 0; i < ids.length; i++) {
	      var r = recipeById(data, ids[i]), meta = metaMap[ids[i]] || {};
	      if (!r || !wDietAllowed(r, meta, inp) || wExcluded(r, meta, inp)) continue;
	      if (wFoodCompatibleWithItems(r, meta, cell.items || [], data, metaMap, cell.slotId, inp)) return r;
	    }
	    return null;
	  }
	  function wMakeRoomForMainRole(cell, wanted, data) {
	    if ((cell.items || []).length < wMealItemLimit(cell.slotId)) return true;
	    var best = null;
	    (cell.items || []).forEach(function (it, idx) {
	      var r = recipeById(data, it.rid), role = wLower(wRole(r));
	      if (!r) return;
	      var priority = role === 'fruit' ? 100 : role === 'dairy' ? 80 : role === 'other' ? 70 : role === 'veg' ? 35 : 0;
	      if (wanted === 'protein' && /protein/.test(role)) priority = -1;
	      if (wanted === 'cereal' && role === 'cereal') priority = -1;
	      if (!best || priority > best.priority) best = { idx: idx, priority: priority };
	    });
	    if (!best || best.priority <= 0) return false;
	    cell.items.splice(best.idx, 1);
	    return true;
	  }
	  function wEnsureMainMealStructure(weekly, inp, targets, data, state) {
	    if (desiredMealCount(inp.meals_per_day) <= 2) return weekly;
	    var metaMap = state.metaMap || wRecipeMetaMap(data);
	    weekly.days.forEach(function (day) {
	      day.slots.forEach(function (cell) {
	        if (!/lunch|dinner/.test(cell.slotId || '') || !cell.items || !cell.items.length) return;
	        var hasCereal = cell.items.some(function (it) {
	          var r = recipeById(data, it.rid); return r && wCerealKind(r, metaMap[r.id] || {});
	        });
	        var hasProtein = cell.items.some(function (it) {
	          var r = recipeById(data, it.rid); return r && wProteinAnchorKind(r, metaMap[r.id] || {});
	        });
	        if (!hasCereal && wMakeRoomForMainRole(cell, 'cereal', data)) {
	          var cerealId = wEnergyStapleCandidate(cell, inp, data, state);
	          if (cerealId) wAddItem(cell, cerealId, 0.75);
	        }
	        var slot = state.slotMap && state.slotMap[cell.slotId] || {};
	        var mealProteinTarget = targets.protein_g * (+slot.kcal_split_pct || 0.3);
	        cell.totals = wFormatTotals(wCellTotals(cell.items, data));
	        if (!hasProtein && (cell.totals.p || 0) < mealProteinTarget * 0.8 && wMakeRoomForMainRole(cell, 'protein', data)) {
	          var protein = wProteinCandidate(cell.slotId, day, inp, data, state, cell);
	          if (protein) wAddItem(cell, protein.id, 0.75);
	        }
	        cell.totals = wFormatTotals(wCellTotals(cell.items, data));
	        if (wCellGroupGrams(cell, data, 'Vegetables + GLV') < 50 && (cell.items || []).length < wMealItemLimit(cell.slotId)) {
	          var vegetable = wVegetableCandidate(cell, inp, data, state);
	          if (vegetable) wAddItem(cell, vegetable.id, 0.75);
	        }
	        cell.totals = wFormatTotals(wCellTotals(cell.items, data));
	      });
	    });
	    return wRecomputeWeekly(weekly, data);
	  }
	  function wFamilyUseMap(weekly, data, state) {
	    var out = {}, metaMap = state.metaMap || wRecipeMetaMap(data);
	    weekly.days.forEach(function (day) {
	      day.slots.forEach(function (cell) {
	        (cell.items || []).forEach(function (it) {
	          var r = recipeById(data, it.rid);
	          if (!r) return;
	          var family = wRecipeFamily(r, metaMap[r.id] || {});
	          out[family] = (out[family] || 0) + 1;
	        });
	      });
	    });
	    return out;
	  }
	  function wRecipeUseMap(weekly) {
	    var out = {};
	    weekly.days.forEach(function (day) {
	      day.slots.forEach(function (cell) {
	        (cell.items || []).forEach(function (it) { out[it.rid] = (out[it.rid] || 0) + 1; });
	      });
	    });
	    return out;
	  }
	  function wEnsureBreakfastProtein(weekly, inp, targets, data, state) {
	    if (desiredMealCount(inp.meals_per_day) <= 2) return weekly;
	    var breakfastFloor = Math.min(24, Math.max(18, targets.protein_g * 0.20));
	    var metaMap = state.metaMap || wRecipeMetaMap(data), recipeUse = wRecipeUseMap(weekly);
	    weekly.days.forEach(function (day) {
	      var cell = weeklyCellBySlotEngine(day, 'breakfast');
	      if (!cell || !cell.items || !cell.items.length) return;
	      var guard = 0;
	      while ((cell.totals.p || 0) < breakfastFloor && guard++ < 3) {
	        var best = null;
	        cell.items.forEach(function (oldItem, oldIndex) {
	          var oldRecipe = recipeById(data, oldItem.rid);
	          if (!oldRecipe) return;
	          var remaining = cell.items.filter(function (_, idx) { return idx !== oldIndex; });
	          var oldFruit = (wRecipeGroupGrams(oldRecipe, oldItem.sv).Fruits || 0) >= 50;
	          wCandidateRecipes('breakfast', inp, data, state, day.dayIndex).forEach(function (candidate) {
	            var anchor = wProteinAnchorKind(candidate.r, candidate.meta), role = wLower(wRole(candidate.r));
	            if (!anchor || candidate.r.id === oldRecipe.id || !/protein|dairy/.test(role)) return;
	            if ((recipeUse[candidate.r.id] || 0) >= 3) return;
	            if (oldFruit && (wRecipeGroupGrams(candidate.r, 1).Fruits || 0) < 50) return;
	            if (wBreakfastMainKind(oldRecipe, metaMap[oldRecipe.id] || {}) &&
	                !wBreakfastMainKind(candidate.r, candidate.meta)) return;
	            if (!wFoodCompatibleWithItems(candidate.r, candidate.meta, remaining, data, metaMap, 'breakfast', inp)) return;
	            var cap = wPracticalServingCap(candidate.r, inp, 'breakfast');
	            var serving = Math.min(cap, Math.max(0.75, Math.ceil((breakfastFloor - (cell.totals.p || 0) + (oldRecipe.p || 0) * oldItem.sv) /
	              Math.max(candidate.r.p || 1, 1) * 4) / 4));
	            var proteinGain = (candidate.r.p || 0) * serving - (oldRecipe.p || 0) * oldItem.sv;
	            if (proteinGain < 2) return;
	            var pulseGrams = wRecipeGroupGrams(candidate.r, serving).Pulses || 0;
	            var value = proteinGain * 18 - (candidate.r.f || 0) * serving * 3 - (candidate.r.fb || 0) * serving * 4 - pulseGrams * 0.6 +
	              candidate.score * 0.15 - (recipeUse[candidate.r.id] || 0) * 28;
	            if ((wRecipeGroupGrams(candidate.r, serving).Fruits || 0) >= 50) value += 30;
	            if (!best || value > best.value) best = { index: oldIndex, r: candidate.r, serving: serving, value: value };
	          });
	        });
	        if (best) {
	          var removedRid = cell.items[best.index].rid;
	          cell.items.splice(best.index, 1, { rid: best.r.id, sv: roundSv(best.serving) });
	          recipeUse[removedRid] = Math.max(0, (recipeUse[removedRid] || 0) - 1);
	          recipeUse[best.r.id] = (recipeUse[best.r.id] || 0) + 1;
	          state.recipeUse[removedRid] = Math.max(0, (state.recipeUse[removedRid] || 0) - 1);
	          state.recipeUse[best.r.id] = (state.recipeUse[best.r.id] || 0) + 1;
	          cell.totals = wFormatTotals(wCellTotals(cell.items, data));
	          continue;
	        }
	        if (!wIncreaseExistingProtein(cell, inp, data)) break;
	      }
	    });
	    return wRecomputeWeekly(weekly, data);
	  }
	  function wCapBreakfastProtein(weekly, inp, targets, data, state) {
	    if (desiredMealCount(inp.meals_per_day) <= 2) return weekly;
	    var floor = Math.min(24, Math.max(18, targets.protein_g * 0.20)) * 0.90;
	    var ceiling = Math.min(34, Math.max(28, targets.protein_g * 0.30));
	    var metaMap = state.metaMap || wRecipeMetaMap(data);
	    weekly.days.forEach(function (day) {
	      var cell = weeklyCellBySlotEngine(day, 'breakfast'), guard = 0;
	      if (!cell || !cell.items || !cell.items.length) return;
	      while ((cell.totals.p || 0) > ceiling + 1 && guard++ < 8) {
	        var best = null;
	        cell.items.forEach(function (it) {
	          var r = recipeById(data, it.rid), meta = metaMap[it.rid] || {};
	          if (!r || it.sv <= 0.5 || !wProteinAnchorKind(r, meta)) return;
	          var projected = (cell.totals.p || 0) - (r.p || 0) * 0.25;
	          if (projected < floor) return;
	          var isMain = !!wBreakfastMainKind(r, meta);
	          var value = (isMain ? 0 : 100) + (r.p || 0) * 4 + (r.f || 0) * 2;
	          if (!best || value > best.value) best = { it: it, r: r, value: value };
	        });
	        if (!best) break;
	        best.it.sv = roundSv(best.it.sv - 0.25);
	        cell.totals = wFormatTotals(wCellTotals(cell.items, data));
	      }
	    });
	    return wRecomputeWeekly(weekly, data);
	  }
	  function wRepairConsecutiveBreakfasts(weekly, inp, targets, data, state) {
	    if (desiredMealCount(inp.meals_per_day) <= 2) return weekly;
	    var metaMap = state.metaMap || wRecipeMetaMap(data), recipeUse = wRecipeUseMap(weekly);
	    var floor = Math.min(24, Math.max(18, targets.protein_g * 0.20)) * 0.90;
	    var ceiling = Math.min(34, Math.max(28, targets.protein_g * 0.30)) + 1;
	    function signature(cell) {
	      return (cell && cell.items || []).map(function (it) { return it.rid; }).sort().join('+');
	    }
	    for (var dayIndex = 1; dayIndex < weekly.days.length; dayIndex++) {
	      var previous = weeklyCellBySlotEngine(weekly.days[dayIndex - 1], 'breakfast');
	      var current = weeklyCellBySlotEngine(weekly.days[dayIndex], 'breakfast');
	      if (!current || !current.items || !current.items.length || signature(previous) !== signature(current)) continue;
	      var best = null, previousSignature = signature(previous);
	      current.items.forEach(function (oldItem, itemIndex) {
	        var oldRecipe = recipeById(data, oldItem.rid), oldMeta = metaMap[oldItem.rid] || {};
	        if (!oldRecipe) return;
	        var oldMain = !!wBreakfastMainKind(oldRecipe, oldMeta);
	        var oldAnchor = wProteinAnchorKind(oldRecipe, oldMeta);
	        var remaining = current.items.filter(function (_, index) { return index !== itemIndex; });
	        wCandidateRecipes('breakfast', inp, data, state, weekly.days[dayIndex].dayIndex).forEach(function (candidate) {
	          if (candidate.r.id === oldRecipe.id || remaining.some(function (it) { return it.rid === candidate.r.id; })) return;
	          if ((recipeUse[candidate.r.id] || 0) >= wMaxFrequency(candidate.r, candidate.meta, inp)) return;
	          if (oldMain !== !!wBreakfastMainKind(candidate.r, candidate.meta)) return;
	          if (!oldMain && !!oldAnchor !== !!wProteinAnchorKind(candidate.r, candidate.meta)) return;
	          if (!wFoodCompatibleWithItems(candidate.r, candidate.meta, remaining, data, metaMap, 'breakfast', inp)) return;
	          var ratio = oldMain ? (oldRecipe.e || 1) * oldItem.sv / Math.max(candidate.r.e || 1, 1)
	            : (oldRecipe.p || 1) * oldItem.sv / Math.max(candidate.r.p || 1, 1);
	          var serving = Math.max(0.75, Math.round(ratio * 4) / 4);
	          serving = Math.min(wPracticalServingCap(candidate.r, inp, 'breakfast'), serving);
	          var projectedItems = remaining.concat([{ rid: candidate.r.id, sv: serving }]);
	          var projected = wCellTotals(projectedItems, data);
	          if (projected.p < floor || projected.p > ceiling) return;
	          var candidateSignature = projectedItems.map(function (it) { return it.rid; }).sort().join('+');
	          if (candidateSignature === previousSignature) return;
	          var value = candidate.score - (recipeUse[candidate.r.id] || 0) * 18 -
	            Math.abs(projected.e - current.totals.e) * 0.10 - Math.abs(projected.p - current.totals.p) * 4;
	          if (!best || value > best.value) best = { index: itemIndex, r: candidate.r, serving: serving, value: value };
	        });
	      });
	      if (!best) continue;
	      var removed = current.items[best.index].rid;
	      current.items.splice(best.index, 1, { rid: best.r.id, sv: roundSv(best.serving) });
	      recipeUse[removed] = Math.max(0, (recipeUse[removed] || 0) - 1);
	      recipeUse[best.r.id] = (recipeUse[best.r.id] || 0) + 1;
	      state.recipeUse[removed] = Math.max(0, (state.recipeUse[removed] || 0) - 1);
	      state.recipeUse[best.r.id] = (state.recipeUse[best.r.id] || 0) + 1;
	      current.totals = wFormatTotals(wCellTotals(current.items, data));
	    }
	    return wRecomputeWeekly(weekly, data);
	  }
	  function wBalanceDailyProtein(weekly, inp, targets, data, state) {
	    if (desiredMealCount(inp.meals_per_day) <= 2) return weekly;
	    weekly.days.forEach(function (day) {
	      var guard = 0;
	      day.totals = wFormatTotals(wSumTotals(day.slots));
	      while ((day.totals.p || 0) < targets.protein_g * 0.85 && guard++ < 6) {
	        var cells = day.slots.filter(function (cell) {
	          return cell.items && cell.items.length && /breakfast|lunch|dinner|snack|morning/.test(cell.slotId || '');
	        }).sort(function (a, b) {
	          var af = Math.max(0, (a.totals.fb || 0) - 14) * 2;
	          var bf = Math.max(0, (b.totals.fb || 0) - 14) * 2;
	          return ((a.totals.p || 0) + af) - ((b.totals.p || 0) + bf);
	        });
	        var changed = false;
	        for (var i = 0; i < cells.length && !changed; i++) changed = wBoostProteinMeal(day, cells[i], inp, data, state, 0.5);
	        if (!changed) break;
	        day.totals = wFormatTotals(wSumTotals(day.slots));
	        if ((day.totals.e || 0) > targets.kcal * 1.07) wTrimCereal(day, data);
	        day.totals = wFormatTotals(wSumTotals(day.slots));
	      }
	    });
	    return wRecomputeWeekly(weekly, data);
	  }
	  function wUpgradeLowProteinDays(weekly, inp, targets, data, state) {
	    if (desiredMealCount(inp.meals_per_day) <= 2) return weekly;
	    var metaMap = state.metaMap || wRecipeMetaMap(data), guard = 0;
	    var dayFloor = targets.protein_g * 0.90, weeklyFloor = targets.protein_g * 0.96;
	    wRecomputeWeekly(weekly, data);
	    while ((weekly.avgTotals.p < weeklyFloor || weekly.days.some(function (day) { return (day.totals.p || 0) < dayFloor; })) && guard++ < 24) {
	      var recipeUse = wRecipeUseMap(weekly);
	      var days = weekly.days.slice().sort(function (a, b) { return (a.totals.p || 0) - (b.totals.p || 0); });
	      var choices = [];
	      days.forEach(function (day) {
	        if ((day.totals.p || 0) >= dayFloor && weekly.avgTotals.p >= weeklyFloor) return;
	        day.slots.forEach(function (cell) {
	          if (!/lunch|dinner/.test(cell.slotId || '')) return;
	          (cell.items || []).forEach(function (it, itemIndex) {
	            var old = recipeById(data, it.rid), oldMeta = metaMap[it.rid] || {};
	            var oldAnchor = old && wProteinAnchorKind(old, oldMeta);
	            if (!old || !/pulse|paneer|dairy/.test(oldAnchor || '')) return;
	            var remaining = cell.items.filter(function (_, index) { return index !== itemIndex; });
	            wCandidateRecipes(cell.slotId, inp, data, state, day.dayIndex).forEach(function (candidate) {
	              var anchor = wProteinAnchorKind(candidate.r, candidate.meta);
	              var candidateFamily = wRecipeFamily(candidate.r, candidate.meta);
	              var oldFamily = wRecipeFamily(old, oldMeta);
	              var denserPulseSwap = oldAnchor === 'pulse' && anchor === 'pulse' && candidateFamily !== oldFamily;
	              var nonPulseSwap = anchor && !/pulse|paneer|dairy/.test(anchor);
	              if ((!denserPulseSwap && !nonPulseSwap) || wLower(wRole(candidate.r)) !== 'protein_lean') return;
	              var useCount = recipeUse[candidate.r.id] || 0;
	              if (useCount >= wMaxFrequency(candidate.r, candidate.meta, inp)) return;
	              var familyAlreadyInDay = day.slots.some(function (dayCell) {
	                return (dayCell.items || []).some(function (dayItem) {
	                  if (dayCell === cell && dayItem === it) return false;
	                  var dayRecipe = recipeById(data, dayItem.rid);
	                  return dayRecipe && wRecipeFamily(dayRecipe, metaMap[dayRecipe.id] || {}) === candidateFamily;
	                });
	              });
	              if (familyAlreadyInDay) return;
	              if (!wFoodCompatibleWithItems(candidate.r, candidate.meta, remaining, data, metaMap, cell.slotId, inp)) return;
	              var dayNeed = Math.max(4, dayFloor - (day.totals.p || 0));
	              var serving = Math.ceil(((old.p || 0) * it.sv + dayNeed) / Math.max(candidate.r.p || 1, 1) * 4) / 4;
	              serving = Math.min(wPracticalServingCap(candidate.r, inp, cell.slotId), Math.max(0.75, serving));
	              var proteinDelta = (candidate.r.p || 0) * serving - (old.p || 0) * it.sv;
	              var fatDelta = (candidate.r.f || 0) * serving - (old.f || 0) * it.sv;
	              var energyDelta = (candidate.r.e || 0) * serving - (old.e || 0) * it.sv;
	              var fibreDelta = (candidate.r.fb || 0) * serving - (old.fb || 0) * it.sv;
	              if (proteinDelta < 3 || weekly.avgTotals.f + fatDelta / 7 > targets.fat_g * 1.15) return;
	              if (weekly.avgTotals.e + energyDelta / 7 > targets.kcal * 1.08) return;
	              var value = proteinDelta * 22 - Math.max(0, fatDelta) * 7 - Math.max(0, energyDelta) * 0.08 -
	                Math.max(0, fibreDelta) * 6 - useCount * 24 + candidate.score * 0.08 + (denserPulseSwap ? 14 : 0);
	              choices.push({ day: day, cell: cell, index: itemIndex, old: old, r: candidate.r, serving: serving, value: value });
	            });
	          });
	        });
	      });
	      choices.sort(function (a, b) { return b.value - a.value; });
	      if (!choices.length) break;
	      var pick = choices[0];
	      pick.cell.items.splice(pick.index, 1, { rid: pick.r.id, sv: roundSv(pick.serving) });
	      state.recipeUse[pick.old.id] = Math.max(0, (state.recipeUse[pick.old.id] || 0) - 1);
	      state.recipeUse[pick.r.id] = (state.recipeUse[pick.r.id] || 0) + 1;
	      pick.cell.totals = wFormatTotals(wCellTotals(pick.cell.items, data));
	      pick.day.totals = wFormatTotals(wSumTotals(pick.day.slots));
	      wRecomputeWeekly(weekly, data);
	    }
	    return weekly;
	  }
	  function wBalanceNonPulseProtein(weekly, inp, targets, data, state) {
	    if (desiredMealCount(inp.meals_per_day) <= 2) return weekly;
	var floor = targets.protein_g * ((inp.high_protein || inp.optimize_balance) ? 1.0 : 0.95), guard = 0;
	    var metaMap = state.metaMap || wRecipeMetaMap(data);
	    wRecomputeWeekly(weekly, data);
	    function needsProtein() {
	      return weekly.avgTotals.p < floor || weekly.days.some(function (day) { return (day.totals.p || 0) < targets.protein_g * 0.85; });
	    }
	    while (needsProtein() && guard++ < 48) {
	      var choices = [];
	      var dairyNow = wExactWeeklyGroupAverage(weekly, data, 'Milk/curd');
	      var dairyTarget = (state.groupTargets && state.groupTargets['Milk/curd']) || 300;
	      var lowDays = weekly.days.filter(function (day) { return (day.totals.p || 0) < targets.protein_g * 0.85; });
	      weekly.days.forEach(function (day) {
	        day.slots.forEach(function (cell) {
	          (cell.items || []).forEach(function (it) {
	            var r = recipeById(data, it.rid), meta = metaMap[it.rid] || {};
	            if (!r || wProteinAnchorKind(r, meta) === 'pulse' || !/protein|dairy/.test(wLower(wRole(r)))) return;
	            var cap = wPracticalServingCap(r, inp, cell.slotId);
	            if (it.sv >= cap) return;
	            var fatAfter = weekly.avgTotals.f + (r.f || 0) * 0.25 / 7;
	            var energyAfter = weekly.avgTotals.e + (r.e || 0) * 0.25 / 7;
	            if (fatAfter > targets.fat_g * 1.12 || energyAfter > targets.kcal * 1.06) return;
	            var dayNeed = Math.max(0, targets.protein_g * 0.90 - (day.totals.p || 0));
	            var dairyGrams = wRecipeGroupGrams(r, 0.25)['Milk/curd'] || 0;
	            if (dairyGrams && dairyNow >= dairyTarget * 1.23) return;
	            var dairyPenalty = dairyGrams * (dairyNow > dairyTarget * 1.20 ? 1.5 : 0.12);
	            var value = dayNeed * 20 + (r.p || 0) * 20 - (r.f || 0) * 7 - (r.fb || 0) * 5 - (r.e || 0) * 0.04 - dairyPenalty;
	            choices.push({ day: day, cell: cell, it: it, r: r, cap: cap, value: value });
	          });
	        });
	      });
	      choices.sort(function (a, b) { return b.value - a.value; });
	      if (!choices.length) {
	        var added = false;
		var addDays = lowDays.length ? lowDays : weekly.days.slice().sort(function (a, b) {
		  return (a.totals.p || 0) - (b.totals.p || 0);
		});
	        for (var lowIndex = 0; lowIndex < addDays.length && !added; lowIndex++) {
	          var lowDay = addDays[lowIndex];
	          var lowCells = lowDay.slots.filter(function (cell) {
	            return cell.items && cell.items.length && (cell.items.length < wMealItemLimit(cell.slotId));
	          }).sort(function (a, b) { return (a.totals.p || 0) - (b.totals.p || 0); });
	          for (var cellIndex = 0; cellIndex < lowCells.length && !added; cellIndex++) {
	            var targetCell = lowCells[cellIndex];
	            var candidates = wCandidateRecipes(targetCell.slotId, inp, data, state, lowDay.dayIndex).filter(function (candidate) {
	              var anchor = wProteinAnchorKind(candidate.r, candidate.meta), role = wLower(wRole(candidate.r));
	              if (!anchor || anchor === 'pulse' || !/protein|dairy/.test(role) || (candidate.r.p || 0) < 8) return false;
	              if ((state.recipeUse[candidate.r.id] || 0) >= wMaxFrequency(candidate.r, candidate.meta, inp)) return false;
	              if ((wRecipeGroupGrams(candidate.r, 1)['Milk/curd'] || 0) && dairyNow >= dairyTarget * 1.23) return false;
	              return wFoodCompatibleWithItems(candidate.r, candidate.meta, targetCell.items, data, metaMap, targetCell.slotId, inp);
	            }).sort(function (a, b) {
	              var aDairy = wRecipeGroupGrams(a.r, 1)['Milk/curd'] || 0;
	              var bDairy = wRecipeGroupGrams(b.r, 1)['Milk/curd'] || 0;
	              var av = (a.r.p || 0) * 18 - (a.r.f || 0) * 7 - (a.r.fb || 0) * 5 - aDairy * (dairyNow > dairyTarget * 1.20 ? 1.5 : 0.12);
	              var bv = (b.r.p || 0) * 18 - (b.r.f || 0) * 7 - (b.r.fb || 0) * 5 - bDairy * (dairyNow > dairyTarget * 1.20 ? 1.5 : 0.12);
	              return bv - av;
	            });
	            if (candidates.length) {
	              wAddItem(targetCell, candidates[0].r.id, 0.75);
	              state.recipeUse[candidates[0].r.id] = (state.recipeUse[candidates[0].r.id] || 0) + 1;
	              targetCell.totals = wFormatTotals(wCellTotals(targetCell.items, data));
	              lowDay.totals = wFormatTotals(wSumTotals(lowDay.slots));
	              added = true;
	            }
	          }
	        }
	        if (!added) break;
	        wRecomputeWeekly(weekly, data);
	        continue;
	      }
	      var pick = choices[0];
	      pick.it.sv = roundSv(Math.min(pick.cap, pick.it.sv + 0.25));
	      pick.cell.totals = wFormatTotals(wCellTotals(pick.cell.items, data));
	      pick.day.totals = wFormatTotals(wSumTotals(pick.day.slots));
	      if ((pick.day.totals.e || 0) > targets.kcal * 1.07) wTrimCereal(pick.day, data);
	      wRecomputeWeekly(weekly, data);
	    }
	    return weekly;
	  }
	  function wReplacePulseExcess(weekly, inp, targets, data, state) {
	    if (desiredMealCount(inp.meals_per_day) <= 2) return weekly;
	    var pulseTarget = (state.groupTargets && state.groupTargets.Pulses) || 75;
	    var pulseCeiling = pulseTarget * 1.15;
	    var metaMap = state.metaMap || wRecipeMetaMap(data), guard = 0;
	    wRecomputeWeekly(weekly, data);
	    while (wExactWeeklyGroupAverage(weekly, data, 'Pulses') > pulseCeiling && guard++ < 28) {
	      var familyUse = wFamilyUseMap(weekly, data, state), choices = [];
	      weekly.days.forEach(function (day) {
	        day.slots.forEach(function (cell) {
	          (cell.items || []).forEach(function (it, idx) {
	            var old = recipeById(data, it.rid), oldMeta = metaMap[it.rid] || {};
	            var oldPulse = wRecipeGroupGrams(old, it.sv).Pulses || 0;
	            if (!old || oldPulse < 12 || !/protein|dairy/.test(wLower(wRole(old)))) return;
	            if (/breakfast/.test(cell.slotId || '') && wBreakfastMainKind(old, oldMeta)) return;
	            var remaining = cell.items.filter(function (_, itemIndex) { return itemIndex !== idx; });
	            wCandidateRecipes(cell.slotId, inp, data, state, day.dayIndex).forEach(function (candidate) {
	              var anchor = wProteinAnchorKind(candidate.r, candidate.meta), role = wLower(wRole(candidate.r));
	              if (!anchor || anchor === 'pulse' || candidate.r.id === old.id || !/protein|dairy/.test(role)) return;
	              if ((state.recipeUse[candidate.r.id] || 0) >= wMaxFrequency(candidate.r, candidate.meta, inp)) return;
	              if (inp.goal === 'fat_loss' && role === 'protein_fat') return;
	              if ((wRecipeGroupGrams(candidate.r, 1).Pulses || 0) > 0 || (candidate.r.p || 0) < 8) return;
	              if (!wFoodCompatibleWithItems(candidate.r, candidate.meta, remaining, data, metaMap, cell.slotId, inp)) return;
	              var family = wRecipeFamily(candidate.r, candidate.meta);
	              var familyCap = family === 'tofu' ? 7 : family === 'dairy' ? 5 : family === 'paneer' ? 2 : 4;
	              if ((familyUse[family] || 0) >= familyCap) return;
	              var neededProtein = (old.p || 0) * it.sv;
	              var serving = Math.ceil(Math.max(0.75, neededProtein / Math.max(candidate.r.p || 1, 1)) * 4) / 4;
	              serving = Math.min(serving, wPracticalServingCap(candidate.r, inp, cell.slotId));
	              var newProtein = (candidate.r.p || 0) * serving;
	              if (newProtein < neededProtein - 3.5) return;
	              var fatDelta = (candidate.r.f || 0) * serving - (old.f || 0) * it.sv;
	              var projectedFat = weekly.avgTotals.f + fatDelta / 7;
	              if (projectedFat > targets.fat_g * 1.12) return;
	              var value = oldPulse * 2 + (newProtein - neededProtein) * 12 + ((old.fb || 0) * it.sv - (candidate.r.fb || 0) * serving) * 9 -
	                Math.max(0, fatDelta) * 5 - (familyUse[family] || 0) * 12 + candidate.score * 0.12;
	              choices.push({ cell: cell, index: idx, old: old, r: candidate.r, serving: serving, family: family, value: value });
	            });
	          });
	        });
	      });
	      choices.sort(function (a, b) { return b.value - a.value; });
	      if (!choices.length) break;
	      var pick = choices[0];
	      var removedRid = pick.cell.items[pick.index].rid;
	      pick.cell.items.splice(pick.index, 1, { rid: pick.r.id, sv: roundSv(pick.serving) });
	      state.recipeUse[removedRid] = Math.max(0, (state.recipeUse[removedRid] || 0) - 1);
	      state.recipeUse[pick.r.id] = (state.recipeUse[pick.r.id] || 0) + 1;
	      pick.cell.totals = wFormatTotals(wCellTotals(pick.cell.items, data));
	      wRecomputeWeekly(weekly, data);
	    }
	    return weekly;
	  }
	  function wExactWeeklyGroupAverage(weekly, data, groupName) {
	    var total = 0;
	    weekly.days.forEach(function (day) {
	      day.slots.forEach(function (cell) { total += wCellGroupGrams(cell, data, groupName); });
	    });
	    return total / 7;
	  }
	  function wTrimFoodGroupExcess(weekly, inp, targets, data, state) {
	    if (desiredMealCount(inp.meals_per_day) <= 2) return weekly;
	    var limits = [
	      { group: 'Pulses', max: (state.groupTargets.Pulses || 80) * 1.15 },
	      { group: 'Milk/curd', max: (state.groupTargets['Milk/curd'] || 300) * 1.25 }
	    ];
	    limits.forEach(function (limit) {
	      var guard = 0;
	      while (wExactWeeklyGroupAverage(weekly, data, limit.group) > limit.max && guard++ < 100) {
	        var best = null;
	        weekly.days.forEach(function (day) {
	          day.slots.forEach(function (cell) {
	            (cell.items || []).forEach(function (it) {
	              var r = recipeById(data, it.rid), grams = wRecipeGroupGrams(r, 1)[limit.group] || 0;
	              if (!r || !grams || it.sv <= 0.5) return;
	              var nextProtein = weekly.avgTotals.p - (r.p || 0) * 0.25 / 7;
	              if (limit.group === 'Pulses' && nextProtein < targets.protein_g * 0.82) return;
	              var value = grams * it.sv + (r.f || 0) * 5 + (r.fb || 0) * 4;
	              if (wDairyFunction(r, (state.metaMap && state.metaMap[r.id]) || {})) value += 30;
	              if (!best || value > best.value) best = { cell: cell, it: it, r: r, value: value };
	            });
	          });
	        });
	        if (!best) break;
	        best.it.sv = roundSv(best.it.sv - 0.25);
	        best.cell.totals = wFormatTotals(wCellTotals(best.cell.items, data));
	        wRecomputeWeekly(weekly, data);
	      }
	    });
	    return weekly;
	  }
	  function wLowerFibreReplacement(cell, item, inp, data, state) {
	    var current = recipeById(data, item.rid), metaMap = state.metaMap || wRecipeMetaMap(data);
	    if (!current) return null;
	    var role = wLower(wRole(current));
	    var otherItems = (cell.items || []).filter(function (it) { return it !== item; });
	    var candidates = wCandidateRecipes(cell.slotId, inp, data, state, 0).filter(function (x) {
	      if (x.r.id === current.id || wLower(wRole(x.r)) !== role) return false;
	      if ((state.recipeUse[x.r.id] || 0) >= wMaxFrequency(x.r, x.meta, inp)) return false;
	      if ((x.r.fb || 0) > (current.fb || 0) - 1.5) return false;
	      if (/protein/.test(role) && (x.r.p || 0) < (current.p || 0) * 0.7) return false;
	      if ((x.r.f || 0) > (current.f || 0) + 3) return false;
	      return wFoodCompatibleWithItems(x.r, x.meta, otherItems, data, metaMap, cell.slotId, inp);
	    }).sort(function (a, b) {
	      var av = ((current.fb || 0) - (a.r.fb || 0)) * 18 + (a.r.p || 0) * 3 - (a.r.f || 0) * 4 - Math.abs((a.r.e || 0) - (current.e || 0)) / 12;
	      var bv = ((current.fb || 0) - (b.r.fb || 0)) * 18 + (b.r.p || 0) * 3 - (b.r.f || 0) * 4 - Math.abs((b.r.e || 0) - (current.e || 0)) / 12;
	      return bv - av;
	    });
	    return candidates.length ? candidates[0].r : null;
	  }
	  function wReduceFibreLoad(weekly, inp, targets, data, state) {
	    if (desiredMealCount(inp.meals_per_day) <= 2) return weekly;
	var preferredMax = inp.lower_fibre ? 35 : inp.optimize_balance ? 42 : Math.min(39, Math.max(36, targets.fibre_g * 1.45));
	    var guard = 0;
	    wRecomputeWeekly(weekly, data);
	    while (weekly.avgTotals.fb > preferredMax && guard++ < 70) {
	      var choices = [];
	      weekly.days.forEach(function (day) {
	        day.slots.forEach(function (cell) {
	          (cell.items || []).forEach(function (it) {
	            var r = recipeById(data, it.rid);
	            if (!r || !(r.fb || 0)) return;
	            choices.push({ cell: cell, it: it, r: r, value: (r.fb || 0) * it.sv + Math.max(0, (cell.totals.fb || 0) - 15) });
	          });
	        });
	      });
	      choices.sort(function (a, b) { return b.value - a.value; });
	      var changed = false;
	      for (var i = 0; i < choices.length && !changed; i++) {
	        var repl = wLowerFibreReplacement(choices[i].cell, choices[i].it, inp, data, state);
	        if (repl) {
	          var projectedReplacementEnergy = weekly.avgTotals.e + ((repl.e || 0) - (choices[i].r.e || 0)) * choices[i].it.sv / 7;
	          var projectedReplacementProtein = weekly.avgTotals.p + ((repl.p || 0) - (choices[i].r.p || 0)) * choices[i].it.sv / 7;
	          if (projectedReplacementEnergy < targets.kcal * (inp.optimize_balance ? 0.91 : 0.93) ||
			  projectedReplacementProtein < targets.protein_g * 0.95) continue;
	          var removedRid = choices[i].it.rid;
	          choices[i].it.rid = repl.id;
	          choices[i].it.sv = Math.min(choices[i].it.sv, wPracticalServingCap(repl, inp, choices[i].cell.slotId));
	          state.recipeUse[removedRid] = Math.max(0, (state.recipeUse[removedRid] || 0) - 1);
	          state.recipeUse[repl.id] = (state.recipeUse[repl.id] || 0) + 1;
	          choices[i].cell.totals = wFormatTotals(wCellTotals(choices[i].cell.items, data));
	          changed = true;
	        } else if (choices[i].it.sv > 0.5) {
	          var projectedTrimEnergy = weekly.avgTotals.e - (choices[i].r.e || 0) * 0.25 / 7;
	          var projectedTrimProtein = weekly.avgTotals.p - (choices[i].r.p || 0) * 0.25 / 7;
	          if (projectedTrimEnergy < targets.kcal * (inp.optimize_balance ? 0.92 : 0.94) ||
			  projectedTrimProtein < targets.protein_g * 0.95) continue;
	          choices[i].it.sv = roundSv(choices[i].it.sv - 0.25);
	          choices[i].cell.totals = wFormatTotals(wCellTotals(choices[i].cell.items, data));
	          changed = true;
	        }
	      }
	      if (!changed) break;
	      wRecomputeWeekly(weekly, data);
	    }
	    return weekly;
	  }
	  function wDistributionRange(slotId, mealCount, slot) {
	    if (mealCount === 3) {
	      if (slotId === 'breakfast') return [0.24, 0.31];
	      if (slotId === 'lunch') return [0.30, 0.37];
	      if (slotId === 'dinner') return [0.27, 0.35];
	    }
	    var split = +slot.kcal_split_pct || 0;
	    return [Math.max(0.04, split * 0.80), Math.min(0.40, split * 1.15)];
	  }
	  function wTrimEnergyCell(cell, data) {
	    var best = null;
	    (cell.items || []).forEach(function (it) {
	      var r = recipeById(data, it.rid);
	      if (!r || it.sv <= 0.5) return;
	      var role = wLower(wRole(r));
	      if (role === 'fruit' && it.sv <= 1) return;
	      var value = (r.e || 0) + (r.f || 0) * 8 + (role === 'cereal' ? 50 : 0);
	      if (!best || value > best.value) best = { it: it, r: r, value: value };
	    });
	    if (!best) return false;
	    best.it.sv = roundSv(best.it.sv - 0.25);
	    cell.totals = wFormatTotals(wCellTotals(cell.items, data));
	    return true;
	  }
	  function wBoostSpecificCell(cell, inp, data, state, preferProtein) {
	    var best = null;
	    (cell.items || []).forEach(function (it) {
	      var r = recipeById(data, it.rid), cap = r ? wPracticalServingCap(r, inp, cell.slotId) : 0;
	      if (!r || it.sv >= cap) return;
	      var role = wLower(wRole(r));
	      if (role !== 'cereal' && role !== 'protein_lean') return;
	      var value = (r.e || 0) - (r.fb || 0) * 70 - (r.f || 0) * 65 + (preferProtein && role === 'protein_lean' ? 1200 : 0);
	      if (!best || value > best.value) best = { it: it, r: r, cap: cap, value: value };
	    });
	    if (best) {
	      best.it.sv = roundSv(Math.min(best.cap, best.it.sv + 0.25));
	      cell.totals = wFormatTotals(wCellTotals(cell.items, data));
	      return true;
	    }
	    if ((cell.items || []).length >= wMealItemLimit(cell.slotId)) return false;
	    var cerealId = wEnergyStapleCandidate(cell, inp, data, state);
	    if (!cerealId) return false;
	    wAddItem(cell, cerealId, 0.5);
	    cell.totals = wFormatTotals(wCellTotals(cell.items, data));
	    return true;
	  }
	  function wBalanceDailyDistribution(weekly, inp, targets, data, state) {
	    var mealCount = desiredMealCount(inp.meals_per_day);
	    if (mealCount <= 2) return weekly;
	    weekly.days.forEach(function (day) {
	      var guard = 0, changed = true;
	      while (changed && guard++ < 28) {
	        changed = false;
	        day.slots.forEach(function (cell) {
	          if (!cell.items || !cell.items.length) return;
	          var slot = state.slotMap[cell.slotId] || {}, range = wDistributionRange(cell.slotId, mealCount, slot);
	          if ((cell.totals.e || 0) > targets.kcal * range[1] + 10 && wTrimEnergyCell(cell, data)) changed = true;
	        });
	      }
	      guard = 0;
	      while (guard++ < 18) {
	        var low = null;
	        day.slots.forEach(function (cell) {
	          if (!cell.items || !cell.items.length) return;
	          var slot = state.slotMap[cell.slotId] || {}, range = wDistributionRange(cell.slotId, mealCount, slot);
	          var gap = targets.kcal * range[0] - (cell.totals.e || 0);
	          if (gap > 10 && (!low || gap > low.gap)) low = { cell: cell, gap: gap };
	        });
	        if (!low || !wBoostSpecificCell(low.cell, inp, data, state)) break;
	      }
	      day.totals = wFormatTotals(wSumTotals(day.slots));
	    });
	    return wRecomputeWeekly(weekly, data);
	  }
	  function wBalanceDailyEnergy(weekly, inp, targets, data, state) {
	    var mealCount = desiredMealCount(inp.meals_per_day);
	    if (mealCount <= 2) return weekly;
	    weekly.days.forEach(function (day) {
	      var guard = 0;
	      day.totals = wFormatTotals(wSumTotals(day.slots));
	      while ((day.totals.e || 0) > targets.kcal * 1.08 && guard++ < 24) {
	        var highCells = day.slots.filter(function (cell) { return cell.items && cell.items.length; })
	          .sort(function (a, b) { return (b.totals.e || 0) - (a.totals.e || 0); });
	        var trimmed = false;
	        for (var i = 0; i < highCells.length && !trimmed; i++) trimmed = wTrimEnergyCell(highCells[i], data);
	        if (!trimmed) break;
	        day.totals = wFormatTotals(wSumTotals(day.slots));
	      }
	      guard = 0;
	      while ((day.totals.e || 0) < targets.kcal * 0.92 && guard++ < 24) {
	        var lowCells = day.slots.filter(function (cell) { return cell.items && cell.items.length; })
	          .sort(function (a, b) { return (a.totals.e || 0) - (b.totals.e || 0); });
	        var boosted = false, preferProtein = (day.totals.p || 0) < targets.protein_g * 0.9;
	        for (var j = 0; j < lowCells.length && !boosted; j++) boosted = wBoostSpecificCell(lowCells[j], inp, data, state, preferProtein);
	        if (!boosted) break;
	        day.totals = wFormatTotals(wSumTotals(day.slots));
	      }
	    });
	    return wRecomputeWeekly(weekly, data);
	  }
	  function wComfortTrimItem(cell, data, preferFibre) {
	    var best = null;
	    (cell.items || []).forEach(function (it) {
	      var r = recipeById(data, it.rid);
	      if (!r || it.sv <= 0.5) return;
	      var role = wLower(wRole(r));
	      if (!/cereal|protein|veg/.test(role)) return;
	      var value = preferFibre ? (r.fb || 0) * it.sv : (r.e || 0) * it.sv;
	      if (!best || value > best.value) best = { it: it, r: r, value: value };
	    });
	    if (!best) return false;
	    best.it.sv = roundSv(Math.max(0.5, best.it.sv - 0.25));
	    cell.totals = wFormatTotals(wCellTotals(cell.items, data));
	    return true;
	  }
	  function wBalanceMealComfort(weekly, inp, targets, data) {
	    if (desiredMealCount(inp.meals_per_day) <= 2) return weekly;
	    var slotMap = {};
	    (weekly.slots || []).forEach(function (s) { slotMap[s.slot_id] = s; });
	    weekly.days.forEach(function (day) {
	      day.slots.forEach(function (cell) {
	        if (!cell.items || !cell.items.length) return;
	        var fibreLimit = /dinner/.test(cell.slotId) ? 18 : /lunch/.test(cell.slotId) ? 20 : 16;
	        var guard = 0;
	        while ((cell.totals.fb || 0) > fibreLimit && guard++ < 14) {
	          if (!wComfortTrimItem(cell, data, true)) break;
	        }
	        var slot = slotMap[cell.slotId] || {}, targetEnergy = targets.kcal * (+slot.kcal_split_pct || 0);
	        var energyLimit = targetEnergy * (/dinner/.test(cell.slotId) ? 1.15 : 1.20);
	        guard = 0;
	        while (targetEnergy && (cell.totals.e || 0) > energyLimit && guard++ < 10) {
	          if (!wComfortTrimItem(cell, data, false)) break;
	        }
	      });
	    });
	    return wRecomputeWeekly(weekly, data);
	  }
	  function wSimplifyWeeklyRecipes(weekly, inp, targets, data, state) {
	    var recipeUse = wRecipeUseMap(weekly), metaMap = state.metaMap || wRecipeMetaMap(data), guard = 0;
	    var recipeTarget = wWeeklyRecipeTarget(inp);
	    function uniqueCount() { return Object.keys(recipeUse).filter(function (rid) { return recipeUse[rid] > 0; }).length; }
	    while (uniqueCount() > recipeTarget && guard++ < 48) {
	      var best = null;
	      weekly.days.forEach(function (day) {
	        day.slots.forEach(function (cell) {
	          (cell.items || []).forEach(function (it, idx) {
	            if ((recipeUse[it.rid] || 0) !== 1) return;
	            var old = recipeById(data, it.rid), oldMeta = metaMap[it.rid] || {};
	            if (!old) return;
	            var oldFamily = wRecipeFamily(old, oldMeta), oldRole = wLower(wRole(old));
	            var remaining = cell.items.filter(function (_, itemIndex) { return itemIndex !== idx; });
	            Object.keys(recipeUse).forEach(function (rid) {
	              if ((recipeUse[rid] || 0) < 2 || rid === old.id) return;
	              var candidate = recipeById(data, rid), candidateMeta = metaMap[rid] || {};
	              if (!candidate || wRecipeSlots(candidate, candidateMeta).indexOf(cell.slotId) < 0) return;
	              if ((recipeUse[rid] || 0) >= wMaxFrequency(candidate, candidateMeta, inp)) return;
	              if (!wDietAllowed(candidate, candidateMeta, inp) || wExcluded(candidate, candidateMeta, inp)) return;
	              if (!wRoleCompatible(oldRole, wLower(wRole(candidate)))) return;
	              if (!wFoodCompatibleWithItems(candidate, candidateMeta, remaining, data, metaMap, cell.slotId, inp)) return;
	              var serving = Math.min(it.sv, wPracticalServingCap(candidate, inp, cell.slotId));
	              var energyDelta = (candidate.e || 0) * serving - (old.e || 0) * it.sv;
	              var proteinDelta = (candidate.p || 0) * serving - (old.p || 0) * it.sv;
	              var fatDelta = (candidate.f || 0) * serving - (old.f || 0) * it.sv;
	              var fibreDelta = (candidate.fb || 0) * serving - (old.fb || 0) * it.sv;
	              var projectedEnergy = weekly.avgTotals.e + energyDelta / 7;
	              var projectedProtein = weekly.avgTotals.p + proteinDelta / 7;
	              var projectedFat = weekly.avgTotals.f + fatDelta / 7;
	              var projectedFibre = weekly.avgTotals.fb + fibreDelta / 7;
	              if (projectedEnergy < targets.kcal * 0.92 || projectedEnergy > targets.kcal * 1.08) return;
	              if (projectedProtein < targets.protein_g * 0.95 && proteinDelta < -0.5) return;
	              if (projectedFat > targets.fat_g * 1.14 || projectedFibre > 45) return;
	              var familyMatch = wRecipeFamily(candidate, candidateMeta) === oldFamily;
	              var value = (familyMatch ? 45 : 0) + (recipeUse[rid] || 0) * 5 - Math.abs(energyDelta) * 0.08 -
	                Math.abs(proteinDelta) * 4 - Math.max(0, fatDelta) * 4 - Math.max(0, fibreDelta) * 4;
	              if (!best || value > best.value) best = { cell: cell, index: idx, oldRid: old.id, rid: rid, serving: serving, value: value };
	            });
	          });
	        });
	      });
	      if (!best) break;
	      best.cell.items.splice(best.index, 1, { rid: best.rid, sv: roundSv(best.serving) });
	      recipeUse[best.oldRid] = Math.max(0, (recipeUse[best.oldRid] || 0) - 1);
	      recipeUse[best.rid] = (recipeUse[best.rid] || 0) + 1;
	      best.cell.totals = wFormatTotals(wCellTotals(best.cell.items, data));
	      wRecomputeWeekly(weekly, data);
	    }
	    return weekly;
	  }
	  function wBuildWeeklyCandidate(inp, targets, data, dailyBuilt) {
	    if (!targets || targets.blocks.length || !data || !data.recipes || !data.recipes.length) return null;
	    var slots = wSlotsForMealCount(wSlotRows(data), inp), patterns = wPatternMap(data);
	    var previousRecipeUse = {};
	    wCsv(inp && inp.previous_plan_recipe_ids).forEach(function (rid) { previousRecipeUse[rid] = 1; });
	    var state = {
	      recipeUse: {}, previousRecipeUse: previousRecipeUse, dayFamilies: {}, dayGroupGrams: {}, weekGroupGrams: {},
	      slotUse: {}, slotFamilyUse: {}, groupTargets: wGroupTargetMap(inp, targets, data),
	      metaMap: wRecipeMetaMap(data), targetKcal: targets.kcal, slotMap: {}
	    };
	    slots.forEach(function (slot) { state.slotMap[slot.slot_id] = slot; });
	    var days = WEEK_DAYS.map(function (day, idx) {
	      state.dayFamilies = {};
	      state.dayGroupGrams = {};
	      var daySlots = slots.map(function (slot) {
	        var pat = patterns[day + '|' + slot.slot_id] || {};
	        return wBuildCell(day, idx, slot, pat, inp, targets, data, state);
	      });
	      return { dayIndex: idx + 1, day: day, slots: daySlots, totals: wFormatTotals(wSumTotals(daySlots)) };
	    });
		    var weekly = { source: data.weeklyMealPatterns && data.weeklyMealPatterns.length ? 'Google Sheet V23 weekly planner' : 'Embedded weekly planner fallback',
		      slots: slots, days: days, targets: targets, estimatedCount: 0, swapEnabled: true };
		    wRecomputeWeekly(weekly, data);
		    wEnsureDailyFruit(weekly, inp, data, state);
		    wEnsureWeeklyNuts(weekly, inp, targets, data, state);
		    wEnsureMainMealStructure(weekly, inp, targets, data, state);
		    wEnsureBreakfastProtein(weekly, inp, targets, data, state);
		    wEnsureDailyFruit(weekly, inp, data, state);
		    wTrimFoodGroupExcess(weekly, inp, targets, data, state);
		    wReplacePulseExcess(weekly, inp, targets, data, state);
		    wReduceFibreLoad(weekly, inp, targets, data, state);
		    wBalanceMealComfort(weekly, inp, targets, data);
		    wBalanceWeeklyProtein(weekly, inp, targets, data, state);
		    wReplacePulseExcess(weekly, inp, targets, data, state);
		    wEnforceWeeklyOilCap(weekly, targets, data);
		    wTrimWeeklyFat(weekly, targets, data, inp);
		    wBalanceWeeklyEnergy(weekly, inp, targets, data, state);
		    wBalanceDailyDistribution(weekly, inp, targets, data, state);
		    wBalanceMealComfort(weekly, inp, targets, data);
		    wBalanceWeeklyProtein(weekly, inp, targets, data, state);
		    wTrimWeeklyFat(weekly, targets, data, inp);
		    wBalanceWeeklyEnergy(weekly, inp, targets, data, state);
		    wEnsureMainMealStructure(weekly, inp, targets, data, state);
		    wBalanceDailyDistribution(weekly, inp, targets, data, state);
		    wTrimFoodGroupExcess(weekly, inp, targets, data, state);
		    wReplacePulseExcess(weekly, inp, targets, data, state);
		    wReduceFibreLoad(weekly, inp, targets, data, state);
		    wBalanceWeeklyProtein(weekly, inp, targets, data, state);
		    wTrimWeeklyFat(weekly, targets, data, inp);
		    wBalanceWeeklyEnergy(weekly, inp, targets, data, state);
		    wBalanceDailyDistribution(weekly, inp, targets, data, state);
		    wBalanceDailyEnergy(weekly, inp, targets, data, state);
		    wEnsureBreakfastProtein(weekly, inp, targets, data, state);
		    wBalanceWeeklyProtein(weekly, inp, targets, data, state);
		    wBalanceDailyProtein(weekly, inp, targets, data, state);
		    wReplacePulseExcess(weekly, inp, targets, data, state);
		    wReduceFibreLoad(weekly, inp, targets, data, state);
		    wBalanceNonPulseProtein(weekly, inp, targets, data, state);
		    wTrimWeeklyFat(weekly, targets, data, inp);
		    wBalanceDailyDistribution(weekly, inp, targets, data, state);
		    wBalanceDailyEnergy(weekly, inp, targets, data, state);
		    wBalanceNonPulseProtein(weekly, inp, targets, data, state);
		    wEnsureBreakfastProtein(weekly, inp, targets, data, state);
		    wEnforceWeeklyOilCap(weekly, targets, data);
		    wSimplifyWeeklyRecipes(weekly, inp, targets, data, state);
		    wReplacePulseExcess(weekly, inp, targets, data, state);
		    wReduceFibreLoad(weekly, inp, targets, data, state);
		    wBalanceNonPulseProtein(weekly, inp, targets, data, state);
		    wEnsureMainMealStructure(weekly, inp, targets, data, state);
		wReduceFibreLoad(weekly, inp, targets, data, state);
		wBalanceNonPulseProtein(weekly, inp, targets, data, state);
		wBalanceWeeklyEnergy(weekly, inp, targets, data, state);
		wEnsureMainMealStructure(weekly, inp, targets, data, state);
		wEnsureBreakfastProtein(weekly, inp, targets, data, state);
		wTrimWeeklyFat(weekly, targets, data, inp);
		wBalanceNonPulseProtein(weekly, inp, targets, data, state);
		wBalanceWeeklyEnergy(weekly, inp, targets, data, state);
		wEnsureMainMealStructure(weekly, inp, targets, data, state);
		wTrimFoodGroupExcess(weekly, inp, targets, data, state);
		wReplacePulseExcess(weekly, inp, targets, data, state);
		wReduceFibreLoad(weekly, inp, targets, data, state);
		wBalanceNonPulseProtein(weekly, inp, targets, data, state);
		wCapBreakfastProtein(weekly, inp, targets, data, state);
		wBalanceMealComfort(weekly, inp, targets, data);
		wBalanceDailyDistribution(weekly, inp, targets, data, state);
		wBalanceDailyEnergy(weekly, inp, targets, data, state);
		wSimplifyWeeklyRecipes(weekly, inp, targets, data, state);
		wTrimFoodGroupExcess(weekly, inp, targets, data, state);
		wBalanceNonPulseProtein(weekly, inp, targets, data, state);
		wBalanceDailyProtein(weekly, inp, targets, data, state);
		wUpgradeLowProteinDays(weekly, inp, targets, data, state);
		wCapBreakfastProtein(weekly, inp, targets, data, state);
		wBalanceDailyDistribution(weekly, inp, targets, data, state);
		wBalanceDailyEnergy(weekly, inp, targets, data, state);
		wReduceFibreLoad(weekly, inp, targets, data, state);
		wTrimFoodGroupExcess(weekly, inp, targets, data, state);
		wBalanceNonPulseProtein(weekly, inp, targets, data, state);
		wUpgradeLowProteinDays(weekly, inp, targets, data, state);
		wTrimWeeklyFat(weekly, targets, data, inp);
		wCapBreakfastProtein(weekly, inp, targets, data, state);
		wBalanceWeeklyEnergy(weekly, inp, targets, data, state);
		wBalanceDailyDistribution(weekly, inp, targets, data, state);
		wBalanceDailyEnergy(weekly, inp, targets, data, state);
		wBalanceNonPulseProtein(weekly, inp, targets, data, state);
		wCapBreakfastProtein(weekly, inp, targets, data, state);
		wReduceFibreLoad(weekly, inp, targets, data, state);
		wSimplifyWeeklyRecipes(weekly, inp, targets, data, state);
		wEnsureBreakfastProtein(weekly, inp, targets, data, state);
		wCapBreakfastProtein(weekly, inp, targets, data, state);
		wReduceFibreLoad(weekly, inp, targets, data, state);
		wEnsureBreakfastProtein(weekly, inp, targets, data, state);
		wCapBreakfastProtein(weekly, inp, targets, data, state);
		wRepairConsecutiveBreakfasts(weekly, inp, targets, data, state);
		wTrimWeeklyFat(weekly, targets, data, inp);
		wBalanceDailyDistribution(weekly, inp, targets, data, state);
		wBalanceDailyEnergy(weekly, inp, targets, data, state);
		wCapBreakfastProtein(weekly, inp, targets, data, state);
		wEnforceWeeklyOilCap(weekly, targets, data);
		wRecomputeWeekly(weekly, data);
		    days.forEach(function (d) {
	      d.slots.forEach(function (cell) {
	        if (/estimated/i.test(cell.confidence || '')) weekly.estimatedCount++;
	        var slot = slots.filter(function (s) { return s.slot_id === cell.slotId; })[0];
	        cell.swapOptions = wSwapOptions(cell, slot, inp, targets, data, state).slice(0, 4);
	      });
	    });
	    return weekly;
	  }
	  function wWeeklyOptimizationSummary(weekly, inp, targets, data) {
	    var groupTargets = wGroupTargetMap(inp, targets, data);
	    var pulseTarget = groupTargets.Pulses || 75;
	    var pulse = wExactWeeklyGroupAverage(weekly, data, 'Pulses');
	    var dairyTarget = groupTargets['Milk/curd'] || 300;
	    var dairy = wExactWeeklyGroupAverage(weekly, data, 'Milk/curd');
	    var proteinRatio = weekly.avgTotals.p / Math.max(targets.protein_g, 1);
	    var proteinFloorMet = meetsProteinFloor(weekly.avgTotals.p, targets.protein_g);
	    var energyRatio = weekly.avgTotals.e / Math.max(targets.kcal, 1);
	    var fatRatio = weekly.avgTotals.f / Math.max(targets.fat_g, 1);
	    var proteinScore = proteinFloorMet
	      ? clamp(Math.round(100 - Math.max(0, proteinRatio - 1.15) * 180), 70, 100)
	      : clamp(Math.round(100 - (0.95 - proteinRatio) * 420), 35, 99);
	    var nutritionScore = clamp(Math.round(100 - Math.abs(1 - energyRatio) * 180 - Math.max(0, fatRatio - 1.10) * 180), 35, 100);
	    var digestiveScore = 100;
	    if (weekly.avgTotals.fb > 40) digestiveScore -= Math.round((weekly.avgTotals.fb - 40) * 4);
	    if (pulse > pulseTarget * 1.15) digestiveScore -= Math.round((pulse / pulseTarget - 1.15) * 90);
	    if (dairy > dairyTarget * 1.25) digestiveScore -= Math.round((dairy / dairyTarget - 1.25) * 70);
	    digestiveScore = clamp(digestiveScore, 35, 100);
	    var familyUse = {}, recipeUse = {}, preparedRecipeUse = {}, proteinUses = 0, distributionIssues = 0;
	    var breakfastMisses = 0, breakfastOverloads = 0, breakfastRepeats = 0, portionIssues = 0;
	    var metaMap = wRecipeMetaMap(data), breakfastFloor = Math.min(24, Math.max(18, targets.protein_g * 0.20));
	    var breakfastCeiling = Math.min(34, Math.max(28, targets.protein_g * 0.30));
	    weekly.days.forEach(function (day) {
	      day.slots.forEach(function (cell) {
	        if (!cell.items || !cell.items.length) return;
	        if (cell.slotId === 'breakfast') {
	          if ((cell.totals.p || 0) < breakfastFloor * 0.90) breakfastMisses++;
	          if ((cell.totals.p || 0) > breakfastCeiling + 1) breakfastOverloads++;
	        }
	        if (desiredMealCount(inp.meals_per_day) >= 3 && wCellGroupGrams(cell, data, 'Milk/curd') > 225) portionIssues++;
	        var slot = (weekly.slots || []).filter(function (s) { return s.slot_id === cell.slotId; })[0] || {};
	        var range = wDistributionRange(cell.slotId, desiredMealCount(inp.meals_per_day), slot);
	        var share = (cell.totals.e || 0) / Math.max(targets.kcal, 1);
	        if (share < range[0] - 0.02 || share > range[1] + 0.02) distributionIssues++;
	        cell.items.forEach(function (it) {
	          var r = recipeById(data, it.rid), meta = metaMap[it.rid] || {};
	          if (!r) return;
	          if (it.sv > wPracticalServingCap(r, inp, cell.slotId) + 0.01) portionIssues++;
	          recipeUse[r.id] = (recipeUse[r.id] || 0) + 1;
	          if (wNeedsWeeklyPrep(r)) preparedRecipeUse[r.id] = (preparedRecipeUse[r.id] || 0) + 1;
	          var anchor = wProteinAnchorKind(r, meta);
	          if (!anchor) return;
	          var family = wRecipeFamily(r, meta);
	          familyUse[family] = (familyUse[family] || 0) + 1;
	          proteinUses++;
	        });
	      });
	    });
	    for (var breakfastIndex = 1; breakfastIndex < weekly.days.length; breakfastIndex++) {
	      var previousBreakfast = weeklyCellBySlotEngine(weekly.days[breakfastIndex - 1], 'breakfast');
	      var currentBreakfast = weeklyCellBySlotEngine(weekly.days[breakfastIndex], 'breakfast');
	      var previousIds = (previousBreakfast && previousBreakfast.items || []).map(function (it) { return it.rid; }).sort().join('+');
	      var currentIds = (currentBreakfast && currentBreakfast.items || []).map(function (it) { return it.rid; }).sort().join('+');
	      if (previousIds && previousIds === currentIds) breakfastRepeats++;
	    }
	    var families = Object.keys(familyUse), dominant = 0;
	    families.forEach(function (family) { dominant = Math.max(dominant, familyUse[family]); });
	    var dominantRatio = proteinUses ? dominant / proteinUses : 1;
	    var varietyScore = clamp(55 + families.length * 9 - Math.max(0, dominantRatio - 0.42) * 80, 35, 100);
	    var uniqueRecipes = Object.keys(recipeUse).length, maxRepeat = 0;
	    Object.keys(preparedRecipeUse).forEach(function (rid) { maxRepeat = Math.max(maxRepeat, preparedRecipeUse[rid]); });
	    var recipeTarget = wWeeklyRecipeTarget(inp), preparedRecipes = Object.keys(preparedRecipeUse).length;
	    var preparedRecipeTarget = wPreparedRecipeTarget(inp);
	    var varietyPreference = wLower(inp.weekly_variety_preference || 'balanced');
	    var recipeMinimum = varietyPreference === 'repeat_friendly' ? 9 : varietyPreference === 'maximum' ? 16 : 12;
	    var preferredRepeat = varietyPreference === 'repeat_friendly' ? 5 : varietyPreference === 'maximum' ? 3 : 4;
	    var practicalityScore = 100 - Math.max(0, preparedRecipes - preparedRecipeTarget) * 7 -
	      Math.max(0, recipeMinimum - preparedRecipes) * 3 - Math.max(0, maxRepeat - preferredRepeat) * 4 - portionIssues * 8;
	    if (inp.cooking_complexity === 'easy' && preparedRecipes > Math.min(preparedRecipeTarget, 14)) practicalityScore -= 8;
	    practicalityScore = clamp(Math.round(practicalityScore), 35, 100);
	    var distributionScore = clamp(100 - distributionIssues * 7 - breakfastMisses * 6 - breakfastOverloads * 7 - breakfastRepeats * 8, 35, 100);
	    var foodGroupScore = 100 - Math.round(Math.max(0, pulse / pulseTarget - 1.15) * 70) -
	      Math.round(Math.max(0, dairy / dairyTarget - 1.25) * 55);
	    foodGroupScore = clamp(foodGroupScore, 35, 100);
	    var total = Math.round(nutritionScore * 0.17 + proteinScore * 0.23 + digestiveScore * 0.17 +
	      distributionScore * 0.16 + varietyScore * 0.09 + practicalityScore * 0.12 + foodGroupScore * 0.06);
	    return {
	      total: clamp(total, 35, 100), nutrition: nutritionScore, protein: proteinScore,
	      digestiveComfort: digestiveScore, distribution: distributionScore, variety: varietyScore,
	      practicality: practicalityScore, foodGroups: foodGroupScore, uniqueRecipes: uniqueRecipes,
	      preparedRecipes: preparedRecipes, preparedRecipeTarget: preparedRecipeTarget,
	      proteinFamilies: families.length, dominantProteinRatio: Math.round(dominantRatio * 100),
	      pulseGrams: Math.round(pulse), dairyGrams: Math.round(dairy), breakfastMisses: breakfastMisses,
	      breakfastOverloads: breakfastOverloads, breakfastRepeats: breakfastRepeats, portionIssues: portionIssues, recipeTarget: recipeTarget,
	      constraintsMet: proteinFloorMet && fatRatio <= 1.15 && weekly.avgTotals.fb <= 45 &&
	        pulse <= pulseTarget * 1.20 && dairy <= dairyTarget * 1.25 && distributionIssues <= 2 &&
	        breakfastMisses === 0 && breakfastOverloads === 0 && breakfastRepeats === 0 && portionIssues === 0 &&
	        preparedRecipes <= preparedRecipeTarget + 2
	    };
	  }
	  Engine.buildWeeklyPlan = function (inp, targets, data, dailyBuilt) {
	    if (!targets || targets.blocks.length || !data || !data.recipes || !data.recipes.length) return null;
	    var baseSeed = wText(inp && inp.plan_seed || wProfileSeed(inp || {}));
	var count = desiredMealCount(inp.meals_per_day) <= 2 ? 2 : (inp.improve_plan ? 16 : 12);
	    var minimumCandidates = desiredMealCount(inp.meals_per_day) <= 2 ? 2 : 4;
	    var candidates = [];
	    for (var candidateIndex = 0; candidateIndex < count; candidateIndex++) {
	      var variant = {};
	      Object.keys(inp || {}).forEach(function (key) { variant[key] = inp[key]; });
	      variant.plan_seed = baseSeed + '|optimizer|' + candidateIndex;
	  variant.optimize_balance = true;
	      var candidate = wBuildWeeklyCandidate(variant, targets, data, dailyBuilt);
	      if (!candidate) continue;
	      candidate.optimization = wWeeklyOptimizationSummary(candidate, variant, targets, data);
	      candidate.optimizerCandidate = candidateIndex + 1;
	      candidates.push(candidate);
	      if (candidateIndex + 1 >= minimumCandidates && candidates.some(function (item) {
	        return item.optimization.constraintsMet && item.optimization.total >= 92;
	      })) break;
	    }
	    candidates.sort(function (a, b) {
	      if (a.optimization.constraintsMet !== b.optimization.constraintsMet) return a.optimization.constraintsMet ? -1 : 1;
	      return b.optimization.total - a.optimization.total;
	    });
	    if (!candidates.length) return null;
	    candidates[0].optimizerCandidatesTested = candidates.length;
	    return candidates[0];
	  };
	  Engine.applyWeeklySwap = function (weekly, dayIndex, slotId, option, data, inp, targets) {
	    if (!weekly || !option || !option.items) return weekly;
	    var day = weekly.days.filter(function (d) { return d.dayIndex === dayIndex; })[0];
	    if (!day) return weekly;
	    var cell = day.slots.filter(function (s) { return s.slotId === slotId; })[0];
	    if (!cell) return weekly;
	    cell.items = option.items.map(function (it) { return { rid: it.rid, sv: it.sv, oilCutG: +it.oilCutG || 0 }; });
	    cell.totals = wFormatTotals(wCellTotals(cell.items, data));
	    cell.confidence = option.type === 'smart_cross_exchange' ? 'smart swap applied' : 'same-exchange swap applied';
	    wRecomputeWeekly(weekly, data);
	    wEnforceWeeklyOilCap(weekly, weekly.targets || {}, data);
	    if (inp && targets) weekly.optimization = wWeeklyOptimizationSummary(weekly, inp, targets, data);
	    weekly.lastSwapNotice = day.day + ' ' + (cell.label || slotId) + ' changed to ' +
	      (option.label || 'your selected swap') + '. Day total is now ' + cleanMacro(day.totals.e) +
	      ' kcal with ' + cleanMacro(day.totals.p) + ' g protein.';
	    return weekly;
	  };
	  Engine.weeklyShoppingList = function (weekly, data) {
	    if (!weekly) return {};
	    var pseudo = { plan: [], totals: { vfat: weekly.avgTotals.vfat || 0 } };
	    weekly.days.forEach(function (d) {
	      d.slots.forEach(function (s) {
	        if (s.items && s.items.length) pseudo.plan.push({ items: s.items });
	      });
	    });
	    return shoppingListFromPlan(pseudo, data, true);
	  };
	  Engine.weeklyPlanGroupGrams = function (weekly, data) {
	    var out = {};
	    if (!weekly) return out;
	    weekly.days.forEach(function (d) { d.slots.forEach(function (s) { (s.items || []).forEach(function (it) {
	      var r = recipeById(data, it.rid);
	      (r && r.ing || []).forEach(function (g) {
	        var grp = DGI_GRP_MAP[g.grp]; if (!grp) return;
	        out[grp] = (out[grp] || 0) + g.g * it.sv / 7;
	      });
	    }); }); });
	    out['Fats & oils'] = weekly.avgTotals.vfat || 0;
	    Object.keys(out).forEach(function (k) { out[k] = Math.round(out[k]); });
	    return out;
	  };
	  Engine.weeklyPulseGrams = function (weekly, data) {
	    var pulse = 0;
	    if (!weekly) return 0;
	    weekly.days.forEach(function (d) { d.slots.forEach(function (s) { (s.items || []).forEach(function (it) {
	      var r = recipeById(data, it.rid);
	      (r && r.ing || []).forEach(function (g) { if (g.grp === 'pulse') pulse += g.g * it.sv / 7; });
	    }); }); });
	    return Math.round(pulse);
	  };

	  /* Accuracy vs tolerances. High fibre is not treated as automatically better. */
  Engine.accuracy = function (built, targets, data) {
    var cfg = data.config, t = built.totals;
    function row(label, target, actual, tolPct) {
      var gap = actual - target;
      var gapPct = target ? gap / target : 0;
      return { label: label, target: Math.round(target * 10) / 10, actual: actual,
               gapPct: Math.round(gapPct * 1000) / 10,
               pass: Math.abs(gapPct) <= tolPct / 100 };
    }
    var rows = [
      row('Energy (kcal)', targets.kcal, t.e, cfg.energy_tolerance_pct),
      row('Protein (g)', targets.protein_g, t.p, cfg.protein_tolerance_pct + 5), /* +5: small plans round coarsely */
      row('Fat (g)', targets.fat_g, t.f, cfg.fat_tolerance_pct + 10),
      row('Carbohydrate (g)', targets.carb_g, t.cb, cfg.carb_tolerance_pct + 8)
    ];
    function bandScore(target, actual, full, zero, max) {
      if (!target) return max;
      var dev = Math.abs(actual - target) / target;
      if (dev <= full) return max;
	  if (dev >= zero) return Math.round(max * 0.25);
	  return Math.round(max * (1 - (dev - full) / (zero - full) * 0.75));
    }
    var score = 0;
	  score += bandScore(targets.kcal, t.e, 0.05, 0.20, 20);
	  score += bandScore(targets.protein_g, t.p, 0.05, 0.25, 25);
	  score += bandScore(targets.fat_g, t.f, 0.08, 0.30, 20);
	  score += bandScore(targets.carb_g, t.cb, 0.08, 0.25, 10);
	  if (t.fb >= targets.fibre_g - 3 && t.fb <= 40) score += 15;
	  else if (t.fb > 45) score += 3;
	  else if (t.fb > 40) score += 9;
	  else score += Math.max(3, Math.round(15 * t.fb / Math.max(targets.fibre_g, 1)));
    score += (t.vfat <= targets.visible_fat_cap_g ? 10 : 5);
    return { rows: rows, score: Math.min(100, score) };
  };

  function clamp(v, min, max) { return Math.max(min, Math.min(max, v)); }
  function meetsProteinFloor(actual, target, fraction) {
    var floorFraction = fraction == null ? 0.95 : fraction;
    return Math.round((+actual || 0) * 10) >= Math.round((+target || 0) * floorFraction * 10);
  }
  function pctScore(target, actual, fullBand, zeroBand) {
    if (!target) return 100;
    var dev = Math.abs(actual - target) / target;
    if (dev <= fullBand) return 100;
    if (dev >= zeroBand) return 35;
    return Math.round(100 - (dev - fullBand) / (zeroBand - fullBand) * 65);
  }
  function mealRoleCounts(meal, data) {
    var out = {};
    meal.items.forEach(function (it) {
      var r = recipeById(data, it.rid);
      if (r && r.role) out[r.role] = (out[r.role] || 0) + it.sv;
    });
    return out;
  }

  function smartMealScoreRow(label, items, totals, targetEnergy, isSnack, isDinner, data, targets) {
      var energyScore = isSnack
        ? pctScore(targetEnergy, totals.e, 0.30, 0.70)
        : pctScore(targetEnergy, totals.e, 0.12, 0.45);
      var roles = mealRoleCounts({ items: items || [] }, data);
	  var split = targetEnergy / Math.max(targets.kcal || 1, 1);
	  var proteinTarget = targets.protein_g * split;
	  var fatTarget = targets.fat_g * split;
	  var isMainMeal = /lunch|dinner/i.test(label || '');
	  var vegetableGrams = wCellGroupGrams({ items: items || [] }, data, 'Vegetables + GLV');
	  var hasProteinStructure = roles.protein_lean || roles.protein_fat || roles.dairy || (proteinTarget && totals.p >= proteinTarget * 0.8);
	  var estimated = (items || []).some(function (it) {
	    var r = recipeById(data, it.rid);
	    return r && /estimated/i.test([r.conf, r.qa_status, r.warning_tags].join(' '));
	  });
      var balance = 60;
      if (roles.veg || roles.fruit) balance += 12;
      if (roles.protein_lean || roles.protein_fat || roles.dairy) balance += 16;
      if (roles.cereal) balance += 8;
      if (totals.fb >= 5) balance += 4;
	  if (proteinTarget && totals.p < proteinTarget * 0.75) balance -= 14;
	  if (fatTarget && totals.f > fatTarget * 1.25) balance -= 12;
	  if (isMainMeal && vegetableGrams < 50) balance -= 10;
      if (totals.vfat > targets.visible_fat_cap_g * 0.45) balance -= 12;
      if (totals.na > 800) balance -= 8;
	  if (totals.fb > 25) balance -= 34;
	  else if (totals.fb > 20) balance -= 24;
	  else if (totals.fb > 15) balance -= 12;
	  else if (totals.fb > 12) balance -= 6;
      if (isDinner && totals.fb > 12) balance -= 7;   /* late high-fibre meal can bloat */
      if (isSnack && totals.p < 5.5) balance -= 6;
	  if (estimated) balance -= 5;
      balance = clamp(balance, 35, 100);
      var score = Math.round(energyScore * 0.55 + balance * 0.45);
      var note = [];
      if (energyScore < 75) note.push('energy split needs review');
	  if (!hasProteinStructure && (!proteinTarget || totals.p < proteinTarget * 0.8)) note.push('add a protein food');
	  else if (proteinTarget && totals.p < proteinTarget * 0.55) note.push('protein is low for this meal');
	  else if (proteinTarget && totals.p < proteinTarget * 0.90) note.push('increase the protein portion slightly');
	  if (fatTarget && totals.f > fatTarget * 1.25) note.push('fat is high for this meal');
	  if (isMainMeal && vegetableGrams < 50) note.push('add a vegetable side');
	  else if (isMainMeal && vegetableGrams < 100) note.push('increase the vegetable portion');
      if (totals.fb < 4 && !(roles.veg || roles.fruit)) note.push('add fibre/produce');
      if (totals.fb > 25) note.push('very high fibre load; reduce pulse/veg if bloated');
	  else if (totals.fb > 20) note.push('high fibre meal; reduce the pulse or grain portion');
	  else if (totals.fb > 15) note.push('high fibre meal; build up gradually');
      else if (totals.fb > 12) note.push(isDinner ? 'moderate-high fibre; keep dinner earlier if bloating occurs' : 'moderate-high fibre meal');
      if (isSnack && totals.p < 5.5) note.push('protein-light snack; use curd, sattu or roasted chana if hungry');
      if (totals.na > 800) note.push('watch sodium');
	  if (estimated) note.push('includes estimated recipe values');
      if (!note.length) note.push('balanced meal structure');
	  if (totals.fb > 25) score = Math.min(score, 69);
	  else if (totals.fb > 20 || (targetEnergy && totals.e > targetEnergy * 1.25)) score = Math.min(score, 79);
	  else if (totals.fb > 15 || estimated || (proteinTarget && totals.p < proteinTarget * 0.75)) score = Math.min(score, 89);
      return {
        meal: label, score: score, energy: Math.round(totals.e),
        protein: Math.round(totals.p * 10) / 10, fibre: Math.round(totals.fb * 10) / 10,
        note: note.join('; ')
      };
  }

  Engine.smartMealScores = function (built, targets, data) {
    if (!built) return [];
	    if (built.weekly && built.weekly.days) {
	      var slotsById = {};
	      (built.weekly.slots || []).forEach(function (s) { slotsById[s.slot_id] = s; });
	      var rows = [];
	      built.weekly.days.forEach(function (d) {
	        d.slots.forEach(function (cell) {
	          if (!cell || !cell.items || !cell.items.length || cell.slotId === 'on_waking') return;
	          var slot = slotsById[cell.slotId] || {};
	          var targetEnergy = targets.kcal * (slot.kcal_split_pct || 0);
	          var label = d.day + ' ' + (cell.label || slot.display_label || cell.slotId);
	          rows.push(smartMealScoreRow(label, cell.items, cell.totals || wCellTotals(cell.items, data),
	            targetEnergy, /snack|morning/.test(cell.slotId), /dinner/.test(cell.slotId), data, targets));
	        });
	      });
	      return rows;
	    }
    return built.plan.map(function (m) {
      var totals = planTotals([m], data, built.oilCut || {});
      var targetEnergy = targets.kcal * m.split;
	      return smartMealScoreRow(m.name, m.items, totals, targetEnergy, /snack/i.test(m.name || ''), /dinner/i.test(m.name || ''), data, targets);
    });
  };

  function foodGroupAlignmentScore(inp, targets, built, data) {
    if (!built || !targets) return 70;
    var targetRows = Engine.foodGroupTargets(targets.kcal, inp.diet_type, data);
    var planned = built.weekly ? Engine.weeklyPlanGroupGrams(built.weekly, data) : plannedGroupGrams(built, data);
    var score = 100;
    targetRows.forEach(function (g) {
      var actual = planned[g.grp];
      if (actual == null || !g.qty) { score -= /nuts/i.test(g.grp) ? 4 : 8; return; }
      var ratio = actual / g.qty, dev = Math.abs(ratio - 1);
      if (dev <= 0.05) return;
      if (dev <= 0.10) score -= 3;
      else if (dev <= 0.15) score -= 7;
      else score -= Math.min(18, 12 + Math.round((dev - 0.15) * 25));
      if (/pulse/i.test(g.grp) && ratio > 1.25) score -= 4;
	  if (/fruit/i.test(g.grp) && ratio < 0.5) score -= 8;
	  if (/nuts/i.test(g.grp) && ratio < 0.5) score -= 8;
    });
    if (built.totals.fb > Math.max(45, targets.fibre_g * 1.8)) score -= 10;
    else if (built.totals.fb > Math.max(38, targets.fibre_g * 1.5)) score -= 6;
    if (built.totals.f > targets.fat_g * 1.15) score -= 8;
    if (built.totals.vfat > targets.visible_fat_cap_g * 1.05) score -= 6;
    if (built.totals.na > data.config.sodium_high_threshold_mg) score -= 8;
    if (built.weekly && built.weekly.estimatedCount) score -= Math.min(8, Math.round(built.weekly.estimatedCount / 5));
    return clamp(Math.round(score), 40, 100);
  }

  Engine.smartPlanScore = function (inp, anthro, targets, built, data, feedback) {
    if (!built) return null;
    var acc = Engine.accuracy(built, targets, data);
    var mealScores = Engine.smartMealScores(built, targets, data);
    var avgMeal = mealScores.length ? Math.round(mealScores.reduce(function (s, m) { return s + m.score; }, 0) / mealScores.length) : 70;
    var warnings = Engine.warningsFor(inp, targets, built, anthro, data);
    var safety = 100;                          /* severity-weighted; info notes don't penalise */
    warnings.forEach(function (w) {
      safety -= w.severity === 'critical' ? 22 : w.severity === 'high' ? 15 : w.severity === 'info' ? 0 : 10;
    });
    safety = clamp(safety, 35, 100);

	    var recipeIds = {}, preparedRecipeIds = {};
	    if (built.weekly && built.weekly.days) {
	      built.weekly.days.forEach(function (d) {
	        d.slots.forEach(function (s) { (s.items || []).forEach(function (it) {
	          recipeIds[it.rid] = 1;
	          var recipe = recipeById(data, it.rid);
	          if (recipe && wNeedsWeeklyPrep(recipe)) preparedRecipeIds[it.rid] = 1;
	        }); });
	      });
	    } else {
	      built.plan.forEach(function (m) { m.items.forEach(function (it) {
	        recipeIds[it.rid] = 1;
	        var recipe = recipeById(data, it.rid);
	        if (recipe && wNeedsWeeklyPrep(recipe)) preparedRecipeIds[it.rid] = 1;
	      }); });
	    }
	    var uniqueRecipes = Object.keys(recipeIds).length;
	    var preparedRecipes = Object.keys(preparedRecipeIds).length;
	    var optimization = built.weekly && built.weekly.optimization ? built.weekly.optimization : null;
	    var recipePracticality;
	    if (!built.weekly) recipePracticality = clamp(Math.round(uniqueRecipes * 100 / 8), 55, 100);
	    else if (optimization) recipePracticality = optimization.practicality;
	    else if (preparedRecipes < 10) recipePracticality = clamp(60 + preparedRecipes * 3, 55, 90);
	    else if (preparedRecipes <= 18) recipePracticality = 96;
	    else recipePracticality = clamp(96 - (preparedRecipes - 18) * 6, 55, 96);
	    if (built.weekly && inp.cooking_complexity === 'easy' && preparedRecipes > 14) recipePracticality = Math.max(55, recipePracticality - 8);
    var dgi = foodGroupAlignmentScore(inp, targets, built, data);
	var proteinFit = optimization ? optimization.protein : pctScore(targets.protein_g, built.totals.p, 0.05, 0.35);
	var digestiveComfort = optimization ? optimization.digestiveComfort
	  : clamp(100 - Math.max(0, built.totals.fb - 40) * 4, 35, 100);
	var distribution = optimization ? optimization.distribution : avgMeal;
	var proteinVariety = optimization ? optimization.variety : recipePracticality;
	if (optimization) recipePracticality = optimization.practicality;

    var fb = feedback || {};
    var adherence = fb.count ? clamp(Math.round(fb.avgFit * 20), 35, 100) : 75;
    var estimatePenalty = built.weekly && built.weekly.estimatedCount ? Math.min(8, Math.round(built.weekly.estimatedCount / 5)) : 0;
    var total = optimization
	  ? Math.round(optimization.total * 0.90 + safety * 0.10 - estimatePenalty)
	  : Math.round(acc.score * 0.25 + proteinFit * 0.18 + safety * 0.12 + avgMeal * 0.15 +
	      dgi * 0.12 + proteinVariety * 0.08 + recipePracticality * 0.05 + adherence * 0.05 - estimatePenalty);
    total = clamp(total, 35, 100);
    var rows = [
	  ['Nutrition fit', acc.score, 'Energy, fat and carbohydrate compared with your targets'],
	  ['Protein target', proteinFit, cleanMacro(built.totals.p) + ' of ' + cleanMacro(targets.protein_g) + ' g/day'],
      ['Safety and comfort checks', safety, warnings.length ? warnings.length + ' review note(s)' : 'No major warning for this profile'],
	  ['Digestive comfort', digestiveComfort, cleanMacro(built.totals.fb) + ' g fibre/day with pulse and dairy load checked'],
	  ['Meal distribution', distribution, 'Calories and protein spread across your selected meal pattern'],
	  ['Protein-source variety', proteinVariety, optimization
	    ? optimization.proteinFamilies + ' protein families; largest family ' + optimization.dominantProteinRatio + '% of protein uses'
	    : 'Rotation across the available protein foods'],
      ['Food-group alignment', dgi, 'Food-group balance, fibre, visible fat and sodium'],
	  ['Cooking practicality', recipePracticality, preparedRecipes + ' cooked dishes; fruit, milk and simple staples are counted separately'],
      ['Personalisation feedback', adherence, fb.count ? fb.count + ' previous feedback record(s)' : 'No prior feedback yet']
    ];
    var next = [];
    if (fb.count && fb.avgHunger <= 2.5) next.push('Previous hunger feedback is low: add volume from vegetables, dal, curd, soup or salad before increasing calories.');
    if (fb.count && fb.avgEnergy <= 2.5) next.push('Previous energy feedback is low: check meal timing, training carbs and sleep before reducing calories further.');
    if (fb.count && fb.avgFit <= 2.5) next.push('Previous adherence feedback is low: choose simpler recipes and reduce cooking complexity.');
	if (!meetsProteinFloor(built.totals.p, targets.protein_g)) next.push(proteinCorrectionText(inp, built.totals.p, targets.protein_g));
	if (optimization && optimization.digestiveComfort < 80) next.push('Digestive load is high: use smaller pulse portions, choose well-cooked dals, and replace one pulse serving with a tolerated non-pulse protein food.');
	if (optimization && optimization.proteinFamilies < 4) next.push('Protein rotation is narrow: include more of the protein foods that suit your diet type across the week.');
	if (optimization && optimization.practicality < 80) next.push('Preparation is demanding: repeat two or three simple meal bases and vary only the vegetable or protein side.');
	if (built.weekly && optimization && optimization.preparedRecipes > optimization.preparedRecipeTarget + 2) next.push('This week needs extra cooking. Repeat two or three simple meal bases to reduce preparation and shopping.');
    if (built.totals.na > data.config.sodium_high_threshold_mg) next.push('Estimated sodium is ' + built.totals.na + ' mg/day from the recipe model: reduce salt, pickles, papad and packaged foods.');
    if (!next.length) next.push('Keep this structure for 1-2 weeks, track feedback, then adjust portions based on progress and hunger.');
	return { total: total, components: rows, meals: mealScores, nextActions: next, optimization: optimization };
  };

  Engine.planRationale = function (inp, anthro, targets, built, data, smart) {
    var out = [];
    out.push(['Safety first', inp.medical_flags && inp.medical_flags.length
      ? 'You selected a health condition, so treat this as an educational draft and review it with a qualified professional.'
      : 'Basic adult safety checks were completed for age, minimum calories, deficit size and protein level.']);
    out.push(['Calorie calculation', 'Your maintenance calories are estimated from sex, age band, body weight and activity, then adjusted to ' +
      targets.kcal + ' kcal/day for your selected goal. Estimated maintenance is ' + targets.tee + ' kcal/day.']);
    out.push(['Protein target', 'Protein is set at ' + targets.protein_g + ' g/day (' + targets.protein_gkg +
      ' g/kg) based on your body weight and goal.']);
    out.push(['Carb and fat balance', 'Fat is planned at ' + targets.fat_g + ' g/day (' + targets.fat_pct +
      '% kcal), then carbohydrate fills the remaining energy at about ' + targets.carb_g + ' g/day with fibre target ' + targets.fibre_g + ' g.']);
    if (built) {
      out.push(['Meal selection', 'Recipes and portions are matched to your calories, protein target, diet type, region and meal pattern.']);
      out.push(['Next adjustment', smart && smart.nextActions ? smart.nextActions.join(' ') : 'Use feedback and weekly progress to adjust the next plan.']);
    }
    return out;
  };

  Engine.publicClinicalHandoff = function (inp) {
    var flags = inp.medical_flags || [];
    function has(v) { return flags.indexOf(v) >= 0; }
    if (!flags.length) return null;

    var rows = [];
    if (has('ckd')) {
      rows.push('Kidney disease selected: a safe renal plan needs CKD stage, dialysis status, edema, albumin, glucose, medicines and recent potassium/phosphorus labs.');
    }
    if (has('diabetes')) {
      rows.push('Diabetes selected: carbohydrate portions should be matched with medicine or insulin timing, glucose targets and low-sugar risk.');
    }
    if (has('hypertension')) {
      rows.push('High BP selected: sodium limit, oil quality, edema, kidney status and BP medicines should be reviewed before use.');
    }
    if (has('pregnancy')) {
      rows.push('Pregnancy/lactation selected: this public adult planner is not suitable for pregnancy or lactation diet prescriptions.');
    }
    if (has('thyroid')) rows.push('Thyroid selected: review medication timing, iodine status, weight goal and coexisting conditions before using the plan.');
    if (has('pcos')) rows.push('PCOS selected: review insulin resistance, cycle history, lipids, activity, sleep and sustainable carbohydrate distribution.');
    if (has('other')) rows.push('Other medical condition selected: review diagnosis, medicines, symptoms, labs, allergies and monitoring before following a plan.');

    return {
      title: has('ckd') ? 'Clinical renal plan required' : 'Clinical safety review recommended',
      rows: rows,
      blockPublicPlan: has('ckd') || has('pregnancy'),
      preset: has('ckd') ? { clinical_setting: 'CKD', guideline: 'KDIGO_CKD' }
        : has('hypertension') ? { clinical_setting: 'Cardiac_HTN', guideline: 'CARDIAC_HTN' }
        : has('diabetes') ? { clinical_setting: 'General', guideline: 'GENERAL_EXCHANGE' }
        : null
    };
  };

  Engine.warningsFor = function (inp, targets, built, anthro, data) {
    var W = data.messages.warnings, out = [];
    function add(code, sev) { out.push({ code: code, msg: W[code], severity: sev || 'warning' }); }
    function addMsg(code, msg, sev) { out.push({ code: code, msg: msg, severity: sev || 'warning' }); }
    if (anthro.bmiCode === 'under' && inp.goal === 'fat_loss') add('WARN_UNDERWEIGHT');
    if (anthro.bmiCode === 'obese2') add('WARN_OBESE2');
    if (inp.medical_flags && inp.medical_flags.length) add('WARN_MEDICAL', 'high');
    if (inp.medical_flags && inp.medical_flags.indexOf('ckd') >= 0) {
      addMsg('WARN_CKD_PUBLIC', 'Kidney disease was selected. This public planner cannot safely prescribe renal protein, sodium, potassium, phosphorus or fluid targets.', 'critical');
    }
    if (inp.medical_flags && inp.medical_flags.indexOf('pregnancy') >= 0) {
      addMsg('WARN_PREG_PUBLIC', 'Pregnancy/lactation was selected. Use an obstetric clinical nutrition workflow; do not use the public adult plan as a prescription.', 'critical');
    }
    if (inp.medical_flags && inp.medical_flags.indexOf('diabetes') >= 0) {
      addMsg('WARN_DM_PUBLIC', 'Diabetes was selected. Treat this as an educational draft only: carbohydrate timing, medication/insulin, hypoglycemia risk, renal status and SMBG/CGM patterns need dietitian or doctor review.', 'warning');
    }
    if (inp.medical_flags && inp.medical_flags.indexOf('hypertension') >= 0) {
      addMsg('WARN_HTN_PUBLIC', 'High BP was selected. Keep the sodium limit stricter, avoid packaged salty foods, pickles and papad, and review BP medicines, edema and kidney labs before using this as a plan.', 'warning');
    }
    if (inp.medical_flags && inp.medical_flags.indexOf('other') >= 0) {
      addMsg('WARN_OTHER_PUBLIC', 'Another medical condition was selected. Diagnosis, medicines, symptoms, allergies, labs and monitoring targets must be reviewed before this can become a prescription.', 'warning');
    }
    if (inp.goal === 'muscle_gain' && !inp.resistance_training) add('WARN_TRAINING');
    if (targets.protein_gkg > data.proteinModes.review_threshold) add('WARN_PROTEIN_REVIEW');
    if (desiredMealCount(inp.meals_per_day) <= 2) {
      addMsg('WARN_LOW_MEAL_FREQUENCY', desiredMealCount(inp.meals_per_day) === 1
        ? 'One-meal plans are shown because you selected 1 meal/day. This can be difficult for protein, fibre, digestion, diabetes medicines, acidity and training recovery. Use professional review if you have any medical condition or symptoms.'
        : 'Two-meal plans are shown because you selected 2 meals/day. Protein and fibre are easier to tolerate when portions are split, so adjust with a dietitian if digestion, hunger, glucose control or training recovery is an issue.', 'info');
    }
    if (built) {
	  if (!meetsProteinFloor(built.totals.p, targets.protein_g)) {
		addMsg('WARN_PROTEIN_LOW', proteinCorrectionText(inp, built.totals.p, targets.protein_g));
      }
      if (built.totals.f > targets.fat_g * 1.15) add('WARN_FAT_HIGH');
      if (built.totals.na > data.config.sodium_high_threshold_mg) {
        var naTone = built.totals.na <= 2200 ? 'This is close to the ideal upper limit'
          : built.totals.na <= 2800 ? 'This is above the ideal limit'
          : 'This is well above the ideal limit';
        var hasBP = inp.medical_flags && inp.medical_flags.indexOf('hypertension') >= 0;
        addMsg('WARN_SALT', 'Estimated sodium: ' + built.totals.na + ' mg/day from this recipe model. ' +
          naTone + (hasBP ? ', especially because high BP was selected' : '') +
          '. Keep added salt, pickles, papad, namkeen, sauces, packaged foods and restaurant meals limited. ' +
          'Your salt cap of ' + targets.salt_cap_g + ' g/day includes salt used in cooking, table salt and hidden sodium from packaged foods.', 'warning');
      }
      /* fibre bands (feedback): high-fibre Indian veg plans should advise gradual
         increase, not "add more fibre" */
      var fb = Math.round(built.totals.fb);
      if (fb > 45) addMsg('WARN_FIBRE_VHIGH', 'This is a very high-fibre plan (' + fb + ' g/day). Increase fibre gradually and reduce pulse or vegetable portions if you get gas or bloating.', 'warning');
	  else if (fb < 20) addMsg('WARN_FIBRE_LOW', 'Fibre is on the lower side (' + fb + ' g/day). Add more vegetables, fruit, pulses and whole grains.', 'info');
	  var groupPlanned = built.weekly ? Engine.weeklyPlanGroupGrams(built.weekly, data) : plannedGroupGrams(built, data);
	  var avoidText = wLower([inp.allergies_text, inp.dislikes_text].join(' '));
	  if ((groupPlanned.Fruits || 0) < 75 && !/fruit|apple|banana|papaya|orange|guava|pear|melon/.test(avoidText)) {
	    addMsg('WARN_FRUIT_LOW', 'This plan does not yet include a full daily fruit serving. Add one whole seasonal fruit on most days, or choose a fruit swap that remains within your calorie target.', 'warning');
	  }
	  if ((groupPlanned['Nuts & seeds'] || 0) < 7 && !/nut|seed|almond|walnut|peanut/.test(avoidText)) {
	    addMsg('WARN_NUTS_LOW', 'Nuts and seeds are low in this plan. Where calories allow, replace some cooking oil or a higher-fat dairy portion with 10-15 g unsalted nuts or seeds.', 'info');
	  }
    }
    return out;
  };

  function roundShopQty(weekly, step) {
    step = step || 50;
    return Math.max(step, Math.ceil(weekly / step) * step);
  }
  function fmtWeightQty(weekly, step) {
	var planned = Math.max(5, Math.round(weekly / 5) * 5);
	var buy = roundShopQty(weekly, step);
	function weight(v) {
	  if (v >= 900 && v < 1100) return '1 kg';
	  return v >= 1000 ? (v / 1000).toFixed(2).replace(/0+$/, '').replace(/\.$/, '') + ' kg' : v + ' g';
	}
	return 'buy about ' + weight(buy) + '; planned use about ' + weight(planned) + '/week';
  }
  function fmtMilkQty(weekly, item) {
	var planned = Math.max(25, Math.round(weekly / 25) * 25);
	var buy = roundShopQty(weekly, 500);
	function volume(v) { return v >= 1000 ? (v / 1000).toFixed(2).replace(/0+$/, '').replace(/\.$/, '') + ' L' : v + ' ml'; }
	return 'buy about ' + volume(buy) + '; planned use about ' + volume(planned) + '/week';
  }
  function fmtFruitQty(weekly) {
	var planned = Math.max(50, Math.round(weekly / 50) * 50);
	var buy = roundShopQty(weekly, 100);
	var servings = Math.max(1, Math.round(planned / 100));
	function weight(v) { return v >= 1000 ? (v / 1000).toFixed(2).replace(/0+$/, '').replace(/\.$/, '') + ' kg' : v + ' g'; }
	return 'buy about ' + weight(buy) + '; planned use about ' + weight(planned) + '/week (about ' + servings + ' medium fruit servings)';
  }
  function fmtLemons(weekly) {
    var count = Math.max(1, Math.ceil(weekly / 35));
    if (count <= 4) return '2-4 small lemons/week';
    return count + ' small lemons/week';
  }
  function fmtOilQty(weekly) {
    var planned = Math.round(weekly);
    var buy = Math.max(10, Math.ceil(weekly / 10) * 10);
    var plannedTsp = Math.round((planned / 5) * 10) / 10;
    var gramsPerDay = Math.round((weekly / 7) * 10) / 10;
    var tspPerDay = Math.round((gramsPerDay / 5) * 10) / 10;
    return 'buy about ' + buy + ' g/week; planned use about ' + planned +
      ' g/week (~' + plannedTsp + ' tsp/week, ' + gramsPerDay + ' g/day, ~' + tspPerDay + ' tsp/day)';
  }

	  function canonicalShopEntry(grp, item) {
	    var lc = String(item || '').toLowerCase(), out = { grp: grp, item: item || 'Ingredient' };
	    if (/besan|gram flour/.test(lc)) out = { grp: 'pulse', item: 'Chickpea flour (besan)' };
	    else if (/sattu|roasted gram/.test(lc)) out = { grp: 'pulse', item: 'Roasted gram flour (sattu)' };
	    else if (/soy chunks|soya chunks|textured soy/.test(lc)) out = { grp: 'pulse', item: 'Soy chunks' };
	    else if (/moong|green gram/.test(lc)) out = { grp: 'pulse', item: 'Moong dal' };
	    else if (/masoor|lentil dal/.test(lc)) out = { grp: 'pulse', item: 'Masoor dal' };
	    else if (/toor|tur dal|arhar|red gram/.test(lc)) out = { grp: 'pulse', item: 'Toor dal' };
	    else if (/urad|black gram/.test(lc)) out = { grp: 'pulse', item: 'Urad dal' };
	    else if (/chana dal|bengal gram,? dal/.test(lc)) out = { grp: 'pulse', item: 'Chana dal' };
	    else if (/rajma|rajmah|kidney bean/.test(lc)) out = { grp: 'pulse', item: 'Rajma' };
	    else if (/chole|kabuli chana|chickpea/.test(lc)) out = { grp: 'pulse', item: 'Kabuli chana' };
	    else if (/atta|whole wheat|wheat flour/.test(lc)) out = { grp: 'cereal', item: 'Whole-wheat flour (atta)' };
	    else if (/dalia|daliya|broken wheat/.test(lc)) out = { grp: 'cereal', item: 'Broken wheat (dalia)' };
	    else if (/brown rice/.test(lc)) out = { grp: 'cereal', item: 'Brown rice' };
	    else if (/\brice\b|plain rice|idli rice|dosa rice/.test(lc)) out = { grp: 'cereal', item: 'White rice' };
	    else if (/curd marinade/.test(lc)) out = { grp: 'dairy_g', item: 'Curd (dahi)' };
	    else if (/greek yogurt/.test(lc)) out = { grp: 'dairy_g', item: 'Greek yogurt' };
	    else if (/curd|dahi|yogurt|yoghurt/.test(lc)) out = { grp: 'dairy_g', item: 'Curd (dahi)' };
	    else if (/buttermilk|chaas|lassi/.test(lc)) out = { grp: 'dairy_ml', item: 'Buttermilk' };
	    else if (/paneer/.test(lc)) out = { grp: 'dairy_g', item: 'Paneer' };
	    else if (/\bmilk\b/.test(lc)) out = { grp: 'dairy_ml', item: 'Milk' };
	    else if (/tofu/.test(lc)) out = { grp: 'soy', item: 'Tofu' };
	    else if (/egg/.test(lc)) out = { grp: 'egg', item: 'Eggs' };
	    else if (/chicken/.test(lc)) out = { grp: 'poultry', item: 'Chicken' };
	    else if (/fish|rohu|pomfret|mackerel|sardine/.test(lc)) out = { grp: 'fish', item: 'Fish' };
	    else if (/almond/.test(lc)) out = { grp: 'nuts', item: 'Almonds' };
	    else if (/walnut/.test(lc)) out = { grp: 'nuts', item: 'Walnuts' };
	    else if (/peanut|groundnut/.test(lc)) out = { grp: 'nuts', item: 'Peanuts' };
	    else if (/amla/.test(lc)) out = { grp: 'fruit', item: 'Amla' };
	    else if (/apple/.test(lc)) out = { grp: 'fruit', item: 'Apple' };
	    if (out.grp === 'dairy') out.grp = /paneer/.test(lc) ? 'dairy_g' : 'dairy_ml';
	    if (out.grp === 'protein' && !/egg|poultry|fish|soy/.test(out.grp)) out.grp = 'protein_other';
	    return out;
	  }
	  function canonicalShopEntries(grp, item) {
	    var lc = String(item || '').toLowerCase();
	    if (/urad/.test(lc) && /toor|tur|arhar/.test(lc)) {
	      return [{ grp: 'pulse', item: 'Urad dal', share: 0.5 }, { grp: 'pulse', item: 'Toor dal', share: 0.5 }];
	    }
	    if (/moong/.test(lc) && /toor|tur|arhar/.test(lc) && /masoor/.test(lc)) {
	      return [
	        { grp: 'pulse', item: 'Moong dal', share: 1 / 3 },
	        { grp: 'pulse', item: 'Toor dal', share: 1 / 3 },
	        { grp: 'pulse', item: 'Masoor dal', share: 1 / 3 }
	      ];
	    }
	    var one = canonicalShopEntry(grp, item);
	    one.share = 1;
	    return [one];
	  }

	  /* Weekly shopping list (Shopping_List_V7 aggregation + rounding rules) */
	  function shoppingListFromPlan(built, data, alreadyWeekly) {
	    var agg = {};
	    built.plan.forEach(function (m) {
	      m.items.forEach(function (it) {
	        var r = recipeById(data, it.rid);
	        if (!r) return;
	        (r.ing || []).forEach(function (g) {
	          canonicalShopEntries(g.grp, g.item || '').forEach(function (entry) {
	            var grp = entry.grp, item = entry.item, daily = g.g * it.sv * entry.share;
          if (grp === 'veg') {
            var itemLc0 = String(item).toLowerCase();
            var recipeName = String(r.name || '').toLowerCase();
            if (itemLc0.indexOf('lemon') >= 0) agg.__lemon = (agg.__lemon || 0) + daily;
            else if (r.id === 'R048' || recipeName.indexOf('salad') >= 0 || recipeName.indexOf('kachumber') >= 0 || itemLc0.indexOf('cucumber') >= 0) {
              agg.__vegSalad = (agg.__vegSalad || 0) + daily;
            } else if (/onion|tomato|capsicum|curry base|tadka/.test(itemLc0)) {
              agg.__vegBase = (agg.__vegBase || 0) + daily;
            } else {
              agg.__vegMain = (agg.__vegMain || 0) + daily;
            }
          } else if (grp === 'glv') {
            agg.__glv = (agg.__glv || 0) + daily;
          } else {
            var key = grp + '|' + item;
            agg[key] = (agg[key] || 0) + daily;
          }
	          });
        });
      });
    });
    var rounding = { cereal: 50, pulse: 50, veg: 250, glv: 250, fruit: 100,
                     dairy_ml: 500, nuts: 50, protein_other: 100, dairy_g: 100,
                     soy: 100, egg: 50, poultry: 100, fish: 100 };
    var label = { cereal: 'Cereals & millets', pulse: 'Pulses & legumes', veg: 'Vegetables',
                  glv: 'Green leafy vegetables', fruit: 'Fruits', dairy_ml: 'Dairy',
                  dairy_g: 'Dairy', nuts: 'Nuts & seeds', oil: 'Visible oil/ghee',
                  soy: 'Dairy alternatives & soy', egg: 'Eggs', poultry: 'Poultry',
                  fish: 'Fish', protein_other: 'Protein foods' };
    var groups = {};
	    function push(grpLabel, item, qty) {
	      (groups[grpLabel] = groups[grpLabel] || []).push({ item: item, qty: qty });
	    }
	    var multiplier = alreadyWeekly ? 1 : 7;
	    Object.keys(agg).forEach(function (key) {
	      if (key.indexOf('__') === 0) return;
	      var parts = key.split('|'), grp = parts[0], item = parts[1];
	      var weekly = agg[key] * multiplier;
	      if (grp === 'oil') return;               /* use final adjusted oil below */
	      if (grp === 'dairy_ml') push(label[grp], item, fmtMilkQty(weekly, item));
	      else if (grp === 'fruit' && /amla/i.test(item)) push(label[grp], item, fmtWeightQty(weekly, 50));
	      else if (grp === 'fruit') push(label[grp], item, fmtFruitQty(weekly));
	      else if (grp === 'egg') push(label[grp], item, Math.ceil(weekly / 50) + ' eggs');
	      else push(label[grp] || grp, item, fmtWeightQty(weekly, rounding[grp] || 50));
	    });
	    if (agg.__vegMain > 0) push(label.veg, 'Mixed seasonal vegetables', fmtWeightQty(agg.__vegMain * multiplier, 250));
	    if (agg.__vegBase > 0) push(label.veg, 'Onion + tomato cooking base', fmtWeightQty(agg.__vegBase * multiplier, 250));
	    if (agg.__vegSalad > 0) push(label.veg, 'Salad vegetables', fmtWeightQty(agg.__vegSalad * multiplier, 250));
	    if (agg.__lemon > 0) push(label.veg, 'Lemons', fmtLemons(agg.__lemon * multiplier));
	    if (agg.__glv > 0) push(label.glv, 'Leafy greens', fmtWeightQty(agg.__glv * multiplier, 250));
	    if (built.totals && built.totals.vfat > 0) {
	      push(label.oil, 'Cooking oil/ghee for all recipes', fmtOilQty(built.totals.vfat * 7));
	    }
	    return groups;
	  }
	  Engine.shoppingList = function (built, data) {
	    return shoppingListFromPlan(built, data, false);
	  };

  /* Analyse-diet mode: totals for a list of {code, grams} vs profile targets */
  Engine.analyse = function (items, profile, data) {
    var keys = ['e', 'p', 'f', 'cb', 'fb', 'ca', 'fe', 'zn', 'mg', 'na', 'k', 'vc', 'fo'];
    var tot = {};
    keys.forEach(function (k) { tot[k] = 0; });
    items.forEach(function (it) {
      var f = null;
      for (var i = 0; i < data.foods.length; i++) if (data.foods[i].c === it.code) { f = data.foods[i]; break; }
      if (!f) return;
      keys.forEach(function (k) { if (f[k]) tot[k] += f[k] * it.grams / 100; });
    });
    keys.forEach(function (k) { tot[k] = Math.round(tot[k] * 10) / 10; });
    var cmp = null;
    if (profile && profile.weight_kg) {
      cmp = {
        protein_rda_g: Math.round(profile.weight_kg * data.proteinModes.maintenance.base * 10) / 10,
        protein_ear_g: Math.round(profile.weight_kg * 0.66 * 10) / 10,
        fibre_g: 25,
        sodium_cap_mg: data.config.sodium_high_threshold_mg
      };
    }
    return { totals: tot, compare: cmp };
  };

  /* Clinical dietitian draft planner. This mirrors the workbook clinical
     target logic and composes the Sheet rule tables, exchange lists and
     carb-counting data. It is intentionally gated for professional use. */
  function cNum(v) {
    if (v == null || v === '') return null;
    var n = +v;
    return isFinite(n) ? n : null;
  }
  function cRound(v, d) {
    if (v == null || v === '') return '';
    var p = Math.pow(10, d || 0);
    return Math.round(v * p) / p;
  }
  function cText(v) { return v == null ? '' : String(v); }
  function cContains(text, needle) {
    return cText(text).toLowerCase().indexOf(String(needle).toLowerCase()) >= 0;
  }
  function rowsFor(data, key) {
    return (data && Array.isArray(data[key])) ? data[key] : [];
  }
  function clinicalRuleMatches(data, inp) {
    var guideline = inp.guideline || '';
    var sourceNeedles = [];
    if (guideline === 'ASPEN_ICU') sourceNeedles = ['ASPEN'];
    else if (guideline === 'ESPEN_ICU') sourceNeedles = ['ESPEN_ICU'];
    else if (guideline === 'INDIAN_ICU') sourceNeedles = ['INDIAN_ICU'];
    else if (guideline === 'KDIGO_CKD') sourceNeedles = ['KDIGO'];
    else if (guideline === 'ESPEN_KIDNEY') sourceNeedles = ['ESPEN_KIDNEY'];
    else if (guideline === 'IAPEN_SCREEN') sourceNeedles = ['IAPEN'];

    function byNeedle(rows) {
      return rows.filter(function (r) {
        var hay = [r.source_id, r.rule_id, r.population, r.domain, r.parameter].join(' ');
        for (var i = 0; i < sourceNeedles.length; i++) if (cContains(hay, sourceNeedles[i])) return true;
        return false;
      });
    }

    var rows = [];
    if (guideline === 'CARDIAC_HTN') rows = rowsFor(data, 'cardiacRules');
    else if (guideline === 'CAD_METABOLIC') rows = rowsFor(data, 'cadRules');
    else rows = byNeedle(rowsFor(data, 'clinicalRules'));
    if (guideline === 'KDIGO_CKD' || inp.clinical_setting === 'Hospital_Kidney') {
      rows = rows.concat(rowsFor(data, 'renalRules'));
    }
    return rows.slice(0, 12);
  }
  function exchangeGoal(inp) {
    if (inp.plan_goal === 'fat_loss' || inp.guideline === 'CAD_METABOLIC') return 'fat_loss';
    return 'maintenance';
  }
  function exchangeDiet(inp) {
    return inp.diet_type === 'nonveg' ? 'nonveg' : 'veg';
  }
  function pickExchangePlan(inp, targetKcal, data) {
    var rows = rowsFor(data, 'exchangePlans');
    if (!rows.length || !targetKcal) return null;
    var goal = exchangeGoal(inp), diet = exchangeDiet(inp);
    var best = null, bestScore = Infinity;
    rows.forEach(function (r) {
      if (cText(r.goal) !== goal || cText(r.diet_type) !== diet) return;
      var kcal = cNum(r.target_kcal);
      if (!kcal) return;
      var score = Math.abs(kcal - targetKcal);
      if (score < bestScore) { best = r; bestScore = score; }
    });
    if (!best) {
      rows.forEach(function (r2) {
        if (cText(r2.diet_type) !== diet) return;
        var kcal2 = cNum(r2.target_kcal);
        if (!kcal2) return;
        var score2 = Math.abs(kcal2 - targetKcal) + 500;
        if (score2 < bestScore) { best = r2; bestScore = score2; }
      });
    }
    return best;
  }
  function mealDistribution(planId, data) {
    return rowsFor(data, 'exchangeMealDistribution').filter(function (r) { return cText(r.plan_id) === cText(planId); });
  }
  function exchangeFoodOptions(data) {
    var out = {};
    rowsFor(data, 'exchangeFoodLists').forEach(function (r) {
      var g = cText(r.exchange_group);
      if (!g) return;
      out[g] = out[g] || [];
      if (out[g].length < 5) out[g].push(r);
    });
    return out;
  }
  function carbChoiceSize(data) {
    var rows = data.carbCounting && Array.isArray(data.carbCounting.exchangeListLogic)
      ? data.carbCounting.exchangeListLogic : [];
    for (var i = 0; i < rows.length; i++) {
      if (cContains(rows[i].setting_key, 'carb_choice') || cContains(rows[i].setting_key, 'carbohydrate')) {
        var v = cNum(rows[i].value);
        if (v) return v;
      }
    }
    return 15;
  }
  function clinicalBlocksAndFlags(inp, bmi) {
    var blocks = [], flags = [];
    function flag(sev, msg) { flags.push({ severity: sev, message: msg }); }
    if (!inp.professional_attest) blocks.push('Professional attestation is required before creating a clinical draft.');
    if (inp.age < 18 || inp.pediatric_patient) blocks.push('Pediatric case: specialist pediatric dietitian review is required.');
    if (inp.pregnancy_lactation) blocks.push('Pregnancy/lactation case: use dedicated obstetric nutrition care.');
    if (inp.en_contraindication || inp.tube_feed_pn) {
      blocks.push('Tube feed, EN contraindication, or PN case: hospital team formula/route protocol required.');
    }
    if (inp.clinical_setting === 'ICU' && inp.hemodynamic_status === 'uncontrolled_shock') {
      flag('critical', 'Uncontrolled shock: delay enteral nutrition until perfusion/shock is controlled.');
    }
    if (inp.clinical_setting === 'ICU' && (inp.aki_present || inp.ckd_stage !== 'none' || inp.dialysis_or_krt !== 'none')) {
      flag('critical', 'ICU plus CKD/AKI/KRT: renal and ICU dietitian review required; do not apply outpatient CKD protein restriction.');
    }
    if (inp.serum_potassium_status === 'high') flag('critical', 'Hyperkalemia: potassium must be individualized; block generic high-potassium fruit/vegetable advice.');
    if (inp.serum_phosphorus_status === 'high') flag('high', 'High phosphorus: review protein sources and phosphate additives.');
    if (inp.malnutrition_refeeding) flag('critical', 'Malnutrition/refeeding risk: use hospital protocol, thiamine/electrolytes and close monitoring.');
    if (inp.cancer_treatment) flag('high', 'Cancer treatment/cachexia risk: avoid generic fat-loss plans; oncology dietitian review needed.');
    if (bmi >= 30 && inp.clinical_setting === 'ICU') flag('high', 'ICU obesity: prefer indirect calorimetry; otherwise use adjusted weight fallback.');
    return { blocks: blocks, flags: flags };
  }

  Engine.clinicalPlan = function (inp, data) {
    var wt = cNum(inp.weight_kg), ht = cNum(inp.height_cm), age = cNum(inp.age);
    var bmi = ht ? wt / Math.pow(ht / 100, 2) : null;
    var ideal = inp.sex === 'female' ? 0.9 * (ht - 106) : 0.9 * (ht - 100);
    var adjusted = bmi > 25 ? ideal + 0.25 * (wt - ideal) : wt;
    var ref = (inp.guideline === 'ASPEN_ICU' || inp.guideline === 'ESPEN_ICU' || inp.guideline === 'INDIAN_ICU') ? adjusted : wt;
    var icuDay = cNum(inp.icu_day) || 1;
    var measured = cNum(inp.measured_ee_kcal);
    var nonNutrition = (cNum(inp.propofol_kcal) || 0) + (cNum(inp.citrate_kcal) || 0);
    var kcalLow = null, kcalHigh = null, proLow = null, proHigh = null;
    var sodiumLimit = '', route = '', notes = [];

    if (inp.guideline === 'ASPEN_ICU') {
      kcalLow = 12 * ref; kcalHigh = 25 * ref; proLow = 1.2 * ref; proHigh = 2.0 * ref;
      route = 'Early EN when safe; avoid supplemental PN before ICU day 7 for average non-malnourished patients.';
    } else if (inp.guideline === 'ESPEN_ICU') {
      if (measured) {
        kcalLow = (icuDay <= 3 ? 0.5 : 0.8) * measured;
        kcalHigh = (icuDay <= 3 ? 0.7 : 1.0) * measured;
      } else {
        kcalLow = (icuDay <= 3 ? 12 : 20) * ref;
        kcalHigh = (icuDay <= 3 ? 18 : 25) * ref;
      }
      proLow = 1.0 * ref; proHigh = 1.3 * ref;
      route = 'Prefer oral/ONS if safe; otherwise early EN within 48 h when shock is controlled.';
    } else if (inp.guideline === 'INDIAN_ICU') {
      kcalLow = 25 * ref; kcalHigh = 30 * ref; proLow = 1.2 * ref; proHigh = 2.0 * ref;
      route = 'ICU nutrition plan requires monitoring of tolerance, glycemia, electrolytes and route.';
    } else if (inp.guideline === 'KDIGO_CKD') {
      if (inp.metabolically_unstable) {
        proLow = 1.0 * ref; proHigh = 1.3 * ref;
        notes.push('Metabolic instability: do not use outpatient low-protein CKD planning.');
      } else {
        proLow = 0.8 * ref; proHigh = 0.8 * ref;
      }
      sodiumLimit = '2000 mg/day';
      notes.push('Energy target should be individualized for CKD; enter a manual kcal target when using the exchange plan.');
    } else if (inp.guideline === 'ESPEN_KIDNEY') {
      if (measured) { kcalLow = 0.8 * measured; kcalHigh = 1.0 * measured; }
      else { kcalLow = 25 * ref; kcalHigh = 30 * ref; }
      if (inp.dialysis_or_krt === 'CKRT' || inp.dialysis_or_krt === 'PIKRT') { proLow = 1.5 * ref; proHigh = 1.7 * ref; }
      else if (inp.dialysis_or_krt === 'Intermittent_KRT') { proLow = 1.3 * ref; proHigh = 1.5 * ref; }
      else if (inp.metabolically_unstable) { proLow = 1.0 * ref; proHigh = 1.3 * ref; }
      else { proLow = 0.8 * ref; proHigh = 1.0 * ref; }
      sodiumLimit = inp.ckd_stage !== 'none' ? 'Individualized; CKD default 2000 mg/day if applicable' : 'Individualized';
    } else if (inp.guideline === 'CARDIAC_HTN' || inp.guideline === 'CAD_METABOLIC') {
      var baseKcal = cNum(inp.target_kcal);
      if (!baseKcal && wt && ht) {
        var bmr = Engine.calcBmr(inp.sex === 'female' ? 'female' : 'male', age || 45, wt, data);
        baseKcal = Math.round(bmr * 1.4 * (inp.plan_goal === 'fat_loss' || inp.guideline === 'CAD_METABOLIC' ? 0.8 : 1));
      }
      kcalLow = baseKcal ? baseKcal * 0.95 : null;
      kcalHigh = baseKcal ? baseKcal * 1.05 : null;
      proLow = 1.0 * wt; proHigh = 1.2 * wt;
      sodiumLimit = '1500-2000 mg/day';
      notes.push('Use DASH/Mediterranean-style fat quality: low SFA, zero trans fat, higher MUFA/PUFA, high fiber.');
    } else {
      kcalLow = 25 * wt; kcalHigh = 30 * wt; proLow = 1.0 * wt; proHigh = 1.2 * wt;
      sodiumLimit = 'Individualized';
    }

    var gate = clinicalBlocksAndFlags(inp, bmi);
    var nutritionLow = kcalLow == null ? null : Math.max(0, kcalLow - nonNutrition);
    var nutritionHigh = kcalHigh == null ? null : Math.max(0, kcalHigh - nonNutrition);
    var targetKcal = cNum(inp.target_kcal) || (nutritionLow != null && nutritionHigh != null ? (nutritionLow + nutritionHigh) / 2 : null);
    var exch = pickExchangePlan(inp, targetKcal, data);
    var carbG = exch && cNum(exch.carb_g) ? cNum(exch.carb_g) : cNum(inp.carb_target_g);
    var carbChoice = carbChoiceSize(data);
    var carbBudget = [];
    if (carbG) {
      [
        ['Breakfast', 0.25],
        ['Lunch', 0.35],
        ['Snack', 0.10],
        ['Dinner', 0.30]
      ].forEach(function (m) {
        var g = carbG * m[1];
        carbBudget.push({ meal: m[0], carb_g: cRound(g, 0), choices: cRound(g / carbChoice, 1) });
      });
    }

    return {
      bmi: cRound(bmi, 1), ideal_weight_kg: cRound(ideal, 1), adjusted_weight_kg: cRound(adjusted, 1),
      reference_weight_kg: cRound(ref, 1), energy_low_kcal: cRound(kcalLow, 0), energy_high_kcal: cRound(kcalHigh, 0),
      nutrition_energy_low_kcal: cRound(nutritionLow, 0), nutrition_energy_high_kcal: cRound(nutritionHigh, 0),
      protein_low_g: cRound(proLow, 0), protein_high_g: cRound(proHigh, 0), sodium_limit: sodiumLimit,
      non_nutrition_kcal: cRound(nonNutrition, 0), target_kcal: cRound(targetKcal, 0), route: route,
      notes: notes, blocks: gate.blocks, flags: gate.flags, exchangePlan: exch,
      mealDistribution: exch ? mealDistribution(exch.plan_id, data) : [],
      exchangeFoodOptions: exchangeFoodOptions(data), carbBudget: carbBudget,
      matchedRules: clinicalRuleMatches(data, inp)
    };
  };

  /* ====================== browser UI (skipped in Node) =================== */
  if (typeof module !== 'undefined' && module.exports) { module.exports = Engine; return; }
  if (typeof document === 'undefined') return;

  var root, DATA, els = {};
	  var LS_FORM = 'dxp_ns_form_v1', LS_DATA = 'dxp_ns_data_v23', LS_TRACK = 'dxp_ns_tracker_v1', LS_FEEDBACK = 'dxp_ns_feedback_v1', LS_UNITS = 'dxp_ns_units_v1', LS_ANALYSE = 'dxp_ns_analyse_v1', LS_PLAN_SEED = 'dxp_ns_plan_seed_v1', LS_RECENT_PLANS = 'dxp_ns_recent_plans_v1';
  var REMOTE_CACHE_HOURS = 6;
  var remoteLoadPromise = null, remoteLoadDone = false;

  function $(sel, ctx) { return (ctx || root).querySelector(sel); }
  function $all(sel, ctx) { return Array.prototype.slice.call((ctx || root).querySelectorAll(sel)); }
  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = cleanUiText(text);
    return n;
  }

  function loadEmbedded() {
    var node = document.getElementById('dxp-dataset');
    return node ? JSON.parse(node.textContent) : null;
  }

  function str(v) { return v == null ? '' : String(v); }
  function numOrNull(v) {
    if (v == null || v === '') return null;
    var n = +v;
    return isFinite(n) ? n : null;
  }
  function numOrZero(v) {
    var n = numOrNull(v);
    return n == null ? 0 : n;
  }
  function first(o, keys) {
    for (var i = 0; i < keys.length; i++) {
      if (o[keys[i]] != null && o[keys[i]] !== '') return o[keys[i]];
    }
    return null;
  }
  function copyObject(o) {
    var out = {};
    Object.keys(o || {}).forEach(function (k) { out[k] = o[k]; });
    return out;
  }
  function mergeObject(base, add) {
    var out = copyObject(base);
    Object.keys(add || {}).forEach(function (k) {
      if (add[k] != null && add[k] !== '') out[k] = add[k];
    });
    return out;
  }
  function mergeNested(base, add) {
    var out = copyObject(base);
    Object.keys(add || {}).forEach(function (k) {
      if (add[k] && typeof add[k] === 'object' && !Array.isArray(add[k])) out[k] = mergeObject(out[k], add[k]);
      else if (add[k] != null && add[k] !== '') out[k] = add[k];
    });
    return out;
  }
  function addQuery(url, key, val) {
    return url + (url.indexOf('?') >= 0 ? '&' : '?') + encodeURIComponent(key) + '=' + encodeURIComponent(val);
  }
  function titleCase(v) {
    return str(v).replace(/_/g, ' ').replace(/\b\w/g, function (m) { return m.toUpperCase(); });
  }
  function safeTitle(v) {
    return str(v || 'Client').replace(/[^\w\s-]/g, '').replace(/\s+/g, ' ').trim() || 'Client';
  }
  function dataSource(payload) {
    if (!payload || typeof payload !== 'object') return null;
    return payload.dataset || payload.data || payload.payload || payload;
  }
  function sourceVersion(payload, src) {
    return (payload && payload.version) || (src && src.version) ||
      (src && src.meta && (src.meta.version || src.meta.remote_version)) || null;
  }
  function hasWeeklyV23(src) {
    return !!(src && Array.isArray(src.weeklyMealSlots) && src.weeklyMealSlots.length >= 6 &&
      Array.isArray(src.weeklyMealPatterns) && src.weeklyMealPatterns.length >= 42 &&
      Array.isArray(src.recipeRotationPool) && src.recipeRotationPool.length >= 20 &&
      Array.isArray(src.recipeIngredients) && src.recipeIngredients.length >= 20);
  }
  function cachePayloadUsable(payload) {
    var src = dataSource(payload);
    if (!src || typeof src !== 'object' || src.status === 'error') return false;
    return hasWeeklyV23(src) || (Array.isArray(src.foods) && src.foods.length >= 20 && Array.isArray(src.recipes) && src.recipes.length >= 20);
  }
  function setDataStatus(state, text) {
    if (!root) return;
    var labels = {
      embedded: 'Nutrition data ready',
      loading: 'Loading nutrition data...',
      live: 'Nutrition data ready',
      cached: 'Nutrition data ready',
      error: 'Nutrition data ready'
    };
    $all('.dxp-data-status').forEach(function (n) {
      n.setAttribute('data-state', state);
      n.textContent = text || labels[state] || labels.embedded;
    });
  }
  /* live Sheet sync is a non-critical background refresh: the embedded database
     is complete and current, so a failed/empty fetch is NOT a user-facing error.
     Keep the pill calm and log the reason quietly for debugging. */
  function dataFallback(reason) {
    try { if (window.console && console.warn) console.warn('[DietXP] using built-in nutrition database (' + reason + ')'); } catch (e) {}
    setDataStatus('embedded');
  }
  function updateVersionLabels() {
    if (!DATA || !DATA.meta) return;
    var version = DATA.meta.remote_version || DATA.meta.version || '';
    $all('.dxp-data-version').forEach(function (n) { n.textContent = version; });
  }
  function normalizeFood(f) {
    var code = first(f, ['c', 'food_code', 'code']);
    var name = first(f, ['n', 'food_name', 'name']);
    if (!code || !name) return null;
    return {
      c: str(code), n: str(name),
      g: str(first(f, ['g', 'food_group', 'category']) || ''),
      d: str(first(f, ['d', 'diet_type']) || ''),
      e: numOrZero(first(f, ['e', 'energy_kcal'])),
      p: numOrZero(first(f, ['p', 'protein_g'])),
      f: numOrZero(first(f, ['f', 'fat_g'])),
      cb: numOrZero(first(f, ['cb', 'carb_g', 'carbohydrate_g'])),
      fb: numOrZero(first(f, ['fb', 'fiber_g', 'fibre_g'])),
      ca: numOrNull(first(f, ['ca', 'calcium_mg'])),
      fe: numOrNull(first(f, ['fe', 'iron_mg'])),
      zn: numOrNull(first(f, ['zn', 'zinc_mg'])),
      mg: numOrNull(first(f, ['mg', 'magnesium_mg'])),
      na: numOrZero(first(f, ['na', 'sodium_mg'])),
      k: numOrNull(first(f, ['k', 'potassium_mg'])),
      vc: numOrNull(first(f, ['vc', 'vitamin_c_mg'])),
      fo: numOrNull(first(f, ['fo', 'folate_ug'])),
      exchange_group: str(first(f, ['exchange_group']) || ''),
      grams_per_carb_choice: numOrNull(first(f, ['grams_per_carb_choice'])),
      qa_status: str(first(f, ['qa_status']) || ''),
      src: str(first(f, ['src', 'source', 'source_version']) || 'Google Sheet')
    };
  }
  function mergeFoods(rows) {
    var byCode = {};
    var order = [];
    DATA.foods.forEach(function (f) { byCode[f.c] = f; order.push(f.c); });
    var valid = 0;
    rows.forEach(function (row) {
      var next = normalizeFood(row);
      if (!next || next.e <= 0) return;
      valid++;
      if (!byCode[next.c]) order.push(next.c);
      byCode[next.c] = mergeObject(byCode[next.c] || {}, next);
    });
    if (valid < 20) return false;
    DATA.foods = order.map(function (code) { return byCode[code]; }).filter(Boolean);
    return true;
  }
  function normalizeRecipe(r) {
    var id = first(r, ['id', 'recipe_id']);
    var name = first(r, ['name', 'recipe_name']);
    if (!id || !name) return null;
    return {
      id: str(id), name: str(name),
      measure: str(first(r, ['measure', 'default_measure']) || ''),
      recipe_serving_label: str(first(r, ['recipe_serving_label', 'serving_label']) || ''),
      household_measure: str(first(r, ['household_measure']) || ''),
      primary_raw_amount: str(first(r, ['primary_raw_amount']) || ''),
      cooked_yield: str(first(r, ['cooked_yield']) || ''),
      key_protein_amount: str(first(r, ['key_protein_amount']) || ''),
      e: numOrZero(first(r, ['e', 'energy_kcal'])),
      p: numOrZero(first(r, ['p', 'protein_g'])),
      f: numOrZero(first(r, ['f', 'fat_g'])),
      cb: numOrZero(first(r, ['cb', 'carb_g'])),
      fb: numOrZero(first(r, ['fb', 'fiber_g', 'fibre_g'])),
      ca: numOrNull(first(r, ['ca', 'calcium_mg'])),
      fe: numOrNull(first(r, ['fe', 'iron_mg'])),
      zn: numOrNull(first(r, ['zn', 'zinc_mg'])),
      na: numOrZero(first(r, ['na', 'sodium_mg'])),
      k: numOrNull(first(r, ['k', 'potassium_mg'])),
      vc: numOrNull(first(r, ['vc', 'vitamin_c_mg'])),
      fo: numOrNull(first(r, ['fo', 'folate_ug'])),
      vfat: numOrZero(first(r, ['vfat', 'visible_fat_g'])),
	      salt_g: numOrNull(first(r, ['salt_g', 'salt_added_g'])),
	      carb_choices: numOrNull(first(r, ['carb_choices', 'rounded_choices_half'])),
	      role: str(first(r, ['role', 'meal_role_or_plate_role', 'meal_role']) || ''),
	      diet_type: str(first(r, ['diet_type', 'diet_allowed']) || ''),
	      region: str(first(r, ['region']) || ''),
	      meal_type: str(first(r, ['meal_type', 'meal_slots']) || ''),
	      goal_fit: str(first(r, ['goal_fit']) || ''),
	      qa_status: str(first(r, ['qa_status']) || ''),
	      note: str(first(r, ['note', 'notes']) || ''),
	      warning_tags: str(first(r, ['warning_tags']) || ''),
	      conf: str(first(r, ['conf', 'confidence']) || ''),
      src: str(first(r, ['src', 'source', 'source_version']) || 'Google Sheet')
    };
  }
  function mergeRecipes(rows) {
    var byId = {};
    var order = [];
    DATA.recipes.forEach(function (r) { byId[r.id] = r; order.push(r.id); });
    rows.forEach(function (row) {
      var next = normalizeRecipe(row);
      if (!next || next.e <= 0) return;
      if (!byId[next.id]) order.push(next.id);
      var merged = mergeObject(byId[next.id] || {}, next);
      if (byId[next.id] && byId[next.id].ing && !next.ing) merged.ing = byId[next.id].ing;
      if (byId[next.id] && byId[next.id].role && !next.role) merged.role = byId[next.id].role;
      byId[next.id] = merged;
    });
	    DATA.recipes = order.map(function (id) { return byId[id]; }).filter(Boolean);
	  }
	  function mergeRecipeIngredients(rows) {
	    if (!Array.isArray(rows) || !rows.length) return false;
	    var byId = {};
	    rows.forEach(function (row) {
	      var rid = first(row, ['recipe_id', 'id']);
	      var name = first(row, ['ingredient_name', 'item', 'food_name']);
	      var grams = numOrNull(first(row, ['raw_g_per_serving', 'grams', 'g']));
	      if (!rid || !name || !grams) return;
	      var grp = str(first(row, ['ingredient_group', 'exchange_group', 'grp']) || '').toLowerCase().trim();
	      if (!grp) grp = 'other';
	      if (grp === 'milk') grp = 'dairy_ml';
	      if (grp === 'fat') grp = 'oil';
	      (byId[str(rid)] = byId[str(rid)] || []).push({
	        grp: grp,
	        item: str(name),
	        g: grams,
	        exchange_group: str(first(row, ['exchange_group']) || ''),
	        shopping_group: str(first(row, ['shopping_group']) || ''),
	        allergen_tags: str(first(row, ['allergen_tags']) || '')
	      });
	    });
	    var attached = 0;
	    DATA.recipes.forEach(function (r) {
	      if (byId[r.id] && byId[r.id].length) {
	        r.ing = byId[r.id];
	        attached++;
	      }
	    });
	    return attached > 0;
	  }
  function fetchJsonp(url) {
    return new Promise(function (resolve, reject) {
      var cb = '__dxpSheet' + Date.now() + Math.floor(Math.random() * 100000);
      var script = document.createElement('script');
      var done = false;
      var timer = window.setTimeout(function () { cleanup(); reject(new Error('Sheet JSONP timeout')); }, 18000);
      function cleanup() {
        if (done) return;
        done = true;
        window.clearTimeout(timer);
        try { delete window[cb]; } catch (e) { window[cb] = undefined; }
        if (script.parentNode) script.parentNode.removeChild(script);
      }
      window[cb] = function (payload) { cleanup(); resolve(payload); };
      script.onerror = function () { cleanup(); reject(new Error('Sheet JSONP failed')); };
      script.src = addQuery(url, 'callback', cb);
      document.head.appendChild(script);
    });
  }
  function requestRemote(url) {
    var full = addQuery(url, 'type', 'all');
    /* Google Apps Script /exec endpoints return a redirect to
       googleusercontent.com without readable CORS headers, so a browser fetch()
       reliably fails the CORS read and only wastes its timeout window before the
       JSONP fallback. For those endpoints, go straight to JSONP. */
    if (typeof fetch !== 'function' || /script\.google(usercontent)?\.com/i.test(url)) {
      return fetchJsonp(full);
    }
    var fetchRun = fetch(full, { credentials: 'omit', cache: 'no-store' })
      .then(function (r) {
        if (!r.ok) throw new Error('Sheet HTTP ' + r.status);
        return r.json();
      });
    var timeout = new Promise(function (_, reject) {
      window.setTimeout(function () { reject(new Error('Sheet fetch timeout')); }, 8000);
    });
    return Promise.race([fetchRun, timeout]).catch(function () { return fetchJsonp(full); });
  }

  /* Apps Script override: version-cached ?type=all fetch. Payloads are
     validated and merged only from public-safe calculator fields. */
  function maybeFetchRemote(url) {
    if (!url) {
      remoteLoadDone = true;
      setDataStatus('embedded');
      updateVersionLabels();
      return Promise.resolve(false);
    }
    setDataStatus('loading');
    try {
      var cached = JSON.parse(localStorage.getItem(LS_DATA) || 'null');
      if (cached && cached.payload && cached.fetchedAt &&
          Date.now() - cached.fetchedAt < REMOTE_CACHE_HOURS * 3600 * 1000 &&
          cachePayloadUsable(cached.payload)) {
        remoteLoadDone = true;
        mergeRemote(cached.payload, 'cached');
        return Promise.resolve(true);
      }
      localStorage.removeItem('dxp_ns_data_v2');
      if (cached && !cachePayloadUsable(cached.payload)) localStorage.removeItem(LS_DATA);
    } catch (e) { /* ignore cache errors */ }
    remoteLoadDone = false;
    remoteLoadPromise = requestRemote(url)
      .then(function (payload) {
        if (mergeRemote(payload, 'live')) {
          try { localStorage.setItem(LS_DATA, JSON.stringify({ fetchedAt: Date.now(), payload: payload })); } catch (e) {}
          return true;
        }
        return false;
      })
      .catch(function () {
        dataFallback('live sync unavailable');
        updateVersionLabels();
        return false;
      })
      .then(function (ok) {
        remoteLoadDone = true;
        return ok;
      });
    return remoteLoadPromise;
  }

  function mergeRemote(payload, state) {
    var src = dataSource(payload);
    if (!src || typeof src !== 'object' || src.status === 'error') {
      dataFallback('invalid Sheet response');
      return false;
    }
    var changed = false;

    if (Array.isArray(src.foods)) {
      if (!mergeFoods(src.foods)) {
        dataFallback('Sheet foods invalid');
        return false;
      }
      buildFoodOptions();
      changed = true;
    }
	    if (Array.isArray(src.recipes)) {
	      mergeRecipes(src.recipes);
	      changed = true;
	    }
	    if (Array.isArray(src.recipeIngredients)) {
	      if (mergeRecipeIngredients(src.recipeIngredients)) changed = true;
	    }
    if (Array.isArray(src.templates) && src.templates.length && src.templates[0].plan) {
      DATA.templates = src.templates;
      changed = true;
    }
    if (src.config && typeof src.config === 'object') { DATA.config = mergeObject(DATA.config, src.config); changed = true; }
    if (src.messages && typeof src.messages === 'object') { DATA.messages = mergeNested(DATA.messages, src.messages); changed = true; }

	    ['exchangePlans', 'exchangeMealDistribution', 'exchangeFoodLists', 'carbCounting',
	     'weeklyMealSlots', 'weeklyMealPatterns', 'recipeRotationPool', 'recipeIngredients', 'smartSwapRules', 'sampleMealPlans',
	     'clinicalRules', 'clinicalIntakeFields', 'clinicalTargetTemplate', 'clinicalSafetyGates', 'clinicalConflicts', 'clinicalReadiness',
	     'cardiacRules', 'renalRules', 'cadRules', 'sourceRegister'].forEach(function (key) {
      if (src[key]) { DATA[key] = src[key]; changed = true; }
    });

    if (!changed) {
      dataFallback('no public Sheet tables');
      return false;
    }
    DATA.meta = DATA.meta || {};
    DATA.meta.remote_version = sourceVersion(payload, src) || DATA.meta.remote_version || DATA.meta.version;
    DATA.meta.remote_loaded_at = new Date().toISOString();
    setDataStatus(state || 'live');
    updateVersionLabels();
    if (analyseItems.length) renderAnalyse();
    return true;
  }

	  var reportBusy = false;
	  function setReportBusy(busy, message) {
	    reportBusy = !!busy;
	    if (root) root.classList.toggle('dxp-is-building', reportBusy);
	    var submit = $('.dxp-submit');
	    if (submit) {
	      if (!submit.getAttribute('data-idle-label')) submit.setAttribute('data-idle-label', submit.textContent);
	      submit.disabled = reportBusy;
	      submit.textContent = reportBusy ? 'Building your week...' : submit.getAttribute('data-idle-label');
	    }
	    $all('.dxp-improve-week, .dxp-regenerate-week').forEach(function (button) { button.disabled = reportBusy; });
	    var status = $('#dxpReportStatus');
	    if (status) status.textContent = message || (reportBusy ? 'Choosing practical portions and balancing your seven days.' : '');
	  }
	  function scheduleReport() {
	    if (reportBusy) return;
	    setReportBusy(true);
	    window.setTimeout(function () {
	      var completed = false;
	      try {
	        runReport();
	        completed = true;
	      } catch (error) {
	        if (window.console && console.error) console.error('DietXP plan generation failed', error);
	      } finally {
	        setReportBusy(false, completed ? '' : 'We could not create this week. Check your entries and try again.');
	      }
	    }, 24);
	  }
  function runReportWhenDataReady() {
    var url = root && root.querySelector('.dxp-calculator') &&
      root.querySelector('.dxp-calculator').getAttribute('data-apps-script-url');
    if (url && remoteLoadPromise && !remoteLoadDone) {
      setDataStatus('loading', 'Loading nutrition data...');
	      remoteLoadPromise.then(scheduleReport, scheduleReport);
      return;
    }
	    scheduleReport();
  }

  /* ----------------------------- wizard --------------------------------- */
  var step = 1, MAX_STEP = 4;
  var STEP_LABELS = ['Your Profile', 'Your Goal', 'Food Preferences', 'Your Plan'];
  var GOAL_LABELS = { fat_loss: 'Lose fat', maintenance: 'Maintain weight', muscle_gain: 'Gain muscle' };
  var DIET_LABELS = { veg: 'Vegetarian', egg: 'Eggitarian', nonveg: 'Non-vegetarian' };
  var REGION_LABELS = { north: 'North Indian', south: 'South Indian', general: 'General Indian' };
  var ACTIVITY_LABELS = {
    sedentary: 'Desk based',
    light: 'Lightly active',
    moderate: 'Moderate',
    heavy: 'Very active',
    vigorous: 'Athletic'
  };

  function setControlVisible(btn, visible) {
    if (!btn) return;
    if (!btn.getAttribute('data-label')) {
      btn.setAttribute('data-label', btn.textContent || '');
    }
    btn.hidden = !visible;
    if (visible) {
      btn.removeAttribute('aria-hidden');
      btn.removeAttribute('tabindex');
      btn.textContent = btn.getAttribute('data-label') || '';
    } else {
      btn.setAttribute('aria-hidden', 'true');
      btn.setAttribute('tabindex', '-1');
      btn.textContent = '';
    }
  }

  function showStep(n) {
    step = Math.min(MAX_STEP, Math.max(1, n));
    $all('.dxp-step').forEach(function (s) {
      s.classList.toggle('active', +s.getAttribute('data-step') === step);
    });
    $all('.dxp-stepper li').forEach(function (s) {
      var idx = +s.getAttribute('data-step-dot');
      s.classList.toggle('is-active', idx === step);
      s.classList.toggle('is-done', idx < step);
    });
    $('.dxp-prev').disabled = step === 1;
    $('.dxp-prev').setAttribute('aria-disabled', step === 1 ? 'true' : 'false');
    setControlVisible($('.dxp-next'), step !== MAX_STEP);
    setControlVisible($('.dxp-submit'), step === MAX_STEP);
    var fill = $('.dxp-progress-fill');
    if (fill) fill.style.width = Math.round(step / MAX_STEP * 100) + '%';
    var lbl = $('.dxp-progress-label');
    if (lbl) lbl.textContent = 'Step ' + step + ' of ' + MAX_STEP + ': ' + STEP_LABELS[step - 1];
    updatePlanPreview();
  }

  function validateStep(n) {
    var ok = true;
    var stepEl = $('.dxp-step[data-step="' + n + '"]');
    var radioNames = {};
    $all('input,select', stepEl).forEach(function (f) {
      var bad = false;
      if (f.type === 'radio') {
        if (radioNames[f.name]) return;
        radioNames[f.name] = true;
        bad = f.required && !stepEl.querySelector('input[name="' + f.name + '"]:checked');
        $all('input[name="' + f.name + '"]', stepEl).forEach(function (r) {
          var card = r.closest ? r.closest('.dxp-choice-card') : null;
          if (card) card.classList.toggle('dxp-invalid', !!bad);
          r.setAttribute('aria-invalid', bad ? 'true' : 'false');
        });
      } else {
        bad = (f.required && !f.value) ||
          (f.type === 'number' && f.value !== '' &&
           ((f.min !== '' && +f.value < +f.min) || (f.max !== '' && +f.value > +f.max)));
        f.classList.toggle('dxp-invalid', !!bad);
        f.setAttribute('aria-invalid', bad ? 'true' : 'false');
      }
      if (bad) ok = false;
    });
    if (n === 1 && !validateMeasures()) ok = false;
    return ok;
  }

  function formValue(form, fd, name) {
    var checked = form ? form.querySelector('input[name="' + name + '"]:checked') : null;
    return checked ? checked.value : fd.get(name);
  }
	  function getPlanSeed() {
    try {
      var seed = localStorage.getItem(LS_PLAN_SEED);
      if (!seed) {
        seed = 'p' + Date.now().toString(36) + Math.floor(Math.random() * 1000000).toString(36);
        localStorage.setItem(LS_PLAN_SEED, seed);
      }
      return seed;
    } catch (e) {
      return 'p' + Math.floor(Math.random() * 1000000).toString(36);
    }
  }

  function getInput() {
    var form = $('#dxpNutriForm');
    var fd = new FormData(form);
    var flags = [];
    $all('input[name="medical_flags"]:checked', form).forEach(function (c) { flags.push(c.value); });
    var imperial = currentUnits() === 'imperial';
    var height_cm = imperial
      ? Math.round(((+fd.get('height_ft') || 0) * 12 + (+fd.get('height_in') || 0)) * 2.54)
      : +fd.get('height_cm');
    var waist = +fd.get('waist_cm') || null, hip = +fd.get('hip_cm') || null;
    if (imperial) {                          /* entered in inches -> cm */
      if (waist) waist = Math.round(waist * 2.54);
      if (hip) hip = Math.round(hip * 2.54);
    }
    return {
      client_name: str(fd.get('client_name')).trim(),
      age: +fd.get('age'), sex: fd.get('sex'),
      height_cm: height_cm, weight_kg: +fd.get('weight_kg'),
      waist_cm: waist, hip_cm: hip,
      goal: formValue(form, fd, 'goal'), activity: formValue(form, fd, 'activity_level'),
      resistance_training: fd.get('resistance_training') === 'yes',
      experience: fd.get('experience') || 'beginner',
	      deficit_pct: fd.get('deficit_pct') ? +fd.get('deficit_pct') : null,
	      diet_type: formValue(form, fd, 'diet_type'), region: fd.get('region') || 'north',
	      meals_per_day: +fd.get('meals_per_day') || 4,
	      allergies_text: str(fd.get('allergies_text')).trim(),
	      dislikes_text: str(fd.get('dislikes_text')).trim(),
	      preferred_foods_text: str(fd.get('preferred_foods_text')).trim(),
	      budget_tier: fd.get('budget_tier') || 'flexible',
	      cooking_complexity: fd.get('cooking_complexity') || 'flexible',
	      weekly_variety_preference: fd.get('weekly_variety_preference') || 'balanced',
	      meal_timing_preference: fd.get('meal_timing_preference') || 'standard',
	      food_compatibility_mode: fd.get('food_compatibility_mode') || 'traditional',
	      plan_seed: getPlanSeed(),
	      on_waking_note: str(fd.get('on_waking_note')).trim(),
	      medical_flags: flags
	    };
	  }
	  function rotatePlanSeed() {
	    var seed = 'p' + Date.now().toString(36) + Math.floor(Math.random() * 1000000).toString(36);
	    try { localStorage.setItem(LS_PLAN_SEED, seed); } catch (e) {}
	    return seed;
	  }

  function persistForm() {
    try {
      var o = { savedAt: Date.now(), fields: {} };
	      $all('#dxpNutriForm input, #dxpNutriForm select, #dxpNutriForm textarea').forEach(function (f) {
	        if (f.type === 'checkbox') o.fields[f.name + ':' + f.value] = f.checked;
	        else if (f.type === 'radio') { if (f.checked) o.fields[f.name] = f.value; }
	        else o.fields[f.name] = f.value;
	      });
      localStorage.setItem(LS_FORM, JSON.stringify(o));
    } catch (e) {}
  }

  function restoreForm() {
    try {
      var o = JSON.parse(localStorage.getItem(LS_FORM) || 'null');
      if (!o || Date.now() - o.savedAt > 30 * 24 * 3600 * 1000) return;
	      $all('#dxpNutriForm input, #dxpNutriForm select, #dxpNutriForm textarea').forEach(function (f) {
	        if (f.type === 'checkbox') {
          var k = f.name + ':' + f.value;
          if (k in o.fields) f.checked = !!o.fields[k];
        } else if (f.type === 'radio') {
          if (f.name in o.fields) f.checked = o.fields[f.name] === f.value;
        } else if (f.name === 'client_name') {
          f.value = '';
        } else if (f.name in o.fields) {
          f.value = o.fields[f.name];
        }
      });
    } catch (e) {}
    syncConditionalFields();
    syncChoiceCards();
    updatePlanPreview();
  }

  /* height/waist unit toggle (cm <-> ft/in). Engine stays in cm; getInput()
     converts. Preference persists per browser. */
  function currentUnits() {
    try { return localStorage.getItem(LS_UNITS) === 'imperial' ? 'imperial' : 'metric'; }
    catch (e) { return 'metric'; }
  }
  function applyUnits(mode) {
    var form = $('#dxpNutriForm'); if (!form) return;
    var imperial = mode === 'imperial';
    $all('.dxp-unit-opt').forEach(function (b) {
      var on = b.getAttribute('data-unit') === mode;
      b.classList.toggle('is-on', on);
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
    var hm = $('.dxp-h-metric'), hi = $('.dxp-h-imperial');
    if (hm) hm.hidden = imperial;
    if (hi) hi.hidden = !imperial;
    var cm = $('input[name="height_cm"]', form), ft = $('input[name="height_ft"]', form);
    if (cm) cm.required = !imperial;
    if (ft) ft.required = imperial;
    $all('.dxp-unit-suffix', form).forEach(function (s) { s.textContent = imperial ? '(in)' : '(cm)'; });
    var help = $('[data-measure-help]', form);
    if (help) help.textContent = imperial
      ? 'Optional: enter in inches. Used for waist-to-height and waist-to-hip ratios.'
      : 'Optional: enter in centimetres. Used for waist-to-height and waist-to-hip ratios.';
    ['waist_cm', 'hip_cm'].forEach(function (nm) {
      var f = $('input[name="' + nm + '"]', form); if (!f) return;
      f.min = f.getAttribute(imperial ? 'data-imperial-min' : 'data-metric-min');
      f.max = f.getAttribute(imperial ? 'data-imperial-max' : 'data-metric-max');
    });
    validateMeasures();   /* re-check ranges in the new unit */
    try { localStorage.setItem(LS_UNITS, mode); } catch (e) {}
  }

  /* unit-aware waist/hip sanity check: catches e.g. "34" typed in cm mode */
  function validateMeasures() {
    var form = $('#dxpNutriForm'); if (!form) return true;
    var imperial = currentUnits() === 'imperial';
    var ranges = imperial
      ? { waist_cm: [20, 70, 'inches', 'Waist'], hip_cm: [25, 75, 'inches', 'Hip'] }
      : { waist_cm: [50, 180, 'centimetres', 'Waist'], hip_cm: [60, 180, 'centimetres', 'Hip'] };
    var ok = true;
    Object.keys(ranges).forEach(function (nm) {
      var f = $('input[name="' + nm + '"]', form), err = $('[data-err-for="' + nm + '"]', form);
      if (!f) return;
      var v = parseFloat(f.value);
      var bad = f.value !== '' && (isNaN(v) || v < ranges[nm][0] || v > ranges[nm][1]);
      f.classList.toggle('dxp-invalid', !!bad);
      f.setAttribute('aria-invalid', bad ? 'true' : 'false');
      if (err) {
        err.textContent = bad ? (ranges[nm][3] + ' value looks unusual for ' + ranges[nm][2] + '. Please check the selected unit.') : '';
        err.classList.toggle('is-shown', !!bad);
      }
      if (bad) ok = false;
    });
    return ok;
  }
  function setupUnits() {
    $all('.dxp-unit-opt').forEach(function (b) {
      b.addEventListener('click', function () { applyUnits(b.getAttribute('data-unit')); });
    });
    applyUnits(currentUnits());
  }

  function syncConditionalFields() {
    var form = $('#dxpNutriForm');
    if (!form) return;
    var fd = new FormData(form);
    var goal = formValue(form, fd, 'goal') || 'fat_loss';
    var deficit = $('.dxp-field-deficit');
    var exp = $('.dxp-field-experience');
    if (deficit) deficit.style.display = goal === 'fat_loss' ? '' : 'none';
    if (exp) exp.style.display = goal === 'muscle_gain' ? '' : 'none';
  }

  function syncChoiceCards() {
    $all('.dxp-choice-card').forEach(function (card) {
      var input = $('input', card);
      card.classList.toggle('is-selected', !!(input && input.checked));
    });
  }

  function setText(id, text) {
    var node = $('#' + id);
    if (node) node.textContent = cleanUiText(text);
  }

  function reviewSummaryCard(label, value, help) {
    var card = el('div', 'dxp-review-item');
    card.appendChild(el('span', null, label));
    card.appendChild(el('strong', null, value));
    if (help) card.appendChild(el('small', null, help));
    return card;
  }

  function updateReviewSummary(inp, targets) {
    var hold = $('#dxpReviewSummary');
    if (!hold) return;
    hold.innerHTML = '';
    hold.appendChild(reviewSummaryCard('Goal', GOAL_LABELS[inp.goal] || titleCase(inp.goal || 'fat loss')));
    hold.appendChild(reviewSummaryCard('Diet type', DIET_LABELS[inp.diet_type] || titleCase(inp.diet_type || 'veg')));
    hold.appendChild(reviewSummaryCard('Region', REGION_LABELS[inp.region] || titleCase(inp.region || 'north')));
	    hold.appendChild(reviewSummaryCard('Meals', (inp.meals_per_day || 4) + ' meals/day'));
	    var varietyLabels = { repeat_friendly: 'Simpler week with planned repeats', balanced: 'Balanced variety', maximum: 'More variety' };
	    hold.appendChild(reviewSummaryCard('Weekly cooking style', varietyLabels[inp.weekly_variety_preference] || varietyLabels.balanced));
	    hold.appendChild(reviewSummaryCard('Activity', ACTIVITY_LABELS[inp.activity] || titleCase(inp.activity || 'light')));
    hold.appendChild(reviewSummaryCard('Calories', targets ? targets.kcal + ' kcal/day' : 'Complete profile'));
    hold.appendChild(reviewSummaryCard('Protein', targets ? targets.protein_g + ' g/day' : 'Complete profile'));
    var flags = inp.medical_flags && inp.medical_flags.length ? inp.medical_flags.map(titleCase).join(', ') : 'None selected';
    hold.appendChild(reviewSummaryCard('Health notes', flags, 'Use professional review for any medical condition.'));
  }

  function updatePlanPreview() {
    var form = $('#dxpNutriForm');
    if (!form || !DATA) return;
    var inp = getInput();
    setText('dxpPreviewMeals', (inp.meals_per_day || 4) + ' meals/day');
    setText('dxpPreviewGoal', GOAL_LABELS[inp.goal] || titleCase(inp.goal || 'fat_loss'));
    setText('dxpPreviewDiet', DIET_LABELS[inp.diet_type] || titleCase(inp.diet_type || 'veg'));
    setText('dxpPreviewRegion', REGION_LABELS[inp.region] || titleCase(inp.region || 'north'));
    setText('dxpPreviewActivity', ACTIVITY_LABELS[inp.activity] || titleCase(inp.activity || 'light'));

    var ready = inp.age >= 18 && inp.age <= 80 && inp.sex && inp.height_cm && inp.weight_kg;
    if (!ready) {
      setText('dxpPreviewKcal', 'Complete profile');
      setText('dxpPreviewProtein', 'Complete profile');
      updateReviewSummary(inp, null);
      return;
    }
    try {
      var targets = Engine.calcTargets(inp, DATA);
      if (targets.blocks && targets.blocks.length) {
        setText('dxpPreviewKcal', 'Review needed');
        setText('dxpPreviewProtein', targets.protein_g + ' g/day');
      } else {
        setText('dxpPreviewKcal', targets.kcal + ' kcal/day');
        setText('dxpPreviewProtein', targets.protein_g + ' g/day');
      }
      updateReviewSummary(inp, targets);
    } catch (e) {
      setText('dxpPreviewKcal', 'Complete profile');
      setText('dxpPreviewProtein', 'Complete profile');
      updateReviewSummary(inp, null);
    }
  }

	  function feedbackKey(inp) {
	    return [inp.goal, inp.diet_type, inp.region, inp.meals_per_day].join('|');
	  }
	  function planMemoryKey(inp) {
	    var preferenceHash = wHashScore([
	      inp.budget_tier, inp.cooking_complexity, inp.weekly_variety_preference, inp.meal_timing_preference,
	      inp.preferred_foods_text, inp.dislikes_text, inp.allergies_text
	    ].join('|')).toString(36);
	    return [inp.goal, inp.diet_type, inp.region, inp.meals_per_day, preferenceHash].join('|');
	  }
	  function recentPlanMemory() {
	    try { return JSON.parse(localStorage.getItem(LS_RECENT_PLANS) || '{}') || {}; }
	    catch (e) { return {}; }
	  }
	  function recentPlanRecipeIds(inp) {
	    var row = recentPlanMemory()[planMemoryKey(inp)];
	    return row && Array.isArray(row.recipeIds) ? row.recipeIds : [];
	  }
	  function rememberWeeklyPlan(inp, weekly) {
	    if (!weekly || !weekly.days) return;
	    var seen = {}, recipeIds = [];
	    weekly.days.forEach(function (day) {
	      (day.slots || []).forEach(function (slot) {
	        (slot.items || []).forEach(function (it) {
	          if (!seen[it.rid]) { seen[it.rid] = 1; recipeIds.push(it.rid); }
	        });
	      });
	    });
	    try {
	      var memory = recentPlanMemory(), keys;
	      memory[planMemoryKey(inp)] = { savedAt: Date.now(), recipeIds: recipeIds };
	      keys = Object.keys(memory).sort(function (a, b) { return (memory[b].savedAt || 0) - (memory[a].savedAt || 0); });
	      keys.slice(12).forEach(function (key) { delete memory[key]; });
	      localStorage.setItem(LS_RECENT_PLANS, JSON.stringify(memory));
	    } catch (e) {}
	  }
  function feedbackRows() {
    try { return JSON.parse(localStorage.getItem(LS_FEEDBACK) || '[]'); } catch (e) { return []; }
  }
  function saveFeedbackRows(rows) {
    try { localStorage.setItem(LS_FEEDBACK, JSON.stringify(rows.slice(-60))); } catch (e) {}
  }
  function feedbackStats(inp) {
    var key = feedbackKey(inp);
    var rows = feedbackRows().filter(function (r) { return r.key === key; }).slice(-10);
    if (!rows.length) return { count: 0, avgFit: 0, avgHunger: 0, avgEnergy: 0 };
    function avg(field) {
      return rows.reduce(function (s, r) { return s + (+r[field] || 0); }, 0) / rows.length;
    }
    return {
      count: rows.length,
      avgFit: Math.round(avg('fit') * 10) / 10,
      avgHunger: Math.round(avg('hunger') * 10) / 10,
      avgEnergy: Math.round(avg('energy') * 10) / 10,
      avgDigestion: Math.round(avg('digestion') * 10) / 10
    };
  }
  function savePlanFeedback(inp, built, fields) {
    var rows = feedbackRows();
    rows.push({
      at: new Date().toISOString(),
      key: feedbackKey(inp),
      goal: inp.goal, diet_type: inp.diet_type, region: inp.region,
      templateId: built && built.templateId,
      fit: +fields.fit || 3,
      hunger: +fields.hunger || 3,
      energy: +fields.energy || 3,
      digestion: +fields.digestion || 3,
      comment: fields.comment || ''
    });
    saveFeedbackRows(rows);
  }

  /* ----------------------------- rendering ------------------------------ */
  function card(title, cls) {
    var c = el('div', 'dxp-card' + (cls ? ' ' + cls : ''));
    if (title) c.appendChild(el('h3', 'dxp-card-title', title));
    return c;
  }
	function reportDisclosure(label, content) {
	  var detail = el('details', 'dxp-report-disclosure');
	  detail.appendChild(el('summary', null, label));
	  detail.appendChild(content);
	  return detail;
	}
  function kpi(label, value, sub, tip) {
    var k = el('div', 'dxp-kpi');
    k.appendChild(el('div', 'dxp-kpi-value', value));
    var lab = el('div', 'dxp-kpi-label', label);
    if (tip) lab.appendChild(infoTip(tip));
    k.appendChild(lab);
    if (sub) k.appendChild(el('div', 'dxp-kpi-sub', sub));
    return k;
  }
  /* small accessible info tooltip (hover + keyboard focus reveal the text) */
  function infoTip(text) {
    var b = el('button', 'dxp-info', 'i');
    b.type = 'button';
    b.setAttribute('aria-label', cleanUiText(text));
    b.setAttribute('data-tip', cleanUiText(text));
    return b;
  }
  function cleanUiText(v) {
    return String(v == null ? '' : v).replace(/\s+—\s+/g, ': ').replace(/—/g, ':').replace(/–/g, '-');
  }
  function table(headers, rows, cls) {
    var wrap = el('div', 'dxp-table-wrap');
    var t = el('table', 'dxp-table' + (cls ? ' ' + cls : ''));
    var tr = el('tr');
    headers.forEach(function (h) { tr.appendChild(el('th', null, cleanUiText(h))); });
    t.appendChild(tr);
    rows.forEach(function (r) {
      var row = el('tr');
      r.forEach(function (cVal) { row.appendChild(el('td', null, cleanUiText(cVal))); });
      t.appendChild(row);
    });
    wrap.appendChild(t);
    return wrap;
  }
  function inputBlocksTerm(inp, terms) {
    var text = wLower([inp.allergies_text, inp.dislikes_text].join(' '));
    return terms.some(function (term) { return text.indexOf(term) >= 0; });
  }
  function proteinSwapItems(inp) {
    var base = [
      { label: 'dal', terms: ['dal'] },
      { label: 'soy chunks', terms: ['soy', 'soya'] },
      { label: 'sattu', terms: ['sattu'] },
      { label: 'tofu', terms: ['tofu', 'soy', 'soya'] },
      { label: 'curd', terms: ['curd', 'dairy', 'milk', 'lactose'] },
      { label: 'paneer', terms: ['paneer', 'dairy', 'milk', 'lactose'] }
    ];
    if (inp.diet_type !== 'veg') base.push({ label: 'egg', terms: ['egg'] });
    if (inp.diet_type === 'nonveg') {
      base.push({ label: 'lean chicken', terms: ['chicken'] });
      base.push({ label: 'fish', terms: ['fish'] });
    }
    var out = base.filter(function (item) {
      return !inputBlocksTerm(inp, item.terms);
    }).map(function (item) { return item.label; });
    return out.length ? out : ['dietitian-reviewed protein exchanges'];
  }
  function proteinSwapText(inp) {
    var items = proteinSwapItems(inp);
    if (items.length === 1) return items[0];
    return items.slice(0, -1).join(', ') + ' or ' + items[items.length - 1];
  }
  function gentleProteinSwapText(inp) {
    var items = proteinSwapItems(inp).filter(function (x) {
      return !/dal|sattu|soy chunks/i.test(x);
    });
    if (!items.length) items = proteinSwapItems(inp);
    if (items.length === 1) return items[0];
    return items.slice(0, -1).join(', ') + ' or ' + items[items.length - 1];
  }
	function proteinCorrectionText(inp, actual, target) {
	  var gap = Math.max(0, Math.round((target - actual) * 10) / 10);
	  var ideas = [];
	  if (inp.diet_type === 'nonveg' && !inputBlocksTerm(inp, ['chicken', 'fish'])) {
		ideas.push('75-100 g cooked lean chicken or fish');
	  }
	  if (inp.diet_type !== 'veg' && !inputBlocksTerm(inp, ['egg'])) ideas.push('2 eggs');
	  if (!inputBlocksTerm(inp, ['tofu', 'soy', 'soya'])) ideas.push('100-150 g tofu');
	  if (!inputBlocksTerm(inp, ['curd', 'dairy', 'milk', 'lactose'])) ideas.push('150 g hung curd or Greek yogurt');
	  if (!inputBlocksTerm(inp, ['paneer', 'dairy', 'milk', 'lactose'])) ideas.push('75-100 g low-fat paneer');
	  if (!ideas.length) ideas.push(proteinSwapText(inp));
	  var choice = ideas.length > 1 ? ideas[0] + ' or ' + ideas[1] : ideas[0];
	  return 'Protein is about ' + gap + ' g short of your daily target. Add ' + choice +
		' to a meal that already has a protein food, then reduce a cereal or oil portion if needed to keep calories on target.';
	}

  /* Convert a scaled serving + recipe measure into a human portion, e.g.
     (1.25, "2 medium chilla") -> "2-3 medium chillas"; (0.5, "200 ml milk")
     -> "100 ml milk"; (1.25, "100 g fruit") -> "125 g fruit". */
  function fmtFrac(x) {
    var q = Math.round(x * 4) / 4, w = Math.floor(q + 1e-6), f = q - w;
    var sym = f === 0.25 ? '¼' : f === 0.5 ? '½' : f === 0.75 ? '¾' : '';
    if (!sym) return String(w || (q < 1 ? q : 0));
    return (w ? w : '') + sym;
  }
  function pluralizeMeasure(rest, n) {
    if (n <= 1.0001) return rest;
    if (/\bidli\b/i.test(rest)) rest = rest.replace(/\bidli\b/i, 'idlis');
	if (/\bglass\b/i.test(rest)) return rest.replace(/\bglass\b/i, 'glasses');
    return rest.replace(/\b(bowl|katori|glass|cup|serving|plate|chapati|phulka|dosa|roti|egg|paratha|roll|chilla)\b(?!s)/i, '$1s');
  }
  function fmtSv(sv, measure) {
    measure = String(measure || '').trim();
    var m = measure.match(/^([\d.]+)\s*(.*)$/);
    if (!m) return measure || (Math.round(sv * 100) / 100) + ' serving';
    var base = parseFloat(m[1]) || 1, rest = m[2].trim(), total = base * sv;
    var gm = rest.match(/^(g\/ml|g|ml)\b\s*(.*)$/i);
    if (gm) {                                   /* weight / volume */
      var unit = gm[1].toLowerCase() === 'ml' ? 'ml' : 'g';
      var label = gm[2].trim();
      var val = total >= 100 ? Math.round(total / 5) * 5 : Math.round(total * 2) / 2;
      return val + ' ' + unit + (label ? ' ' + label : '');
    }
    if (/chilla|chapati|phulka|idli|dosa|roti|egg|paratha|roll|toast/i.test(rest)) {
      var lo = Math.floor(total + 1e-6), hi = Math.ceil(total - 1e-6);
      if (hi < 1) hi = 1;
      if (lo < 1) lo = total >= 0.5 ? 1 : 0;
      if (lo === hi || Math.abs(total - Math.round(total)) < 0.12) {
        var nWhole = Math.max(1, Math.round(total));
        return nWhole + ' ' + pluralizeMeasure(rest, nWhole);
      }
      return lo + '-' + hi + ' ' + pluralizeMeasure(rest, hi);
    }
    return fmtFrac(total) + ' ' + pluralizeMeasure(rest, total);   /* bowls/katori/etc */
  }

  function scaledAmountText(text, sv) {
    text = String(text || '').trim();
    var match = text.match(/^([\d.]+)\s*(g|ml)\b\s*(.*)$/i);
    if (!match) return text;
    var value = Math.round((parseFloat(match[1]) || 0) * sv / 5) * 5;
    return value + ' ' + match[2].toLowerCase() + (match[3] ? ' ' + match[3].trim() : '');
  }
  function cleanIngredientName(name) {
    return String(name || '').replace(/\s*\(.*?\)/g, '').replace(/\s+/g, ' ').trim().toLowerCase();
  }
  function ingredientAmount(parts, sv, label) {
    var grams = parts.reduce(function (sum, g) { return sum + (+g.g || 0); }, 0) * sv;
    grams = Math.round(grams / 5) * 5;
    return grams >= 5 ? grams + ' g ' + label : '';
  }
  function primaryIngredientHint(r, sv) {
    if (!r) return '';
    if (r.primary_raw_amount) return scaledAmountText(r.primary_raw_amount, sv);
    var ing = (r.ing || []).filter(function (g) {
      return g.grp !== 'oil' && !/water|salt|spice|masala|tadka|lemon/i.test(g.item || '');
    });
    if (!ing.length) return '';
    var name = String(r.name || '').toLowerCase();
    var pulses = ing.filter(function (g) { return g.grp === 'pulse'; });
    var cereals = ing.filter(function (g) { return g.grp === 'cereal'; });
    var proteins = ing.filter(function (g) { return /protein|soy/.test(g.grp || ''); });
    var dairy = ing.filter(function (g) { return /dairy/.test(g.grp || ''); });
    if (/curd rice/.test(name)) {
      var rice = ingredientAmount(cereals, sv, 'raw rice');
      var curd = ingredientAmount(dairy, sv, 'curd');
      return [rice, curd].filter(Boolean).join(' + ');
    }
    if (/soy|soya/.test(name) && /pulao/.test(name)) {
      var soy = ingredientAmount(pulses, sv, 'dry soy chunks');
      var pulaoRice = ingredientAmount(cereals, sv, 'raw rice');
      return [soy, pulaoRice].filter(Boolean).join(' + ');
    }
	if (/idli|dosa|uttapam|khichdi/.test(name) && cereals.length && pulses.length) {
	  var grainNames = cereals.map(function (g) { return cleanIngredientName(g.item); }).join(' + ');
	  var fermentedGrain = ingredientAmount(cereals, sv,
	    /rice/.test(grainNames) && !/ragi|millet/.test(grainNames) ? 'raw rice' : 'dry grain mix');
	  var fermentedDal = ingredientAmount(pulses, sv, 'raw dal');
	  return [fermentedGrain, fermentedDal].filter(Boolean).join(' + ');
	}
    if (pulses.length) {
      var pulseLabel = /soy|soya/.test(name) ? 'dry soy chunks'
	    : /sattu/.test(name) ? 'sattu'
        : /rajma/.test(name) ? 'dry rajma'
        : /chole|chana/.test(name) ? 'dry chana'
        : /sprout/.test(name) ? 'whole pulses for sprouting'
        : 'raw dal';
      return ingredientAmount(pulses, sv, pulseLabel);
    }
    if (cereals.length) {
      var cerealName = cleanIngredientName(cereals[0].item);
      var cerealLabel = /rice/.test(cerealName) ? 'raw rice'
        : /atta|flour/.test(cerealName) ? cerealName
        : /poha|rice flakes/.test(cerealName) ? 'dry poha'
        : /oat/.test(cerealName) ? 'dry oats'
        : cerealName + ' (dry)';
      return ingredientAmount(cereals, sv, cerealLabel);
    }
    if (proteins.length) return ingredientAmount(proteins, sv, cleanIngredientName(proteins[0].item));
    if (dairy.length && /paneer|tofu|yogurt|curd|milk/i.test(name)) {
      return ingredientAmount(dairy, sv, cleanIngredientName(dairy[0].item));
    }
    return '';
  }
  function recipeHouseholdPortion(r, sv) {
    var explicit = String(r.recipe_serving_label || r.household_measure || '').trim();
    if (explicit) return fmtSv(sv, explicit);
    var measure = String(r.measure || '').trim();
    if (/^[\d.]+\s*(g|ml)\b/i.test(measure)) {
	  var concrete = fmtSv(sv, measure);
	  return /^[\d.]+\s*(g|ml)\s*$/i.test(measure)
	    ? concrete + ' ' + recipeDisplayName(r.name || 'food').toLowerCase().replace(/\s+portion$/, '')
	    : concrete;
	}
    if (/chilla|chapati|phulka|idli|dosa|roti|egg|paratha|roll|toast/i.test(measure)) return fmtSv(sv, measure);
    var name = recipeDisplayName(r.name || 'recipe').toLowerCase();
    var bm = measure.match(/^([\d.]+)\s*(bowl|katori|plate|glass)(?:es|s)?\b/i);
    if (bm) return fmtSv(sv, bm[1] + ' ' + bm[2]) + ' ' + name;
    if (/serving/i.test(measure)) {
      var unit = /dal|curry|sabzi|raita|khichdi|daliya|upma|poha|rice/i.test(name) ? 'medium bowl' : 'serving';
      return fmtSv(sv, '1 ' + unit) + ' ' + name;
    }
    return (measure ? fmtSv(sv, measure) + ' ' : '') + name;
  }

  /* Recipe-level household portion first, then the raw/dry amount used by the
     nutrition calculation. Ingredient order never decides the serving label. */
  function naturalPortion(r, sv) {
    var portion = recipeHouseholdPortion(r, sv);
    var raw = primaryIngredientHint(r, sv);
    var yieldText = r && r.cooked_yield ? scaledAmountText(r.cooked_yield, sv) : '';
    var proteinText = r && r.key_protein_amount ? scaledAmountText(r.key_protein_amount, sv) : '';
    return [portion, raw, yieldText, proteinText].filter(function (x, i, all) {
      return x && all.indexOf(x) === i;
    }).join(' · ');
  }

  /* sum planned raw grams per DGI food group from recipe ingredients */
  var DGI_GRP_MAP = {
    cereal: 'Cereals incl. millets', pulse: 'Pulses', protein: 'Chicken/meat/eggs',
    dairy: 'Milk/curd', dairy_ml: 'Milk/curd', dairy_g: 'Milk/curd',
    veg: 'Vegetables + GLV', glv: 'Vegetables + GLV', fruit: 'Fruits',
    nuts: 'Nuts & seeds', oil: 'Fats & oils'
  };
  function plannedGroupGrams(built, data) {
    var out = {};
    if (!built) return out;
    built.plan.forEach(function (m) { m.items.forEach(function (it) {
      var r = recipeById(data, it.rid);
      (r.ing || []).forEach(function (g) {
        var grp = DGI_GRP_MAP[g.grp]; if (!grp) return;
        out[grp] = (out[grp] || 0) + g.g * it.sv;
      });
    }); });
    /* visible oil/ghee: use the adjusted final daily oil so it matches the plan */
    if (built.totals && built.totals.vfat != null) out['Fats & oils'] = built.totals.vfat;
    Object.keys(out).forEach(function (k) { out[k] = Math.round(out[k]); });
    return out;
  }
  function dgiStatus(grp, target, planned) {
    if (planned == null || !target) return '-';
    var ratio = planned / target;
    var dev = Math.abs(ratio - 1);
    if (dev <= 0.05) return 'Excellent';
    if (dev <= 0.10) return 'Acceptable';
    if (dev <= 0.15) return 'Review';
    if (ratio > 1.15) return /pulse/i.test(grp) ? 'High: protein-driven' : 'High';
    return 'Low';
  }
  function macroStatusLabel(row) {
    var label = String(row.label || '').toLowerCase(), gap = +row.gapPct || 0, abs = Math.abs(gap);
    if (/energy/.test(label)) {
      if (abs <= 5) return 'On target';
      if (abs <= 10) return 'Acceptable variation';
      return 'Outside planning range';
    }
    if (/protein/.test(label)) {
      if (gap >= -5 && gap <= 10) return 'On target';
      if ((gap < -5 && gap >= -10) || (gap > 10 && gap <= 15)) return 'Acceptable variation';
      return gap < -10 ? 'Needs adjustment' : 'Review';
    }
    if (/fat|carbohydrate/.test(label)) {
      if (abs <= 10) return 'On target';
      if (abs <= 15) return 'Acceptable variation';
      return 'Needs adjustment';
    }
    return row.pass ? 'On target' : 'Review';
  }
  function macroFitHeadline(rows) {
    var statuses = (rows || []).map(function (row) {
      return { row: row, status: macroStatusLabel(row) };
    });
    var major = statuses.filter(function (x) { return /Needs adjustment|Outside planning range/.test(x.status); });
    var moderate = statuses.filter(function (x) { return /Acceptable variation|Review/.test(x.status); });
    if (major.length) {
      return {
        label: 'Needs rebalancing',
        caption: major.map(function (x) { return x.row.label.replace(/\s*\(.*?\)/, ''); }).join(' and ') + (major.length === 1 ? ' needs' : ' need') + ' adjustment.'
      };
    }
    if (moderate.length) {
      return {
        label: 'Fair fit',
        caption: moderate.map(function (x) { return x.row.label.replace(/\s*\(.*?\)/, ''); }).join(' and ') + (moderate.length === 1 ? ' shows' : ' show') + ' acceptable variation.'
      };
    }
    return { label: 'Good fit', caption: 'Energy and macros are within planning tolerance.' };
  }
  function dailyPulseGrams(built, data) {
    var pulseDay = 0;
    if (!built) return 0;
    built.plan.forEach(function (m) { m.items.forEach(function (it) {
      var r = recipeById(data, it.rid);
      (r.ing || []).forEach(function (g) { if (g.grp === 'pulse') pulseDay += g.g * it.sv; });
    }); });
    return Math.round(pulseDay);
  }
  function shoppingRoundedPulseDay(built, data) {
    var byItem = {};
    if (!built) return 0;
    built.plan.forEach(function (m) { m.items.forEach(function (it) {
      var r = recipeById(data, it.rid);
      (r.ing || []).forEach(function (g) {
        if (g.grp === 'pulse') byItem[g.item || 'Pulses'] = (byItem[g.item || 'Pulses'] || 0) + g.g * it.sv * 7;
      });
    }); });
    var weekly = 0;
    Object.keys(byItem).forEach(function (item) { weekly += roundShopQty(byItem[item], 50); });
    return Math.round(weekly / 7);
  }
  function fibreBand(fibre, target) {
    if (!fibre) return '';
    if (fibre > Math.max(45, target * 1.8)) return 'very high';
	if (fibre > Math.max(40, target * 1.5)) return 'moderately high';
    if (fibre >= target) return 'meets minimum';
    return 'below target';
  }
  function recipeDisplayName(name) {
	var display = String(name || '')
      .replace(/,\s*controlled oil\b/i, '')
      .replace(/,\s*\d+(\.\d+)?\s*(medium|small|large)?\b.*$/i, '')
      .replace(/\s+/g, ' ')
      .trim();
	return /^plain cooked rice$/i.test(display) ? 'Plain rice' : display;
  }
  function goalPhrase(goal) {
    return goal === 'fat_loss' ? 'fat-loss' : goal === 'muscle_gain' ? 'muscle-gain' : 'maintenance';
  }
  function expectedPace(inp, targets) {
    if (inp.goal === 'fat_loss') {
      var dailyDeficit = Math.max(0, (targets.tee || 0) - (targets.kcal || 0));
      var weeklyDeficit = dailyDeficit * 7;
      var kg = weeklyDeficit / 7700;
      var low = Math.max(0.1, Math.round(kg * 0.75 * 10) / 10);
      var high = Math.max(low, Math.round(kg * 1.25 * 10) / 10);
      return 'Expected pace: estimated from your calculated deficit of about ' + Math.round(dailyDeficit) +
        ' kcal/day, roughly ' + low + '-' + high + ' kg/week. Scale change can vary with adherence, steps, sleep, menstrual cycle, sodium and water retention.';
    }
    if (inp.goal === 'muscle_gain') return 'Expected pace: slow lean gain; review training, strength progress and waist change every 2-3 weeks.';
    return 'Expected pace: weight maintenance; use weekly weight and waist trends to fine-tune portions.';
  }

  function renderFitSummary(inp, anthro, targets, built) {
    var c = card('Why this plan fits you');
    var ul = el('ul', 'dxp-logic-list');
    ul.appendChild(el('li', null, 'Calories are estimated from your sex, age band, body weight and activity, then adjusted to ' +
      targets.kcal + ' kcal/day for ' + titleCase(inp.goal) + '.'));
    ul.appendChild(el('li', null, 'Protein is set from body weight and goal: ' + targets.protein_g +
      ' g/day (' + targets.protein_gkg + ' g/kg), with carbohydrates and fat calculated around that target.'));
    ul.appendChild(el('li', null, 'The meal plan matches your ' + titleCase(inp.diet_type) + ', ' +
      titleCase(inp.region) + ' preference and ' + inp.meals_per_day + '-meal pattern.'));
    ul.appendChild(el('li', null, 'Visible oil is capped at ' + targets.visible_fat_cap_g +
      ' g/day and the final plan uses ' + built.totals.vfat + ' g/day after recipe oil adjustment.'));
    if (anthro.whtr) ul.appendChild(el('li', null, 'Waist-to-height ratio is included because waist was entered: ' + anthro.whtr + '.'));
    c.appendChild(ul);
    return c;
  }

  function renderBeforeStartCard(inp) {
    var c = card('Before you start', 'dxp-warning');
    c.insertBefore(el('p', 'dxp-kicker', 'Safety note'), c.firstChild);
    var ul = el('ul', 'dxp-logic-list dxp-logic-spaced');
    ul.appendChild(el('li', null, 'Use this as an educational diet draft, not a medical prescription.'));
    ul.appendChild(el('li', null, 'Measure oil, cereals and pulses for the first week, because household measures vary.'));
    ul.appendChild(el('li', null, 'Seek clinical advice if you feel dizziness, swelling, vomiting, repeated low sugar, chest pain, breathlessness or unusual weakness.'));
    if (inp.medical_flags && inp.medical_flags.length) {
      ul.appendChild(el('li', null, 'You selected a health condition. Treat this as a draft until a qualified professional reviews it.'));
    }
    c.appendChild(ul);
    return c;
  }

  function renderBloatingCard(inp, pulseDay, fibreDay) {
    var c = card('If you feel bloated');
    var ul = el('ul', 'dxp-logic-list');
    ul.appendChild(el('li', null, 'Reduce pulse portions by 20-25% for a few days, then build back gradually if comfortable.'));
    ul.appendChild(el('li', null, 'Soak dals/beans well, cook until soft, and split pulses across meals instead of taking one very large serving.'));
    ul.appendChild(el('li', null, 'Keep fluids steady and choose ' + gentleProteinSwapText(inp) + ' when large pulse servings feel too heavy.'));
    ul.appendChild(el('li', null, 'Lower-fibre version: reduce heavy beans and large dal portions, keep salad smaller, and use ' + gentleProteinSwapText(inp) + ' to protect protein intake. Use a smaller serving of well-cooked split dal only if tolerated.'));
    c.appendChild(el('p', 'dxp-muted', 'This plan currently gives about ' + fibreDay + ' g fibre/day and about ' + pulseDay + ' g raw pulses/day.'));
    c.appendChild(ul);
    return c;
  }

  function renderFeedbackCard(inp, built, smart) {
    var c = card('Help DietXP improve your next plan', 'dxp-feedback-card');
    var old = feedbackStats(inp);
    c.appendChild(el('p', 'dxp-muted', old.count
      ? 'This profile has ' + old.count + ' previous feedback record(s). Average fit ' + old.avgFit +
        '/5, hunger satisfaction ' + old.avgHunger + '/5, energy ' + old.avgEnergy + '/5.'
      : 'After trying the plan, save quick feedback. DietXP will use it to score and adjust future plans for the same goal, diet type, region and meal pattern.'));
    var grid = el('div', 'dxp-feedback-grid');
    [
      ['fit', 'Overall fit'],
      ['hunger', 'Hunger satisfaction'],
      ['energy', 'Energy level'],
      ['digestion', 'Digestion/comfort']
    ].forEach(function (pair) {
      var label = el('label', null, pair[1]);
      var sel = document.createElement('select');
      sel.name = pair[0];
      [5, 4, 3, 2, 1].forEach(function (v) {
        var o = document.createElement('option');
        o.value = String(v);
        o.textContent = v + (v === 5 ? ' - excellent' : v === 3 ? ' - okay' : v === 1 ? ' - poor' : '');
        if (v === 3) o.selected = true;
        sel.appendChild(o);
      });
      label.appendChild(sel);
      grid.appendChild(label);
    });
    var comment = el('label', 'dxp-feedback-comment', 'Notes for next plan');
    var txt = document.createElement('textarea');
    txt.name = 'comment';
    txt.rows = 3;
    txt.placeholder = 'e.g. breakfast too heavy, hungry at 5 pm, dal works well';
    comment.appendChild(txt);
    grid.appendChild(comment);
    c.appendChild(grid);
    var status = el('p', 'dxp-muted', '');
    var btn = el('button', 'dxp-btn dxp-btn-primary', 'Save feedback');
    btn.type = 'button';
    btn.addEventListener('click', function () {
      var fields = {};
      $all('select, textarea', c).forEach(function (f) { fields[f.name] = f.value; });
      savePlanFeedback(inp, built, fields);
      var next = feedbackStats(inp);
      status.textContent = 'Saved. Future score memory now has ' + next.count + ' record(s) for this profile.';
    });
    var actions = el('div', 'dxp-result-actions');
    actions.appendChild(btn);
    c.appendChild(actions);
    c.appendChild(status);
    return c;
  }

  function makeRelatedLink(title, body, href) {
    var a = el('a', 'dxp-link-card');
    a.href = href;
    a.appendChild(el('strong', null, title));
    a.appendChild(el('span', null, body));
    return a;
  }

  function renderRelatedLinksCard() {
    var c = card('Continue with DietXP', 'dxp-related-card');
    var grid = el('div', 'dxp-link-grid');
    grid.appendChild(makeRelatedLink('Health calculators', 'Check BMI, BMR, TDEE, renal risk and more DietXP tools.', 'https://www.dietxp.com/pages/calculators'));
    grid.appendChild(makeRelatedLink('DietXP products', 'Browse DietXP nutrition products and daily wellness options.', 'https://www.dietxp.com/collections/all'));
    grid.appendChild(makeRelatedLink('Food guides', 'Read Indian nutrition guides, food swaps, health topics and dietitian resources.', 'https://blog.dietxp.com/'));
    c.appendChild(grid);
    return c;
  }

  function reportTitle(inp) {
    return 'DietXP Diet Plan - ' + safeTitle(inp.client_name) +
      ' - Age ' + inp.age + ' - ' + titleCase(inp.sex || 'sex');
  }
  function printReport(inp) {
    var oldTitle = document.title;
    document.title = reportTitle(inp);
    window.setTimeout(function () { window.print(); }, 50);
    window.setTimeout(function () { document.title = oldTitle; }, 1500);
  }
  function printNamedReport(title) {
    var oldTitle = document.title;
    document.title = title;
    window.setTimeout(function () { window.print(); }, 50);
    window.setTimeout(function () { document.title = oldTitle; }, 1500);
  }

	  function renderPublicClinicalHandoff(handoff) {
	    var c = card(handoff.title, 'dxp-warning');
    c.appendChild(el('p', 'dxp-block-msg', handoff.blockPublicPlan
      ? 'A safe meal plan needs more clinical details before it can be created for this selection.'
      : 'The public plan can be used only as an educational draft. Review it with a qualified professional before following it.'));
    var ul = el('ul', 'dxp-logic-list');
    handoff.rows.forEach(function (msg) { ul.appendChild(el('li', null, msg)); });
    c.appendChild(ul);
    var actions = el('div', 'dxp-result-actions');
    var btn = el('button', 'dxp-btn dxp-btn-primary', 'Open clinical tool');
    btn.type = 'button';
    btn.addEventListener('click', function () {
      var tab = $('.dxp-tab[data-mode="clinical"]');
      if (tab) tab.click();
      if (handoff.preset) {
        Object.keys(handoff.preset).forEach(function (k) {
          var field = $('#dxpClinicalForm [name="' + k + '"]');
          if (field) field.value = handoff.preset[k];
        });
      }
      var panel = $('.dxp-mode[data-mode="clinical"]');
      if (panel) panel.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
    actions.appendChild(btn);
	    c.appendChild(actions);
	    return c;
	  }

	  function weeklyCellBySlot(day, slotId) {
	    for (var i = 0; i < day.slots.length; i++) {
	      if (day.slots[i].slotId === slotId) return day.slots[i];
	    }
	    return null;
	  }
	  function cleanMacro(v) {
	    var n = +v;
	    if (!isFinite(n)) return '0';
	    var rounded = Math.round(n * 10) / 10;
	    return Math.abs(rounded - Math.round(rounded)) < 0.01 ? String(Math.round(rounded)) : String(rounded);
	  }
	  function macroText(t) {
	    t = t || {};
	    return cleanMacro(t.e) + ' kcal | P ' + cleanMacro(t.p) + ' g | C ' + cleanMacro(t.cb) +
	      ' g | F ' + cleanMacro(t.f) + ' g | fibre ' + cleanMacro(t.fb) + ' g';
	  }
	  function weeklyItemsText(items, data) {
	    var names = [];
	    (items || []).forEach(function (it) {
	      var r = recipeById(data, it.rid);
	      if (r) names.push(recipeDisplayName(r.name));
	    });
	    return names.join(' + ');
	  }
	  function weeklyIngredientLines(cell, data) {
	    var lines = [];
	    (cell.items || []).forEach(function (it) {
	      var r = recipeById(data, it.rid);
	      if (!r) return;
	      var parts = (r.ing || []).slice(0, 5).map(function (g) {
	        var grams = Math.max(0, (+g.g || 0) - (g.grp === 'oil' ? (+it.oilCutG || 0) : 0));
	        var state = /cereal|pulse/.test(g.grp || '') ? ' raw/dry' : '';
	        return g.item + ' ' + Math.round(grams * it.sv) + ' g' + state;
	      });
	      lines.push(recipeDisplayName(r.name) + ': ' + (parts.length ? parts.join(', ') : naturalPortion(r, it.sv)));
	    });
	    return lines;
	  }
	  function renderWeeklyDetailContent(cell, day, slot, data, onSwap) {
	    var box = el('div', 'dxp-weekly-panel-content');
	    if (!cell || !cell.items || !cell.items.length) {
	      box.appendChild(el('h4', null, (day ? day.day + ' ' : '') + (slot ? slot.display_label : 'Meal')));
	      box.appendChild(el('p', 'dxp-muted', cell && cell.text ? cell.text : 'No recipe selected for this slot.'));
	      return box;
	    }
	    var title = (day ? day.day + ' ' : '') + (slot ? slot.display_label : 'Meal');
	    box.appendChild(el('h4', null, title));
    box.appendChild(el('p', 'dxp-weekly-panel-main', weeklyItemsText(cell.items, data)));

	    var grid = el('div', 'dxp-weekly-panel-grid');
	    var macros = el('div', 'dxp-weekly-panel-section');
	    macros.appendChild(el('strong', null, 'Portion and macros'));
	    var portionBits = [];
	    cell.items.forEach(function (it) {
	      var r = recipeById(data, it.rid);
	      if (r) portionBits.push(naturalPortion(r, it.sv));
	    });
	    macros.appendChild(el('p', null, portionBits.join(' + ')));
	    macros.appendChild(el('p', null, macroText(cell.totals || {})));
	    grid.appendChild(macros);

	    var fit = el('div', 'dxp-weekly-panel-section');
	    fit.appendChild(el('strong', null, 'Meal fit'));
	    fit.appendChild(el('p', null, 'Built to match this meal slot and your day targets.'));
	    if (/estimated/i.test(cell.confidence || '')) {
	      fit.appendChild(el('p', null, 'Some recipe values are estimated and will improve as recipes are verified.'));
	    }
	    grid.appendChild(fit);

	    var ing = el('div', 'dxp-weekly-panel-section');
	    ing.appendChild(el('strong', null, 'Ingredients'));
	    var ul = el('ul', 'dxp-weekly-ingredients');
	    weeklyIngredientLines(cell, data).forEach(function (line) { ul.appendChild(el('li', null, line)); });
	    ing.appendChild(ul);
	    grid.appendChild(ing);

	    var swaps = el('div', 'dxp-weekly-panel-section dxp-weekly-panel-swaps');
	    swaps.appendChild(el('strong', null, 'Swaps'));
	    if (cell.swapOptions && cell.swapOptions.length) {
	      var buttons = el('div', 'dxp-swap-buttons');
	      cell.swapOptions.forEach(function (opt) {
	        var label = opt.label.replace(/^Smart swap:\s*|^Swap:\s*/i, '');
	        var b = el('button', 'dxp-mini-swap', label);
	        b.type = 'button';
	        b.title = opt.caution || '';
	        b.addEventListener('click', function () { if (onSwap) onSwap(day.dayIndex, cell.slotId, opt); });
	        buttons.appendChild(b);
	      });
	      swaps.appendChild(buttons);
	    } else {
	      swaps.appendChild(el('p', null, 'No swap listed for this meal yet.'));
	    }
	    grid.appendChild(swaps);
	    box.appendChild(grid);
	    return box;
	  }
	  function renderWeeklyCell(cell, day, slot, data, onSelect, panelId) {
	    var td = el('td', 'dxp-weekly-cell');
	    if (!cell) return td;
	    if (!cell.items || !cell.items.length) {
	      td.appendChild(el('div', 'dxp-weekly-line', cell.text || 'Water'));
	      return td;
	    }
	    var mealText = weeklyItemsText(cell.items, data);
	    td.appendChild(el('div', 'dxp-weekly-line dxp-weekly-main', mealText || cell.pattern || slot.display_label));
	    var portionBits = [];
	    cell.items.forEach(function (it) {
	      var r = recipeById(data, it.rid);
	      if (r) portionBits.push(naturalPortion(r, it.sv));
	    });
	    if (portionBits.length) td.appendChild(el('div', 'dxp-weekly-portion', portionBits.join(' + ')));
	    if (/estimated/i.test(cell.confidence || '')) td.appendChild(el('span', 'dxp-weekly-badge', 'estimated'));
	    var detailBtn = el('button', 'dxp-cell-detail-btn');
	    detailBtn.type = 'button';
	    detailBtn.title = 'Details open below the weekly table';
	    detailBtn.setAttribute('aria-label', 'View details below the weekly table for ' + day.day + ' ' + slot.display_label);
	    detailBtn.setAttribute('aria-expanded', 'false');
	    if (panelId) detailBtn.setAttribute('aria-controls', panelId);
	    detailBtn.appendChild(el('span', null, 'Details'));
	    detailBtn.appendChild(el('span', 'dxp-detail-arrow', '↓'));
	    detailBtn.addEventListener('click', function () {
	      var table = td.closest && td.closest('.dxp-weekly-table');
	      if (table) {
	        $all('.dxp-weekly-cell.is-selected', table).forEach(function (node) { node.classList.remove('is-selected'); });
	        $all('.dxp-cell-detail-btn[aria-expanded="true"]', table).forEach(function (node) { node.setAttribute('aria-expanded', 'false'); });
	      }
	      td.classList.add('is-selected');
	      detailBtn.setAttribute('aria-expanded', 'true');
	      if (onSelect) onSelect(cell, day, slot, true);
	    });
	    td.appendChild(detailBtn);
	    return td;
	  }
	  function renderPlanCalculationDetails(weekly, built, data, smart) {
	    var detail = el('details', 'dxp-daily-fallback');
	    detail.appendChild(el('summary', null, weekly ? 'Advanced calculation details' : 'Detailed recipe calculations'));
	    if (weekly && weekly.days) {
	      weekly.days.forEach(function (d) {
	        d.slots.forEach(function (cell) {
	          if (!cell || !cell.items || !cell.items.length || cell.slotId === 'on_waking') return;
	          var mt = el('div', 'dxp-meal');
	          mt.appendChild(el('h4', null, d.day + ' · ' + (cell.label || cell.slotId)));
	          var rows = cell.items.map(function (it) {
	            var r = recipeById(data, it.rid);
	            if (!r) return ['', '', '', '', '', ''];
	            var oilCut = +it.oilCutG || 0;
	            return [
	              recipeDisplayName(r.name),
	              naturalPortion(r, it.sv),
	              Math.round((r.e - oilCut * 9) * it.sv) + ' kcal',
	              Math.round(r.p * it.sv * 10) / 10 + ' g',
	              Math.round(r.cb * it.sv * 10) / 10 + ' g',
	              Math.round(r.fb * it.sv * 10) / 10 + ' g'
	            ];
	          });
	          mt.appendChild(table(['Recipe', 'Portion', 'Energy', 'Protein', 'Carbs', 'Fibre'], rows));
	          mt.appendChild(el('p', 'dxp-muted', 'Meal total: ' + macroText(cell.totals || wCellTotals(cell.items, data))));
	          detail.appendChild(mt);
	        });
	      });
	      if (smart && smart.meals) {
	        detail.appendChild(table(['Meal', 'Score', 'Energy', 'Protein', 'Fibre', 'Dietitian note'], smart.meals.map(function (mScore) {
	          return [mScore.meal, mScore.score + '/100', mScore.energy + ' kcal', mScore.protein + ' g', mScore.fibre + ' g', mScore.note];
	        })));
	      }
	      return detail;
	    }
	    built.plan.forEach(function (m) {
	      var mt = el('div', 'dxp-meal');
	      mt.appendChild(el('h4', null, m.no + '. ' + m.name + ' · ~' + Math.round(m.split * 100) + '% of energy'));
	      var rows = m.items.map(function (it) {
	        var r = recipeById(data, it.rid);
	        var cut = (built.oilCut && built.oilCut[it.rid]) || 0;
	        var e = Math.round((r.e - cut * 9) * it.sv);
	        var p = Math.round(r.p * it.sv * 10) / 10;
	        return [recipeDisplayName(r.name), naturalPortion(r, it.sv), e + ' kcal', p + ' g protein'];
	      });
	      mt.appendChild(table(['Recipe', 'Portion', 'Energy', 'Protein'], rows));
	      detail.appendChild(mt);
	    });
	    if (smart && smart.meals) {
	      detail.appendChild(table(['Meal', 'Score', 'Energy', 'Protein', 'Fibre', 'Dietitian note'], smart.meals.map(function (mScore) {
	        return [mScore.meal, mScore.score + '/100', mScore.energy + ' kcal', mScore.protein + ' g', mScore.fibre + ' g', mScore.note];
	      })));
	    }
	    return detail;
	  }
	  function weeklyQualityMetric(label, value, suffix) {
	    var item = el('div', 'dxp-quality-metric');
	    item.appendChild(el('strong', null, value + (suffix || '')));
	    item.appendChild(el('span', null, label));
	    return item;
	  }
	  function weeklyVarietyLabel(inp) {
	    var preference = wLower(inp && inp.weekly_variety_preference || 'balanced');
	    if (preference === 'repeat_friendly') return 'simpler week with planned repeats';
	    if (preference === 'maximum') return 'more variety';
	    return 'balanced variety';
	  }
	  function renderWeeklyPlanCard(weekly, inp, targets, built, data, smart, onSwap, onRegenerate, onImprove) {
	    var c = card('Your 7-day Indian diet plan');
	    if (!weekly) {
	      c.appendChild(el('p', 'dxp-muted', 'A detailed one-day plan is shown because the weekly planner data is not available.'));
	      c.appendChild(renderPlanCalculationDetails(null, built, data, smart));
	      return c;
	    }
	    c.appendChild(el('p', 'dxp-muted dxp-weekly-average', 'Weekly average: ' + cleanMacro(weekly.avgTotals.e) +
	      ' kcal/day · protein ' + cleanMacro(weekly.avgTotals.p) + ' g · carbs ' + cleanMacro(weekly.avgTotals.cb) +
	      ' g · fat ' + cleanMacro(weekly.avgTotals.f) + ' g' +
	      (weekly.estimatedCount ? ' · some recipe values are estimates' : '')));
	    if (weekly.lastSwapNotice) c.appendChild(el('p', 'dxp-swap-notice', weekly.lastSwapNotice));
	    if (weekly.optimization) {
	      var quality = el('div', 'dxp-plan-quality-grid');
	      quality.setAttribute('aria-label', 'Plan quality scores');
	      quality.appendChild(weeklyQualityMetric('Overall', weekly.optimization.total, '/100'));
	      quality.appendChild(weeklyQualityMetric('Protein fit', weekly.optimization.protein));
	      quality.appendChild(weeklyQualityMetric('Digestive comfort', weekly.optimization.digestiveComfort));
	      quality.appendChild(weeklyQualityMetric('Meal balance', weekly.optimization.distribution));
	      quality.appendChild(weeklyQualityMetric('Easy to follow', weekly.optimization.practicality));
	      c.appendChild(quality);
	      var highlights = el('ul', 'dxp-weekly-highlights');
	      highlights.appendChild(el('li', null, cleanMacro(weekly.avgTotals.p) + ' g protein/day against your ' + targets.protein_g + ' g target'));
	      highlights.appendChild(el('li', null, weekly.optimization.proteinFamilies + ' protein families rotated through the week'));
	      highlights.appendChild(el('li', null, weekly.optimization.preparedRecipes + ' cooked dishes, with staples and simple add-ons arranged for ' + weeklyVarietyLabel(inp)));
	      highlights.appendChild(el('li', null, 'Average pulse ' + weekly.optimization.pulseGrams + ' g and dairy ' + weekly.optimization.dairyGrams + ' g/day'));
	      c.appendChild(highlights);
	      if (!weekly.optimization.constraintsMet) {
	        c.appendChild(el('p', 'dxp-plan-caution', 'This is the closest practical match found. Check the highlighted scores and use Improve this plan before following it.'));
	      }
	    }
	    if (onRegenerate || onImprove) {
	      var weekActions = el('div', 'dxp-weekly-actions');
	      weekActions.appendChild(el('p', 'dxp-quick-note', 'Improve the balance or create another week with different meal ideas.'));
	  var actionButtons = el('div', 'dxp-weekly-action-buttons');
	  if (onImprove) {
		var improve = el('button', 'dxp-btn dxp-btn-primary dxp-improve-week', 'Improve this plan');
		improve.type = 'button';
		improve.addEventListener('click', onImprove);
		actionButtons.appendChild(improve);
	  }
	  if (onRegenerate) {
		var regenerate = el('button', 'dxp-btn dxp-btn-ghost dxp-regenerate-week', 'Create a different week');
		regenerate.type = 'button';
		regenerate.addEventListener('click', onRegenerate);
		actionButtons.appendChild(regenerate);
	  }
	      weekActions.appendChild(actionButtons);
	      c.appendChild(weekActions);
	    }
	    var wrap = el('div', 'dxp-weekly-wrap');
	    wrap.setAttribute('tabindex', '0');
	    wrap.setAttribute('aria-label', 'Seven-day meal plan. Choose a day above on smaller screens.');
	    var panelId = 'dxpWeeklyDetailsPanel-' + Math.random().toString(36).slice(2);
	    var panelHint = el('p', 'dxp-weekly-panel-hint', 'Tap Details in any meal. The selected meal is highlighted and its portions, ingredients and swaps appear below the table.');
	    var panel = el('div', 'dxp-weekly-panel');
	    panel.id = panelId;
	    panel.setAttribute('tabindex', '-1');
	    var firstSelection = null;
	    function showWeeklyPanel(cell, day, slot, jumpToPanel) {
	      panel.innerHTML = '';
	      panel.appendChild(renderWeeklyDetailContent(cell, day, slot, data, onSwap));
	      if (jumpToPanel) {
	        panel.classList.remove('is-updated');
	        void panel.offsetWidth;
	        panel.classList.add('is-updated');
	        window.setTimeout(function () { panel.classList.remove('is-updated'); }, 1200);
	        var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
	        panel.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
	        if (panel.focus) window.setTimeout(function () { panel.focus({ preventScroll: true }); }, reduceMotion ? 0 : 250);
	      }
	    }
	    var t = el('table', 'dxp-weekly-table');
	    t.classList.add('dxp-mobile-day-0');
	    var head = el('tr');
	    head.appendChild(el('th', null, 'Time'));
	    weekly.days.forEach(function (d) { head.appendChild(el('th', null, d.day)); });
	    t.appendChild(head);
	    weekly.slots.forEach(function (slot) {
	      var tr = el('tr');
	      tr.appendChild(el('th', 'dxp-weekly-time', slot.display_label));
	      weekly.days.forEach(function (d) {
	        var cell = weeklyCellBySlot(d, slot.slot_id);
	        if (!firstSelection && cell && cell.items && cell.items.length) firstSelection = { cell: cell, day: d, slot: slot };
	        tr.appendChild(renderWeeklyCell(cell, d, slot, data, showWeeklyPanel, panelId));
	      });
	      t.appendChild(tr);
	    });
	    wrap.appendChild(t);
	    var swipeHint = el('p', 'dxp-weekly-swipe-hint', 'Choose a day to view its meals.');
	    var dayNav = el('div', 'dxp-weekly-day-nav');
	    dayNav.setAttribute('aria-label', 'Choose a day in the weekly plan');
	    weekly.days.forEach(function (day, dayIndex) {
	      var dayBtn = el('button', 'dxp-weekly-day-button' + (dayIndex === 0 ? ' is-active' : ''), day.day.slice(0, 3));
	      dayBtn.type = 'button';
	      dayBtn.setAttribute('aria-pressed', dayIndex === 0 ? 'true' : 'false');
	      dayBtn.addEventListener('click', function () {
	        $all('.dxp-weekly-day-button', dayNav).forEach(function (node) {
	          var active = node === dayBtn;
	          node.classList.toggle('is-active', active);
	          node.setAttribute('aria-pressed', active ? 'true' : 'false');
	        });
	        for (var mobileDayIndex = 0; mobileDayIndex < 7; mobileDayIndex += 1) {
	          t.classList.remove('dxp-mobile-day-' + mobileDayIndex);
	        }
	        t.classList.add('dxp-mobile-day-' + dayIndex);
	        if (window.matchMedia && window.matchMedia('(max-width: 420px)').matches) {
	          wrap.scrollLeft = 0;
	          window.requestAnimationFrame(function () { wrap.scrollLeft = 0; });
	          return;
	        }
	        var headerCell = t.rows[0] && t.rows[0].cells[dayIndex + 1];
	        if (headerCell) {
	          var stickyWidth = t.rows[0].cells[0] ? t.rows[0].cells[0].offsetWidth : 92;
	          wrap.scrollTo({ left: Math.max(0, headerCell.offsetLeft - stickyWidth), behavior: 'smooth' });
	        }
	      });
	      dayNav.appendChild(dayBtn);
	    });
	    c.appendChild(swipeHint);
	    c.appendChild(dayNav);
	    c.appendChild(wrap);
	    if (firstSelection) {
	      showWeeklyPanel(firstSelection.cell, firstSelection.day, firstSelection.slot);
	      c.appendChild(panelHint);
	      c.appendChild(panel);
	    }
	    c.appendChild(el('p', 'dxp-plan-total', 'Full-week average: ' + macroText(weekly.avgTotals) +
	      ' | visible oil ' + cleanMacro(weekly.avgTotals.vfat) + ' g/day'));
	    c.appendChild(el('p', 'dxp-muted', 'Meals are rotated for variety and matched to your goal, diet type, region, budget, cooking preference and foods to avoid. Tap Details to see portions, ingredients and swap options.'));
	    c.appendChild(el('p', 'dxp-muted', 'Cooking-oil guide: keep visible oil/ghee to about ' +
	      weekly.avgTotals.vfat + ' g/day (~' + (Math.round((weekly.avgTotals.vfat / 5) * 10) / 10) +
	      ' tsp/day) across the week. The shopping list is based on the meals selected in this table.'));
	    c.appendChild(renderPlanCalculationDetails(weekly, built, data, smart));
	    return c;
	  }

	  /* report-level quick-adjustment presets, re-applied on every (re)generation */
	  var reportOpts = { high_protein: false, lower_fibre: false, improve_plan: false };
	  function runReport() {
	    persistForm();
	    var inp = getInput();
	    inp.previous_plan_recipe_ids = recentPlanRecipeIds(inp);
    inp.high_protein = inp.high_protein || reportOpts.high_protein;
    inp.lower_fibre = reportOpts.lower_fibre;
	inp.improve_plan = reportOpts.improve_plan;
    if (inp.high_protein) {
      inp.preferred_foods_text = [inp.preferred_foods_text, proteinSwapItems(inp).join(', ')]
        .filter(Boolean).join(', ');
    }
    var anthro = Engine.calcAnthro(inp);
    var targets = Engine.calcTargets(inp, DATA);
    var built = (targets.blocks.length || inp.age < 18 || inp.age > 80)
      ? null : Engine.buildPlan(inp, targets, DATA);
    renderResults(inp, anthro, targets, built, DATA);
  }

	  function renderResults(inp, anthro, targets, built, data, weeklyOverride) {
	    var res = $('#dxpResults');
	    res.innerHTML = '';
	    res.hidden = false;
	    root.classList.add('dxp-has-report');
	    var handoff = Engine.publicClinicalHandoff(inp);
	    var publicPlanBlocked = handoff && handoff.blockPublicPlan;
	    var weekly = (!publicPlanBlocked && built)
	      ? (weeklyOverride || Engine.buildWeeklyPlan(inp, targets, data, built))
	      : null;
	    if (weekly) rememberWeeklyPlan(inp, weekly);
	    var builtMetrics = (!publicPlanBlocked && built)
	      ? (weekly ? mergeObject(built, { totals: weekly.avgTotals, weekly: weekly }) : built)
	      : null;

    /* blocks stop everything */
    if (targets.blocks.length || inp.age < 18 || inp.age > 80) {
      var blockCard = card('Plan not generated', 'dxp-warning');
      var codes = targets.blocks.slice();
      if (inp.age < 18 || inp.age > 80) codes.unshift('ERR_AGE_LOW');
      codes.forEach(function (code) {
        blockCard.appendChild(el('p', 'dxp-block-msg', data.messages.blocks[code] || code));
      });
      res.appendChild(blockCard);
      res.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }

	    var feedback = null, smart = null, rationale = null, pulseDay = null, tot = null;
	    if (!publicPlanBlocked && built && builtMetrics) {
	      feedback = feedbackStats(inp);
	      smart = Engine.smartPlanScore(inp, anthro, targets, builtMetrics, data, feedback);
	      rationale = Engine.planRationale(inp, anthro, targets, builtMetrics, data, smart);
	      pulseDay = weekly ? Engine.weeklyPulseGrams(weekly, data) : dailyPulseGrams(built, data);
	      tot = builtMetrics.totals;
	      res.appendChild(renderWeeklyPlanCard(weekly, inp, targets, built, data, smart, function (dayIndex, slotId, opt) {
	        var swapped = Engine.applyWeeklySwap(weekly, dayIndex, slotId, opt, data, inp, targets);
	        renderResults(inp, anthro, targets, built, data, swapped);
	      }, function () {
	        rotatePlanSeed();
	        runReportWhenDataReady();
	  }, function () {
		reportOpts.improve_plan = true;
		rotatePlanSeed();
		runReportWhenDataReady();
	      }));
	    }

    /* 1 — weekly plan first, then profile + targets */
    var profile = card('Report profile');
    var profileGrid = el('div', 'dxp-result-grid');
    profileGrid.appendChild(kpi('Name', inp.client_name || 'Not entered'));
    profileGrid.appendChild(kpi('Age / Sex', inp.age + ' years · ' + titleCase(inp.sex)));
    profileGrid.appendChild(kpi('Height / Weight', inp.height_cm + ' cm · ' + inp.weight_kg + ' kg'));
    profileGrid.appendChild(kpi('Goal / Activity', titleCase(inp.goal) + ' · ' + titleCase(inp.activity)));
    profileGrid.appendChild(kpi('Diet / Region', titleCase(inp.diet_type) + ' · ' + titleCase(inp.region)));
    profileGrid.appendChild(kpi('Report date', new Date().toLocaleDateString()));
    profile.appendChild(profileGrid);
    var profileNotes = el('ul', 'dxp-logic-list dxp-profile-notes');
    profileNotes.appendChild(el('li', null, 'Plan type: Educational ' + goalPhrase(inp.goal) + ' meal plan.'));
    profileNotes.appendChild(el('li', null, 'Best for: healthy adults looking for ' +
      (inp.goal === 'fat_loss' ? 'gradual fat loss' : inp.goal === 'muscle_gain' ? 'structured muscle gain' : 'weight maintenance') +
      ' using familiar ' + titleCase(inp.region) + ' ' + titleCase(inp.diet_type) + ' meals.'));
    profileNotes.appendChild(el('li', null, 'Not suitable without review: pregnancy, lactation, kidney disease, diabetes on medication, active illness, eating disorders, cancer, liver disease, heart failure, or any condition needing medical nutrition therapy.'));
    profileNotes.appendChild(el('li', null, expectedPace(inp, targets)));
    profile.appendChild(profileNotes);
    /* quick-adjustment presets — re-generate the report with the preset applied */
    var adjust = el('div', 'dxp-quick-adjust');
    adjust.appendChild(el('span', 'dxp-quick-label', 'Quick versions:'));
    function presetChip(label, key) {
      var b = el('button', 'dxp-chip' + (reportOpts[key] ? ' is-on' : ''), label);
      b.type = 'button';
      b.setAttribute('aria-pressed', reportOpts[key] ? 'true' : 'false');
      b.addEventListener('click', function () { reportOpts[key] = !reportOpts[key]; runReportWhenDataReady(); });
      return b;
    }
    adjust.appendChild(presetChip('Higher protein', 'high_protein'));
    adjust.appendChild(presetChip('Lower fibre (comfort)', 'lower_fibre'));
    profile.appendChild(adjust);
    profile.appendChild(el('p', 'dxp-quick-note', 'Higher protein prioritises leaner protein exchanges. Lower fibre reduces heavy pulse and salad portions and protects protein with suitable swaps. Tap a chip to regenerate the report.'));

    var profileActions = el('div', 'dxp-result-actions dxp-result-actions-top');
    var topPrint = el('button', 'dxp-btn dxp-btn-primary', 'Download PDF report');
    topPrint.type = 'button';
    topPrint.addEventListener('click', function () { printReport(inp); });
    profileActions.appendChild(topPrint);
    var editBtn = el('button', 'dxp-btn dxp-btn-ghost', 'Edit inputs & regenerate');
    editBtn.type = 'button';
    editBtn.addEventListener('click', function () {
	  root.classList.remove('dxp-has-report');
      showStep(1);
      var form = $('#dxpNutriForm');
      if (form) form.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
    profileActions.appendChild(editBtn);
    profile.appendChild(profileActions);
    res.appendChild(profile);

    var grid = el('div', 'dxp-result-grid');
    var bmiCat = anthro.bmiCategory.replace(/risk$/i, 'risk range');   /* softer framing */
    var bmiSub = bmiCat + ' · Asian-Indian cut-offs';
    bmiSub += anthro.whtr ? ' · waist-to-height ' + anthro.whtr : ' · add waist for waist-to-height';
    grid.appendChild(kpi('BMI', anthro.bmi, bmiSub));
    grid.appendChild(kpi('Maintenance (TEE)', targets.tee + ' kcal', 'BMR ' + targets.bmr + ' × PAL ' + targets.pal,
      'BMR (Basal Metabolic Rate) is the calories your body uses at rest, from an Indian-adjusted equation. PAL (Physical Activity Level) is your activity multiplier. TEE = BMR × PAL = your maintenance calories.'));
    var goalSub = inp.goal === 'fat_loss' ? (targets.deficit_pct + '% deficit')
      : inp.goal === 'muscle_gain' ? ('+' + targets.surplus + ' kcal surplus') : 'maintenance';
    grid.appendChild(kpi('Daily calorie target', targets.kcal + ' kcal', goalSub));
    grid.appendChild(kpi('Protein', targets.protein_g + ' g', targets.protein_gkg + ' g/kg'));
    grid.appendChild(kpi('Fat', targets.fat_g + ' g', targets.fat_pct + '% energy'));
	    grid.appendChild(kpi('Carbohydrate', targets.carb_g + ' g', builtMetrics
	      ? 'fibre target at least ' + targets.fibre_g + ' g; planned ' + builtMetrics.totals.fb + ' g (' + fibreBand(builtMetrics.totals.fb, targets.fibre_g) + ')'
	      : 'fibre target at least ' + targets.fibre_g + ' g',
      'Fibre is the plant roughage from pulses, vegetables, fruit and whole grains. It aids fullness and digestion; increase it gradually with enough water to avoid gas or bloating.'));
    var c1 = card('Your daily targets');
    c1.appendChild(grid);
    var caps = el('p', 'dxp-caps');
    caps.appendChild(document.createTextNode('Visible oil cap ' + targets.visible_fat_cap_g + ' g/day'));
    caps.appendChild(infoTip('Visible oil/ghee added while cooking (1 tsp ≈ 5 g). The plan trims this further to the final daily amount shown in the meal plan.'));
    caps.appendChild(document.createTextNode(' · Added sugar cap ' + targets.sugar_cap_g + ' g/day · Salt cap ' + targets.salt_cap_g + ' g/day'));
    caps.appendChild(infoTip('Total salt from cooking, table salt and hidden sodium in packaged foods. 5 g salt ≈ 2000 mg sodium.'));
    caps.appendChild(document.createTextNode(' · Water target about ' + targets.water_l + ' L/day; adjust for heat, sweating, exercise and medical conditions'));
    c1.appendChild(caps);
    res.appendChild(c1);

    /* high-fibre heads-up surfaced right under the targets (feedback) */
	    if (builtMetrics && builtMetrics.totals.fb > 45) {
	      var fbCard = card('High-fibre plan: build up gradually', 'dxp-warning');
	      fbCard.appendChild(el('p', null, 'This is a high-fibre ' + titleCase(inp.diet_type) +
	        ' plan (about ' + builtMetrics.totals.fb + ' g fibre/day, mainly from pulses, vegetables and whole grains). ' +
        'Increase fibre gradually and drink enough water. If you feel gas or bloating, reduce heavy beans and large dal portions by ' +
        '20-25% and use ' + gentleProteinSwapText(inp) + ' as suitable for your diet type.'));
      res.appendChild(fbCard);
    }

    /* one strong clinical-safety card right after targets when a flag is set
       (feedback: lead with safety, then keep later notes shorter) */
    if (handoff) {
      res.appendChild(renderPublicClinicalHandoff(handoff));
    }

	    /* 2 — food group targets vs planned (feedback: show transparency) */
	    var fg = Engine.foodGroupTargets(targets.kcal, inp.diet_type, data);
	    var planned = weekly ? Engine.weeklyPlanGroupGrams(weekly, data) : plannedGroupGrams(builtMetrics, data);
    var c2 = card('Food-group balance');
    c2.querySelector('.dxp-card-title').appendChild(infoTip('This compares your plan with Indian food-group guidance scaled to your calorie target.'));
    c2.appendChild(table(['Food group', 'Daily target', 'Planned', 'Status'], fg.map(function (g) {
      var extra = g.whole_grain_min ? ' (≥' + g.whole_grain_min + ' g whole grain, millets ' + g.millet_min + '-' + g.millet_max + ' g)' : '';
      var unit = g.unit.replace('/day', '');
      var pl = planned[g.grp];
      var label = g.grp === 'Milk/curd' ? 'Milk, curd and dairy equivalents' : g.grp;
      var plannedDisplay, status;
      if (pl != null) { plannedDisplay = pl + ' ' + unit; status = dgiStatus(g.grp, g.qty, pl); }
      else if (/nuts/i.test(g.grp)) { plannedDisplay = '0 ' + unit; status = 'Optional add-on'; }
      else { plannedDisplay = '-'; status = '-'; }
      return [label, g.qty + ' ' + unit + extra, plannedDisplay, status];
    })));
	    if (builtMetrics) {
	      var dietWord = inp.diet_type === 'veg' ? 'vegetarian' : inp.diet_type === 'egg' ? 'eggetarian' : 'non-vegetarian';
      c2.appendChild(el('p', 'dxp-muted', 'This ' + goalPhrase(inp.goal) + ' ' + dietWord +
        ' plan keeps cereals moderate and uses pulses, dairy, vegetables and protein-rich foods to support fullness and protein intake. Targets are adjusted for your goal, diet type and meal pattern.'));
      c2.appendChild(el('p', 'dxp-muted', 'Dairy values include curd and paneer as practical dairy-equivalent estimates. Optional: add 10-15 g nuts or seeds if calories allow, especially if hunger is high or fat quality needs improving.'));
    }
	res.appendChild(reportDisclosure('View detailed food-group balance', c2));

    if (handoff && handoff.blockPublicPlan) {
      var hold = card('Public meal plan not generated', 'dxp-warning');
      hold.appendChild(el('p', null, 'Use the clinical tool with recent labs and medical details. The public plan cannot safely adjust potassium, phosphorus, protein, fluid or medicine-related needs.'));
      res.appendChild(hold);
    } else if (!built) {
      var nc = card('Meal plan unavailable', 'dxp-warning');
      nc.appendChild(el('p', null, 'No ready meal plan matches this combination yet. Targets above are still valid.'));
      res.appendChild(nc);
	    } else {
	      res.appendChild(renderFitSummary(inp, anthro, targets, builtMetrics));

	      /* 3 — accuracy (feedback: qualitative headline, not a bare 100/100) */
	      var acc = Engine.accuracy(builtMetrics, targets, data);
	      var macroFit = macroFitHeadline(acc.rows);
	      var c3 = card('Macro fit');
	      var badge = el('div', 'dxp-score');
	      badge.appendChild(el('span', 'dxp-score-num dxp-score-word', macroFit.label));
	      badge.appendChild(el('span', 'dxp-score-cap', macroFit.caption));
	      c3.appendChild(badge);
	      c3.appendChild(el('p', 'dxp-muted', 'This summary follows the least-aligned meaningful macro, so an energy match cannot hide a protein or fat problem. Sodium, safety notes and food-group quality are checked separately.'));
      c3.appendChild(table(['Metric', 'Target', 'Planned', 'Gap %', 'Status'], acc.rows.map(function (r2) {
        return [r2.label, r2.target, r2.actual, (r2.gapPct > 0 ? '+' : '') + r2.gapPct + '%', macroStatusLabel(r2)];
      })));
      res.appendChild(c3);

      var cSmart = card('Plan quality score');
      var smartBadge = el('div', 'dxp-score');
      smartBadge.appendChild(el('span', 'dxp-score-num', smart.total + '/100'));
      smartBadge.appendChild(el('span', 'dxp-score-cap', 'Nutrition fit, comfort checks and food-group quality'));
      cSmart.appendChild(smartBadge);
      cSmart.appendChild(table(['Area', 'Score', 'What it checks'], smart.components));
      var nextList = el('ul', 'dxp-logic-list');
      smart.nextActions.forEach(function (a) { nextList.appendChild(el('li', null, a)); });
      cSmart.appendChild(nextList);
	      res.appendChild(reportDisclosure('View plan quality details', cSmart));

      var cLogic = card('How your plan was calculated');
      rationale.forEach(function (r3) {
        var block = el('div', 'dxp-logic-block');
        block.appendChild(el('h4', null, r3[0]));
        block.appendChild(el('p', null, r3[1]));
        cLogic.appendChild(block);
      });
	      res.appendChild(reportDisclosure('View calculation method', cLogic));

      res.appendChild(renderBeforeStartCard(inp));

      /* 5 — warnings (real review notes) + informational plan notes */
	      var warns = Engine.warningsFor(inp, targets, builtMetrics, anthro, data);
      var realWarns = warns.filter(function (w) { return w.severity !== 'info'; });
      var infoNotes = warns.filter(function (w) { return w.severity === 'info'; });
      if (realWarns.length) {
        var c5 = card('Review notes', 'dxp-warning');
        realWarns.forEach(function (w) { c5.appendChild(el('p', 'dxp-warn-msg', w.msg)); });
        res.appendChild(c5);
      }
      if (infoNotes.length) {
        var c5b = card('Plan notes');
        infoNotes.forEach(function (w) { c5b.appendChild(el('p', 'dxp-muted', w.msg)); });
        res.appendChild(c5b);
      }

      /* 6 — swaps, grouped by the problem they solve (feedback: easier to scan) */
      var c6 = card('Smart food swaps');
      c6.appendChild(el('p', 'dxp-muted', 'Quick fixes grouped by what you want to change:'));
      var quickFixes = [
        ['If protein is low', 'Add ' + proteinSwapText(inp) + '. Keep portions to your protein target.'],
        ['If fibre feels too high', 'Reduce heavy beans, large dal servings and raw salad a little. Use ' + gentleProteinSwapText(inp) + ' and build fibre back up slowly.'],
        ['If hunger is high', 'Add volume first: extra vegetables, salad, soup, buttermilk, a whole fruit or roasted chana before adding cereals or oil.'],
        ['If sodium is high', 'Cut pickle, papad, namkeen, packaged sauces and salted snacks; season with lemon, herbs and spices instead of extra salt.']
      ];
      var qfWrap = el('div', 'dxp-swaps');
      quickFixes.forEach(function (q) {
        var b = el('div', 'dxp-swap-item');
        b.appendChild(el('h4', null, q[0]));
        b.appendChild(el('p', null, q[1]));
        qfWrap.appendChild(b);
      });
      c6.appendChild(qfWrap);
      var detail = el('details', 'dxp-swap-detail');
      detail.appendChild(el('summary', null, 'More swap ideas with reasons and cautions'));
      detail.appendChild(table(['When', 'Use', 'Why', 'Caution'], data.swaps.map(function (s) {
        return [s.when, s.use, s.why, s.caution];
      })));
      c6.appendChild(detail);
      res.appendChild(c6);

      /* 7 — shopping list */
	      var sl = weekly ? Engine.weeklyShoppingList(weekly, data) : Engine.shoppingList(built, data);
	      var c7 = card('Weekly shopping list');
	      c7.appendChild(el('p', 'dxp-muted', weekly
        ? 'Summed from the actual seven-day table and rounded for practical buying. Amounts can vary with cooking yield, household measures and recipe thickness.'
        : 'Summed from the one-day plan repeated for a week and rounded for practical buying. Amounts can vary with cooking yield, household measures and recipe thickness.'));
      var shopWrap = el('div', 'dxp-shop-grid');
      Object.keys(sl).forEach(function (grp) {
        var d = el('div', 'dxp-shop-group');
        d.appendChild(el('h4', null, grp));
        var ul = el('ul');
        sl[grp].forEach(function (it) { ul.appendChild(el('li', null, it.item + ': ' + it.qty)); });
        d.appendChild(ul);
        shopWrap.appendChild(d);
      });
      c7.appendChild(shopWrap);
      /* pulse intake vs DGI baseline target (feedback): explain, don't hide */
      var fgT = Engine.foodGroupTargets(targets.kcal, inp.diet_type, data), pulseTgt = 0;
      fgT.forEach(function (g) { if (/pulse/i.test(g.grp)) pulseTgt = g.qty; });
      if (pulseTgt && pulseDay > pulseTgt * 1.25) {
        c7.appendChild(el('p', 'dxp-muted', 'This vegetarian plan uses more pulses than the baseline My Plate target to help meet protein needs, and the shopping list is rounded for practical buying. If you feel bloated, reduce heavy beans and large dal portions by 20-25% and use ' + gentleProteinSwapText(inp) + ' as suitable.'));
      }
      res.appendChild(c7);

	  if (tot.fb > 45 || pulseDay > 105) res.appendChild(renderBloatingCard(inp, pulseDay, tot.fb));

      /* 8 — practical adjustments (feedback) */
      var cAdj = card('How to adjust this plan');
      var adjUl = el('ul', 'dxp-logic-list');
      adjUl.appendChild(el('li', null, 'If you feel hungry: add volume first with extra salad, vegetables, dal, a whole fruit, or ' + proteinSwapText(inp) + ' before increasing cereals or oil.'));
      adjUl.appendChild(el('li', null, 'For a stronger snack: pair fruit with roasted chana, 10-15 g nuts/seeds, or ' + proteinSwapText(inp) + ' as suited to your diet type.'));
      adjUl.appendChild(el('li', null, 'If weight is not changing after 2-3 weeks: re-generate the plan with your current weight, tighten portion sizes, and check daily steps and sleep.'));
      adjUl.appendChild(el('li', null, 'If energy is low or training suffers: keep protein steady, shift more carbohydrate around your workout, and review sleep before cutting calories further.'));
      cAdj.appendChild(adjUl);
      res.appendChild(cAdj);

      res.appendChild(renderFeedbackCard(inp, built, smart));
    }

    /* 8 — disclaimer + actions */
    var c8 = card(null, 'dxp-disclaimer-card');
    c8.appendChild(el('p', 'dxp-disclaimer', 'This DietXP planner gives educational diet-planning estimates. It is not a medical diagnosis or treatment plan.'));
    c8.appendChild(el('p', 'dxp-disclaimer', data.messages.disclaimers.medical));
    c8.appendChild(el('p', 'dxp-disclaimer dxp-source', 'Nutrition values are estimates and can vary with ingredients, brands, cooking method and portion size.'));
    var actions = el('div', 'dxp-result-actions');
    var printBtn = el('button', 'dxp-btn dxp-btn-primary', 'Download PDF report');
    printBtn.type = 'button';
    printBtn.addEventListener('click', function () { printReport(inp); });
    actions.appendChild(printBtn);
    var ctaUrl = root.getAttribute('data-consult-url');
    if (ctaUrl) {
      var a = el('a', 'dxp-btn dxp-btn-ghost', 'Book a dietitian consultation');
      a.href = ctaUrl;
      actions.appendChild(a);
    }
    var calcLink = el('a', 'dxp-btn dxp-btn-ghost', 'More health calculators');
    calcLink.href = 'https://www.dietxp.com/pages/calculators';
    actions.appendChild(calcLink);
    var productLink = el('a', 'dxp-btn dxp-btn-ghost', 'Shop DietXP products');
    productLink.href = 'https://www.dietxp.com/collections/all';
    actions.appendChild(productLink);
    var blogLink = el('a', 'dxp-btn dxp-btn-ghost', 'Read food guides');
    blogLink.href = 'https://blog.dietxp.com/';
    actions.appendChild(blogLink);
    c8.appendChild(actions);
    res.appendChild(c8);
    res.appendChild(renderRelatedLinksCard());

    res.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  /* --------------------------- analyse mode ----------------------------- */
  var analyseItems = [];

  function buildFoodOptions() {
    var dl = $('#dxpFoodList');
    if (!dl) return;
    dl.innerHTML = '';
    DATA.foods.forEach(function (f) {
      var o = document.createElement('option');
      o.value = f.n;
      dl.appendChild(o);
    });
  }

  function analyseStatus(msg, kind) {
    var box = $('#dxpAnalyseStatus');
    if (!box) return;
    box.innerHTML = '';
    if (msg) box.appendChild(el('p', kind === 'warn' ? 'dxp-warn-msg' : 'dxp-muted', msg));
  }

  function analyseProfileTargets() {
    try {
      var inp = getInput();
      if (!(inp.age >= 18 && inp.age <= 80 && inp.height_cm > 0 && inp.weight_kg > 0 &&
            inp.sex && inp.goal && inp.activity && inp.diet_type)) return null;
      var targets = Engine.calcTargets(inp, DATA);
      return targets && !targets.blocks.length ? targets : null;
    } catch (e) { return null; }
  }

  function compareRow(label, amount, target, unit, note) {
    var pct = target ? Math.round(amount / target * 100) + '%' : '-';
    return [label, amount + ' ' + unit, target ? target + ' ' + unit : '-', pct, note || ''];
  }

  function renderAnalyse() {
    var holder = $('#dxpAnalyseItems');
    holder.innerHTML = '';
    if (!analyseItems.length) {
      holder.appendChild(el('p', 'dxp-muted', 'Add foods above to analyse a meal or a full day.'));
      $('#dxpAnalyseTotals').innerHTML = '';
      analyseStatus('', 'info');
      return;
    }
    var rows = analyseItems.map(function (it, idx) {
      return [it.name, it.grams + ' g', Math.round(it.f.e * it.grams / 100) + ' kcal',
              (Math.round(it.f.p * it.grams / 10) / 10) + ' g', '×'];
    });
    var t = table(['Food', 'Amount', 'Energy', 'Protein', ''], rows);
    $all('tr', t).forEach(function (tr, i) {
      if (i === 0) return;
      var last = tr.lastChild;
      last.className = 'dxp-remove';
      last.setAttribute('role', 'button');
      last.setAttribute('tabindex', '0');
      last.addEventListener('click', function () { analyseItems.splice(i - 1, 1); saveAnalyse(); renderAnalyse(); });
    });
    holder.appendChild(t);

    var weight = +($('#dxpNutriForm [name="weight_kg"]').value) || null;
    var result = Engine.analyse(analyseItems.map(function (it) { return { code: it.f.c, grams: it.grams }; }),
                                weight ? { weight_kg: weight } : null, DATA);
    var tt = result.totals;
    var totalsEl = $('#dxpAnalyseTotals');
    totalsEl.innerHTML = '';
    var c = card('Nutrient totals');
    var grid = el('div', 'dxp-result-grid');
    grid.appendChild(kpi('Energy', tt.e + ' kcal'));
    grid.appendChild(kpi('Protein', tt.p + ' g', result.compare ? 'Reference ' + result.compare.protein_rda_g + ' g (0.83 g/kg)' : ''));
    grid.appendChild(kpi('Fat', tt.f + ' g'));
    grid.appendChild(kpi('Carbohydrate', tt.cb + ' g'));
    grid.appendChild(kpi('Fibre', tt.fb + ' g', 'reference ≥25 g/day'));
    grid.appendChild(kpi('Sodium', tt.na + ' mg', 'high if >' + DATA.config.sodium_high_threshold_mg + ' mg'));
    c.appendChild(grid);
    c.appendChild(table(['Micronutrient', 'Amount'], [
      ['Calcium', tt.ca + ' mg'], ['Iron', tt.fe + ' mg'], ['Zinc', tt.zn + ' mg'],
      ['Magnesium', tt.mg + ' mg'], ['Potassium', tt.k + ' mg'],
      ['Vitamin C', tt.vc + ' mg'], ['Folate', tt.fo + ' µg']
    ]));
    var profileTargets = analyseProfileTargets();
    if (profileTargets) {
      c.appendChild(table(['Target check', 'Current total', 'Profile target', '% of target', 'Note'], [
        compareRow('Energy', tt.e, profileTargets.kcal, 'kcal', 'full-day target'),
        compareRow('Protein', tt.p, profileTargets.protein_g, 'g', 'full-day target'),
        compareRow('Fat', tt.f, profileTargets.fat_g, 'g', 'full-day target'),
        compareRow('Carbohydrate', tt.cb, profileTargets.carb_g, 'g', 'full-day target'),
        compareRow('Fibre', tt.fb, profileTargets.fibre_g, 'g', 'target is minimum')
      ]));
    } else {
      c.appendChild(el('p', 'dxp-muted', 'Enter the main profile fields in Generate Plan to compare these totals with personalised targets.'));
    }
    c.appendChild(el('p', 'dxp-muted', 'Micronutrient percentages are not shown here yet. Use these totals as estimates, because values can change with food variety, cooking method and portion size.'));
    totalsEl.appendChild(c);
  }

  function setupAnalyse() {
    buildFoodOptions();
    analyseItems = loadAnalyse();
    if (analyseItems.length) renderAnalyse();
    $('#dxpAnalyseAdd').addEventListener('click', function () {
      var nameIn = $('#dxpAnalyseFood'), gIn = $('#dxpAnalyseGrams');
      var f = null;
      for (var i = 0; i < DATA.foods.length; i++) {
        if (DATA.foods[i].n.toLowerCase() === nameIn.value.toLowerCase().trim()) { f = DATA.foods[i]; break; }
      }
      if (!f) {
        for (var j = 0; j < DATA.foods.length; j++) {
          if (DATA.foods[j].n.toLowerCase().indexOf(nameIn.value.toLowerCase().trim()) >= 0) { f = DATA.foods[j]; break; }
        }
      }
      var grams = +gIn.value;
      nameIn.classList.toggle('dxp-invalid', !f);
      gIn.classList.toggle('dxp-invalid', !(grams > 0));
      if (!f || !(grams > 0)) {
        analyseStatus(!f
          ? 'No exact food found. Try a simpler food name like atta, rice, dal, curd, milk, paneer, egg, chicken or banana.'
          : 'Enter the cooked/raw amount in grams before adding.', 'warn');
        return;
      }
      analyseStatus('Added ' + f.n + ' (' + grams + ' g).', 'info');
      analyseItems.push({ f: f, name: f.n, grams: grams });
      saveAnalyse();
      nameIn.value = ''; gIn.value = '';
      nameIn.focus();
      renderAnalyse();
    });
    var reset = $('#dxpAnalyseReset');
    if (reset) reset.addEventListener('click', function () {
      analyseItems = [];
      saveAnalyse();
      $('#dxpAnalyseFood').value = '';
      $('#dxpAnalyseGrams').value = '';
      renderAnalyse();
      analyseStatus('Analysis reset.', 'info');
    });
    var print = $('#dxpAnalysePrint');
    if (print) print.addEventListener('click', function () { printNamedReport('DietXP Meal Analysis'); });
    var gramsInput = $('#dxpAnalyseGrams');
    if (gramsInput) gramsInput.addEventListener('keydown', function (ev) {
      if (ev.key === 'Enter') {
        ev.preventDefault();
        $('#dxpAnalyseAdd').click();
      }
    });
    $all('.dxp-food-chips button').forEach(function (chip) {
      chip.addEventListener('click', function () {
        $('#dxpAnalyseFood').value = chip.getAttribute('data-food') || '';
        $('#dxpAnalyseGrams').value = chip.getAttribute('data-grams') || '';
        $('#dxpAnalyseAdd').click();
      });
    });
  }

  /* ---------------------- clinical dietitian mode ----------------------- */
  function clinicalInput() {
    var form = $('#dxpClinicalForm');
    var fd = new FormData(form);
    function checked(name) { return !!form.querySelector('input[name="' + name + '"]:checked'); }
    return {
      professional_attest: checked('professional_attest'),
      client_name: str(fd.get('client_name')).trim(),
      age: +fd.get('age'), sex: fd.get('sex'), height_cm: +fd.get('height_cm'), weight_kg: +fd.get('weight_kg'),
      diet_type: fd.get('diet_type'), plan_goal: fd.get('plan_goal'), clinical_setting: fd.get('clinical_setting'),
      guideline: fd.get('guideline'), icu_day: +fd.get('icu_day'), measured_ee_kcal: +fd.get('measured_ee_kcal'),
      propofol_kcal: +fd.get('propofol_kcal'), citrate_kcal: +fd.get('citrate_kcal'),
      target_kcal: +fd.get('target_kcal'), carb_target_g: +fd.get('carb_target_g'),
      ckd_stage: fd.get('ckd_stage'), dialysis_or_krt: fd.get('dialysis_or_krt'),
      serum_potassium_status: fd.get('serum_potassium_status'),
      serum_phosphorus_status: fd.get('serum_phosphorus_status'),
      hemodynamic_status: fd.get('hemodynamic_status'),
      metabolically_unstable: checked('metabolically_unstable'),
      aki_present: checked('aki_present'),
      diabetes: checked('diabetes'),
      pregnancy_lactation: checked('pregnancy_lactation'),
      pediatric_patient: checked('pediatric_patient'),
      en_contraindication: checked('en_contraindication'),
      tube_feed_pn: checked('tube_feed_pn'),
      malnutrition_refeeding: checked('malnutrition_refeeding'),
      cancer_treatment: checked('cancer_treatment')
    };
  }
  function validateClinicalForm() {
    var ok = true;
    $all('#dxpClinicalForm [required]').forEach(function (f) {
      var bad = f.type === 'checkbox' ? !f.checked : !f.value;
      f.classList.toggle('dxp-invalid', bad);
      f.setAttribute('aria-invalid', bad ? 'true' : 'false');
      if (bad) ok = false;
    });
    return ok;
  }
  function exchangeRows(plan) {
    if (!plan) return [];
    return [
      ['Cereal', plan.cereal_ex || 0],
      ['Pulse', plan.pulse_ex || 0],
      ['Protein', plan.protein_ex || 0],
      ['Vegetables', plan.veg_ex || 0],
      ['Fruit', plan.fruit_ex || 0],
      ['Milk/curd', plan.milk_ex || 0],
      ['Fat', plan.fat_ex || 0]
    ];
  }
  function renderClinicalResult(inp, plan) {
    var holder = $('#dxpClinicalResults');
    holder.innerHTML = '';
    holder.hidden = false;

    if (plan.blocks.length) {
      var blocked = card('Clinical draft blocked', 'dxp-warning');
      plan.blocks.forEach(function (b) { blocked.appendChild(el('p', 'dxp-block-msg', b)); });
      blocked.appendChild(el('p', 'dxp-muted', 'This case needs specialist review before an automated clinical plan is issued.'));
      holder.appendChild(blocked);
      holder.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }

    var profile = card('Clinical report profile');
    var profileGrid = el('div', 'dxp-result-grid');
    profileGrid.appendChild(kpi('Name', inp.client_name || 'Not entered'));
    profileGrid.appendChild(kpi('Age / Sex', inp.age + ' years · ' + titleCase(inp.sex)));
    profileGrid.appendChild(kpi('Height / Weight', inp.height_cm + ' cm · ' + inp.weight_kg + ' kg'));
    profileGrid.appendChild(kpi('Clinical setting', titleCase(inp.clinical_setting) + ' · ' + titleCase(inp.guideline)));
    profileGrid.appendChild(kpi('Diet / Goal', titleCase(inp.diet_type) + ' · ' + titleCase(inp.plan_goal)));
    profileGrid.appendChild(kpi('Report date', new Date().toLocaleDateString()));
    profile.appendChild(profileGrid);
    holder.appendChild(profile);

    var c1 = card('Clinical nutrition targets');
    var grid = el('div', 'dxp-result-grid');
    grid.appendChild(kpi('BMI', plan.bmi || '-', 'Ref wt ' + plan.reference_weight_kg + ' kg'));
    grid.appendChild(kpi('Energy range', (plan.nutrition_energy_low_kcal || '-') + '-' + (plan.nutrition_energy_high_kcal || '-') + ' kcal', 'after non-nutrition kcal'));
    grid.appendChild(kpi('Protein range', (plan.protein_low_g || '-') + '-' + (plan.protein_high_g || '-') + ' g/day'));
    grid.appendChild(kpi('Plan kcal', plan.target_kcal || '-', plan.exchangePlan ? 'exchange pattern ' + plan.exchangePlan.plan_id : 'manual review'));
    grid.appendChild(kpi('Sodium', plan.sodium_limit || 'Individualized'));
    grid.appendChild(kpi('Non-nutrition kcal', plan.non_nutrition_kcal + ' kcal', 'propofol/citrate/KRT'));
    c1.appendChild(grid);
    if (plan.route) c1.appendChild(el('p', 'dxp-caps', plan.route));
    holder.appendChild(c1);

    if (plan.flags.length || plan.notes.length) {
      var c2 = card('Safety gates and review flags', 'dxp-warning');
      plan.flags.forEach(function (f) { c2.appendChild(el('p', 'dxp-warn-msg', f.severity.toUpperCase() + ': ' + f.message)); });
      plan.notes.forEach(function (n) { c2.appendChild(el('p', 'dxp-warn-msg', n)); });
      holder.appendChild(c2);
    }

    var c3 = card('Exchange prescription');
    if (plan.exchangePlan) {
      c3.appendChild(el('p', 'dxp-muted', 'Closest exchange pattern selected: ' + plan.exchangePlan.plan_id +
        ' (' + plan.exchangePlan.target_kcal + ' kcal, ' + plan.exchangePlan.diet_type + ', ' + plan.exchangePlan.goal + ').'));
      c3.appendChild(table(['Exchange group', 'Daily exchanges'], exchangeRows(plan.exchangePlan)));
      if (plan.exchangePlan.protein_note) c3.appendChild(el('p', 'dxp-muted', plan.exchangePlan.protein_note));
    } else {
      c3.appendChild(el('p', 'dxp-muted', 'No matching exchange pattern is available for this calorie target. Enter a manual target or add this target to the exchange plan table.'));
    }
    holder.appendChild(c3);

    if (plan.mealDistribution.length) {
      var c4 = card('Meal-wise exchange distribution');
      c4.appendChild(table(['Group', 'Breakfast', 'Lunch', 'Snack', 'Dinner', 'Daily total'],
        plan.mealDistribution.map(function (r) {
          return [r.exchange_group, r.breakfast_ex, r.lunch_ex, r.snack_ex, r.dinner_ex, r.daily_total_ex];
        })));
      holder.appendChild(c4);
    }

    if (plan.carbBudget.length) {
      var c5 = card('Carb counting budget');
      c5.appendChild(table(['Meal', 'Carbohydrate target', 'Carb choices'],
        plan.carbBudget.map(function (r) { return [r.meal, r.carb_g + ' g', r.choices]; })));
      holder.appendChild(c5);
    }

    var groups = Object.keys(plan.exchangeFoodOptions);
    if (groups.length) {
      var c6 = card('Exchange food options');
      c6.appendChild(table(['Group', 'Food options'], groups.map(function (g) {
        return [g, plan.exchangeFoodOptions[g].map(function (r) {
          return r.food_name + ' (' + r.grams_per_1_exchange + ' g)';
        }).join(', ')];
      })));
      holder.appendChild(c6);
    }

    if (plan.matchedRules.length) {
      var c7 = card('Clinical rule trace');
      c7.appendChild(table(['Rule', 'Domain', 'Logic'], plan.matchedRules.map(function (r) {
        return [r.rule_id || r.source_id || '-', r.domain || r.trigger || '-', r.rule || r.logic_note || r.target_or_rule || '-'];
      })));
      holder.appendChild(c7);
    }

    var c8 = card('Clinical sign-off');
    c8.appendChild(el('p', 'dxp-disclaimer', 'This is a dietitian-facing clinical draft. Final prescription requires clinical judgment, diagnosis, medication and lab review, route feasibility, allergies, food access, monitoring plan and local policy sign-off.'));
    var actions = el('div', 'dxp-result-actions');
    var printBtn = el('button', 'dxp-btn dxp-btn-primary', 'Download clinical PDF');
    printBtn.type = 'button';
    printBtn.addEventListener('click', function () { printReport(inp); });
    actions.appendChild(printBtn);
    c8.appendChild(actions);
    holder.appendChild(c8);
    holder.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
  /* Soft passcode gate for the Clinical (professional) mode. The clinical
     fields live in a <template> and are only cloned into the DOM after the
     correct code — so ICU/CKD/etc. fields are not in page source until unlock.
     NOTE: this is a convenience deterrent; real restriction must be enforced at
     the page/account level (Shopify login / locked page). The professional
     attestation + disclaimers remain the actual safety layer. Session-scoped. */
  function setupClinicalLock() {
    var panel = $('.dxp-mode[data-mode="clinical"]');
    if (!panel) return;
    var lock = $('[data-clinical-lock]', panel);
    var mount = $('#dxpClinicalMount'), tpl = document.getElementById('dxpClinicalTpl');
    if (!lock || !mount || !tpl) return;
    var calc = root.querySelector('.dxp-calculator');
    var code = ((calc && calc.getAttribute('data-clinical-passcode')) || '').trim();
    var KEY = 'dxp_ns_clinical_unlocked';
    var mounted = false;
    function unlocked() {
      if (!code) return true;                 // blank code = no gate
      try { return sessionStorage.getItem(KEY) === '1'; } catch (e) { return false; }
    }
    function mountForm() {
      if (mounted) return;
      mount.appendChild(tpl.content.cloneNode(true));   // clinical fields enter the DOM only now
      var form = $('#dxpClinicalForm');
      if (form) form.addEventListener('submit', function (ev) {
        ev.preventDefault();
        if (!validateClinicalForm()) return;
        renderClinicalResult(clinicalInput(), Engine.clinicalPlan(clinicalInput(), DATA));
      });
      mounted = true;
    }
    function apply() { var ok = unlocked(); lock.hidden = ok; if (ok) mountForm(); }
    apply();
    var input = $('#dxpClinicalPass'), btn = $('#dxpClinicalUnlock'), msgBox = $('#dxpClinicalLockMsg');
    function showError(show) {
      if (!msgBox) return;
      msgBox.innerHTML = '';
      if (show) msgBox.appendChild(el('p', 'dxp-warn-msg', 'Incorrect code. Please try again.'));
    }
    function tryUnlock() {
      if (((input && input.value) || '').trim() === code && code) {
        try { sessionStorage.setItem(KEY, '1'); } catch (e) {}
        showError(false);
        if (input) { input.value = ''; input.classList.remove('dxp-invalid'); }
        apply();
      } else {
        showError(true);
        if (input) input.classList.add('dxp-invalid');
      }
    }
    if (btn) btn.addEventListener('click', tryUnlock);
    if (input) input.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') { e.preventDefault(); tryUnlock(); }
    });
  }

  /* --------------------------- tracker mode ----------------------------- */
  function trackerData() {
    try { return JSON.parse(localStorage.getItem(LS_TRACK) || '[]'); } catch (e) { return []; }
  }
  function saveTracker(rows) {
    try { localStorage.setItem(LS_TRACK, JSON.stringify(rows)); } catch (e) {}
  }

  /* analyse list persists in-browser so a logged "usual day" survives reloads */
  function saveAnalyse() {
    try {
      localStorage.setItem(LS_ANALYSE, JSON.stringify(analyseItems.map(function (it) {
        return { c: it.f.c, grams: it.grams };
      })));
    } catch (e) {}
  }
  function loadAnalyse() {
    var out = [];
    try {
      var raw = JSON.parse(localStorage.getItem(LS_ANALYSE) || '[]');
      raw.forEach(function (r) {
        for (var i = 0; i < DATA.foods.length; i++) {
          if (DATA.foods[i].c === r.c && r.grams > 0) {
            out.push({ f: DATA.foods[i], name: DATA.foods[i].n, grams: r.grams });
            break;
          }
        }
      });
    } catch (e) {}
    return out;
  }

  function svgNode(tag, attrs) {
    var n = document.createElementNS('http://www.w3.org/2000/svg', tag);
    for (var k in attrs) if (attrs.hasOwnProperty(k)) n.setAttribute(k, attrs[k]);
    return n;
  }
  /* mini trend chart — weight (+ waist) over the logged entries, each line
     scaled to its own range so the shape of the trend is clear */
  function renderTrendChart(rows) {
    var host = $('#dxpTrackChart'); if (!host) return;
    host.innerHTML = '';
    if (rows.length < 2) return;
    var W = 600, H = 190, padL = 16, padR = 16, padT = 16, padB = 26, n = rows.length;
    function xAt(i) { return padL + (W - padL - padR) * (i / (n - 1)); }
    var svg = svgNode('svg', { viewBox: '0 0 ' + W + ' ' + H, class: 'dxp-chart', preserveAspectRatio: 'none',
      role: 'img', 'aria-label': 'Trend of your logged weight over time' });
    function plot(key, color) {
      var vals = rows.map(function (r) { return +r[key]; });
      if (vals.some(function (v) { return !(v > 0); })) return null;
      var mn = Math.min.apply(null, vals), mx = Math.max.apply(null, vals);
      if (mx - mn < 0.5) { mx += 0.5; mn -= 0.5; }
      function yAt(v) { return padT + (H - padT - padB) * (1 - (v - mn) / (mx - mn)); }
      var d = '';
      rows.forEach(function (r, i) { d += (i ? 'L' : 'M') + xAt(i).toFixed(1) + ' ' + yAt(+r[key]).toFixed(1) + ' '; });
      svg.appendChild(svgNode('path', { class: 'dxp-chart-line', d: d.trim(), stroke: color }));
      rows.forEach(function (r, i) { svg.appendChild(svgNode('circle', { class: 'dxp-chart-dot', cx: xAt(i).toFixed(1), cy: yAt(+r[key]).toFixed(1), r: '3.5', fill: color })); });
      return { mn: mn, mx: mx };
    }
    var legend = [];
    var w = plot('weight', '#f4d35e'); if (w) legend.push(['Weight (kg)', '#f4d35e', w]);
    var wa = plot('waist', '#ff691a'); if (wa) legend.push(['Waist (cm)', '#ff691a', wa]);
    [rows[0].date, rows[n - 1].date].forEach(function (dt, i) {
      var t = svgNode('text', { class: 'dxp-chart-label', x: i ? W - padR : padL, y: H - 8,
        'text-anchor': i ? 'end' : 'start' });
      t.textContent = dt; svg.appendChild(t);
    });
    var cardEl = card('Trend chart');
    cardEl.classList.add('dxp-track-chart');
    cardEl.appendChild(svg);
    if (legend.length) {
      var leg = el('div', 'dxp-chart-legend');
      legend.forEach(function (s) {
        var item = el('span');
        var sw = el('i'); sw.style.background = s[1]; item.appendChild(sw);
        item.appendChild(document.createTextNode(s[0] + ' ' + s[2].mn + '-' + s[2].mx));
        leg.appendChild(item);
      });
      cardEl.appendChild(leg);
    }
    host.appendChild(cardEl);
  }

  function renderTracker() {
    var rows = trackerData().sort(function (a, b) { return a.date < b.date ? -1 : 1; });
    var holder = $('#dxpTrackerTable');
    holder.innerHTML = '';
    var chartHost = $('#dxpTrackChart'); if (chartHost) chartHost.innerHTML = '';
    if (!rows.length) {
      holder.appendChild(el('p', 'dxp-muted', 'No entries yet. Add your first weekly check-in above. Entries are stored only in this browser.'));
      return;
    }
    renderTrendChart(rows);
    var hIn = +($('#dxpNutriForm [name="height_cm"]').value) || null;
    var trows = rows.map(function (r, i) {
      var prev = i > 0 ? rows[i - 1] : null;
      var dW = prev ? (Math.round((r.weight - prev.weight) * 10) / 10) : 0;
      var bmi = hIn ? Math.round(r.weight / Math.pow(hIn / 100, 2) * 10) / 10 : '-';
      var whtr = (hIn && r.waist) ? Math.round(r.waist / hIn * 100) / 100 : '-';
      return [r.date, r.weight + ' kg', (prev ? (dW > 0 ? '+' : '') + dW + ' kg' : '-'),
              r.waist ? r.waist + ' cm' : '-', bmi, whtr, '×'];
    });
    var t = table(['Date', 'Weight', 'Δ weight', 'Waist', 'BMI', 'WHtR', ''], trows);
    $all('tr', t).forEach(function (tr, i) {
      if (i === 0) return;
      var last = tr.lastChild;
      last.className = 'dxp-remove';
      last.addEventListener('click', function () {
        var all = trackerData().sort(function (a, b) { return a.date < b.date ? -1 : 1; });
        all.splice(i - 1, 1);
        saveTracker(all);
        renderTracker();
      });
    });
    holder.appendChild(t);

    if (rows.length >= 2) {
      var first = rows[0], lastR = rows[rows.length - 1];
      var change = Math.round((lastR.weight - first.weight) * 10) / 10;
      var weeks = Math.max(1, Math.round((new Date(lastR.date) - new Date(first.date)) / (7 * 24 * 3600 * 1000)));
      var rate = Math.round(change / weeks * 100) / 100;
      var msg = 'Total change ' + (change > 0 ? '+' : '') + change + ' kg over ~' + weeks +
        ' week(s) (' + (rate > 0 ? '+' : '') + rate + ' kg/week). ';
      var goal = getInput().goal;
      if (goal === 'fat_loss') {
        msg += rate <= -1 ? 'Loss is fast. Consider reducing the deficit and reviewing protein.'
          : rate < -0.2 ? 'Steady fat-loss range. Keep the current plan.'
          : 'Loss has stalled. Re-generate the plan with your current weight, or review adherence.';
      } else if (goal === 'muscle_gain') {
        msg += rate >= 0.5 ? 'Gain is fast. Reduce the surplus to limit fat gain.'
          : rate > 0.05 ? 'Controlled lean-gain range. Keep training.'
          : 'No gain. Re-generate the plan with your current weight or increase intake slightly.';
      }
      holder.appendChild(el('p', 'dxp-track-note', msg));
      if (Math.abs(change) >= 2) {
        var actions = el('div', 'dxp-result-actions');
        var regen = el('button', 'dxp-btn dxp-btn-primary', 'Regenerate plan with current weight');
        regen.type = 'button';
        regen.addEventListener('click', function () {
          var weightField = $('#dxpNutriForm [name="weight_kg"]');
          if (weightField) weightField.value = lastR.weight;
          persistForm();
          var tab = $('.dxp-tab[data-mode="generate"]');
          if (tab) tab.click();
          showStep(1);
          var form = $('#dxpNutriForm');
          if (form) form.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
        actions.appendChild(regen);
        holder.appendChild(actions);
      }
    }

    /* check-in averages (hunger / energy / digestion) + practical notes */
    var ci = [['hunger', 'Hunger'], ['energy', 'Energy'], ['digestion', 'Digestion']].map(function (p) {
      var v = rows.map(function (r) { return +r[p[0]]; }).filter(function (x) { return x > 0; });
      return v.length ? { key: p[0], label: p[1], avg: Math.round(v.reduce(function (a, b) { return a + b; }, 0) / v.length * 10) / 10 } : null;
    }).filter(Boolean);
    if (ci.length) {
      var ciMsg = 'Check-in averages: ' + ci.map(function (c) { return c.label + ' ' + c.avg + '/5'; }).join(' | ') + '. ';
      var byKey = {}; ci.forEach(function (c) { byKey[c.key] = c.avg; });
      if (byKey.hunger != null && byKey.hunger <= 2) ciMsg += 'Hunger has been on the harder side. Add volume (vegetables, dal, curd, salad) before adding calories. ';
      if (byKey.energy != null && byKey.energy <= 2) ciMsg += 'Energy has been low. Check sleep, meal timing and carbohydrate around training. ';
      if (byKey.digestion != null && byKey.digestion <= 2) ciMsg += 'Digestion has been uncomfortable. Reduce pulse/fibre portions and build up gradually. ';
      holder.appendChild(el('p', 'dxp-track-note', ciMsg));
    }
  }

  function setupTracker() {
    var dateIn = $('#dxpTrackDate');
    if (dateIn && !dateIn.value) dateIn.valueAsDate = new Date();
    $('#dxpTrackAdd').addEventListener('click', function () {
      var date = $('#dxpTrackDate').value;
      var weight = +$('#dxpTrackWeight').value;
      var waist = +$('#dxpTrackWaist').value || null;
      $('#dxpTrackWeight').classList.toggle('dxp-invalid', !(weight > 25));
      if (!date || !(weight > 25)) return;
      var ci = {
        hunger: +($('#dxpTrackHunger') || {}).value || null,
        energy: +($('#dxpTrackEnergy') || {}).value || null,
        digestion: +($('#dxpTrackDigestion') || {}).value || null
      };
      var rows = trackerData().filter(function (r) { return r.date !== date; });
      rows.push({ date: date, weight: weight, waist: waist, hunger: ci.hunger, energy: ci.energy, digestion: ci.digestion });
      saveTracker(rows);
      ['dxpTrackHunger', 'dxpTrackEnergy', 'dxpTrackDigestion'].forEach(function (id) { var s = $('#' + id); if (s) s.value = ''; });
      renderTracker();
    });
  }

  /* ------------------------------- tabs --------------------------------- */
  function setupTabs() {
    var tabs = $all('.dxp-tab');
    tabs.forEach(function (tab, i) {
      tab.addEventListener('click', function () { selectTab(i); });
      tab.addEventListener('keydown', function (ev) {
        var dir = ev.key === 'ArrowRight' ? 1 : ev.key === 'ArrowLeft' ? -1 : 0;
        if (!dir) return;
        ev.preventDefault();
        var next = (i + dir + tabs.length) % tabs.length;
        selectTab(next);
        tabs[next].focus();
      });
    });
    var descs = {
      generate: 'Create a full personalised Indian meal plan.',
      analyse: 'Check calories and nutrients from your own foods.',
      track: 'Save weekly weight, waist, hunger, energy and digestion.',
      clinical: 'Professional calculation workspace for reviewed use.'
    };
    var descEl = $('#dxpTabDesc');
    function selectTab(i) {
      var mode = tabs[i] ? tabs[i].getAttribute('data-mode') : null;
      tabs.forEach(function (t, j) {
        var on = i === j;
        t.classList.toggle('active', on);
        t.setAttribute('aria-selected', on ? 'true' : 'false');
        t.tabIndex = on ? 0 : -1;
      });
      /* match panels by data-mode so the tab order is independent of DOM order */
      $all('.dxp-mode').forEach(function (p) {
        var on = p.getAttribute('data-mode') === mode;
        p.hidden = !on;
        p.setAttribute('aria-hidden', on ? 'false' : 'true');
      });
      if (descEl && mode) descEl.textContent = descs[mode] || '';
      if (mode === 'track') renderTracker();
    }
    selectTab(0);
  }

  /* Brand two-tone headings: white text with the trailing word(s) in
     Nutrition Yellow, matching the DietXP brand. Applied to the hero and the
     main step/mode headings (kept off small card titles for readability). */
  function brandAccentHeadings() {
    $all('.dxp-hero h1, .dxp-step h2, .dxp-mode > h2').forEach(function (h) {
      if (h.querySelector('.dxp-accent')) return;
      var words = (h.textContent || '').trim().split(/\s+/);
      if (words.length < 2) return;
      var n = words.length >= 4 ? 2 : 1;
      var head = words.slice(0, words.length - n).join(' ');
      var span = el('span', 'dxp-accent', words.slice(words.length - n).join(' '));
      h.textContent = head + ' ';
      h.appendChild(span);
    });
  }

  /* ------------------------------- init --------------------------------- */
  /* SEO safety net: if the page has no <meta name="description"> (e.g. the page's
     admin SEO listing is blank and no head snippet is installed), inject a
     planner-specific one so JS-rendering search engines still get a real
     description. Never duplicates an existing tag. Social scrapers that do not
     run JS still need the snippets/dietxp-seo-head.liquid head tags. */
  function ensureMetaDescription() {
    try {
      if (!document.head || document.querySelector('meta[name="description"]')) return;
      var m = document.createElement('meta');
      m.setAttribute('name', 'description');
      m.setAttribute('content', 'Free Indian diet plan calculator with calorie and macro targets, a recipe-based Indian meal plan, a weekly shopping list and a printable dietitian-style report for fat loss, muscle gain or maintenance.');
      document.head.appendChild(m);
    } catch (e) {}
  }
  function ensurePageTitle() {
    try {
      if (!document || !/come back|untitled|pages?/i.test(document.title || '')) return;
      document.title = 'Smart Indian Diet Planner: 7-Day Personalised Meal Plan | DietXP';
    } catch (e) {}
  }

  function init() {
    root = document.querySelector('.dxp-nutrisuite');
    if (!root) return;
    DATA = loadEmbedded();
    if (!DATA) return;
    setDataStatus('embedded');
    updateVersionLabels();
    ensureMetaDescription();
    ensurePageTitle();
    remoteLoadPromise = maybeFetchRemote(root.querySelector('.dxp-calculator').getAttribute('data-apps-script-url'));

    restoreForm();
    setupUnits();
    syncConditionalFields();
    syncChoiceCards();
    showStep(1);

    $('.dxp-next').addEventListener('click', function () {
      if (validateStep(step)) { persistForm(); showStep(step + 1); }
    });
    $('.dxp-prev').addEventListener('click', function () { showStep(step - 1); });
    $('#dxpNutriForm').addEventListener('input', function () {
      persistForm();
      updatePlanPreview();
    });
    $('#dxpNutriForm').addEventListener('change', function () {
      syncConditionalFields();
      syncChoiceCards();
      persistForm();
      updatePlanPreview();
    });

    $('#dxpNutriForm').addEventListener('submit', function (ev) {
      ev.preventDefault();
      for (var s = 1; s <= MAX_STEP; s++) {
        if (!validateStep(s)) { showStep(s); return; }
      }
      runReportWhenDataReady();
    });

    setupTabs();
    setupAnalyse();
    setupClinicalLock();
    brandAccentHeadings();
    setupTracker();

    window.DietXP = window.DietXP || {};
    window.DietXP.getData = function () { return DATA; };
    window.DietXP.reloadSheetData = function () {
      try { localStorage.removeItem(LS_DATA); } catch (e) {}
      maybeFetchRemote(root.querySelector('.dxp-calculator').getAttribute('data-apps-script-url'));
    };
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
